export async function api<T = any>(path: string, init?: RequestInit): Promise<T> {
  // Only declare a JSON content-type when actually sending a body. Fastify rejects an
  // EMPTY body that still carries `Content-Type: application/json` (400 — which silently
  // broke DELETE-unlink and the body-less Pull/Sync POSTs).
  const headers = new Headers(init?.headers);
  if (init?.body != null && !headers.has('Content-Type')) headers.set('Content-Type', 'application/json');
  const res = await fetch(`/api${path}`, { ...init, headers });
  if (!res.ok) {
    const body = await res.text().catch(() => '');
    throw new Error(`${res.status} ${res.statusText}${body ? `: ${body}` : ''}`);
  }
  if (res.status === 204) return undefined as T;
  return res.json();
}

export interface PackageSummary {
  id: string;
  name: string;
  project_name: string | null;
  procurement_mode: string;
  budget_envelope_rmb: number | null;
  current_stage: number;
  awarded_engagement_id: string | null;
  engagement_count: number;
  work_item_count: number;
  wtl_project_uuid?: string | null;
  tender_due_at?: string | null;     // B7/A7 · sealed-bid deadline
  bids_opened_at?: string | null;    // B7/A7 · null = sealed until 开标
}

export interface WorkItem {
  id: string;
  seq_label?: string | null;   // fb_6685b243 · the imported 清单's own 序号, verbatim (null for hand-added rows)
  category_top: string | null;
  category_sub: string | null;
  item_label: string;
  scope_boundary_md: string | null;
  qty: number | null;
  unit: string | null;
  est_unit_price_rmb: number | null;
  est_total_rmb: number | null;
  sort_order: number;
  changed_in_revision?: number;   // WS2 · == package.boq_revision ⇒ changed this round
  revision_note?: string | null;  // WS2 · the 做法/厚度/基层 clarification shown to labor
  // Full 工程量清单 columns (改改 feedback). 项目特征/界限划分 reuse the scope_* fields.
  scope_description_md?: string | null; // 项目特征
  measurement_rule?: string | null;     // 工程量计算规则
  tax_rate_pct?: number | null;         // 税率% → 税金/含税单价/合计 computed in the UI
  brand_requirement?: string | null;    // 品牌要求
  remarks_md?: string | null;           // 备注说明
  ref_unit_price_rmb?: number | null;   // 参考价 — company guidance shown to labor (改改 fb_eadc55f7)
}

export interface BidLineItem {
  id: string;
  work_item_id: string | null;
  unit_price_rmb: number | null;
  qty_quoted: number | null;
  amount_rmb: number | null;
  outlier_flag: 'low' | 'high' | null;
  outlier_pct: number | null;
  remarks_md: string | null;
  item_label?: string | null;   // F3 · set only on 自行补充项 supplement lines (work_item_id null)
  // 多轮清单版本管理: on an ARCHIVED round the work_item was replaced (work_item_id → null); these hold the
  // name/unit/qty snapshotted at archive time so the round stays readable against the new 清单.
  snapshot_item_label?: string | null;
  snapshot_unit?: string | null;
  snapshot_qty?: number | null;
}

export interface Bid {
  id: string;
  engagement_id: string;
  round_number: number;
  source: string;
  received_at: string;
  screening_status: string;
  cleaning_status: string;
  cleaning_notes_md: string | null;
  total_rmb: number | null;
  line_items: BidLineItem[];
  terms?: Record<string, string>;
  // 多轮清单版本管理: a prior round KEPT for history after a 清单 re-import. archived rounds are excluded from
  // the active views (比家/比轮) and shown, labelled 旧清单v{rev}, only in 比家比轮.
  archived?: number;
  archived_boq_revision?: number | null;
  // Phase B · the staff-authored 议价后 final-offer round awaiting/holding the labor party's confirmation.
  is_final_offer?: number;
}

export interface Engagement {
  id: string;
  labor_co_id: string | null;
  labor_co_name: string | null;
  current_stage: number;
  shortlisted: number;
  withdrawn: number;
  withdrawn_reason: string | null;
  award_status: string | null;
  // 议价最终价确认: staff request → labor confirm the negotiated final total.
  final_confirm_requested_at?: string | null;
  final_total_rmb?: number | null;   // 不含税
  final_tax_pct?: number | null;     // fb_e5f31dc1 · 税金点 — 含税 is derived: total × (1 + pct/100)
  final_confirmed_at?: string | null;
  // Phase B · points at the staff-authored final-offer round (pending until final_confirmed_at = the lock).
  final_offer_bid_id?: string | null;
  bids: Bid[];
}

export interface Discussion {
  id: string;
  author_name: string;
  author_role: string | null;
  body_md: string;
  references_engagement_id: string | null;
  references_bid_id: string | null;
  created_at: string;
}

export interface PackageDetail extends PackageSummary {
  scope_summary_md: string | null;
  budget_envelope_notes_md: string | null;
  brief_md: string | null;
  wtl_project_uuid: string | null;
  wtl_last_pull_at: string | null;
  boq_revision?: number;
  sealed?: boolean;                  // B7/A7 · true when tender_due_at set & bids not yet opened
  work_items: WorkItem[];
  engagements: Engagement[];
  discussions: Discussion[];
}

// B7/A7 · the human 开标 (bid opening) ceremony — unseals the comparison after the deadline.
export function openBids(pid: string) {
  return apiSubmit<PackageSummary>(`/packages/${pid}/open-bids`, {});
}

// WS2 · mid-tender BOQ revise / supplement / re-issue (staff side).
export function addWorkItem(pid: string, body: Partial<WorkItem>) {
  return apiSubmit<WorkItem>(`/packages/${pid}/work-items`, body);
}
export function patchWorkItem(pid: string, wid: string, body: Partial<WorkItem>) {
  return api<WorkItem>(`/packages/${pid}/work-items/${wid}`, { method: 'PATCH', body: JSON.stringify(body) });
}
export function deleteWorkItem(pid: string, wid: string) {
  return api<{ ok: boolean }>(`/packages/${pid}/work-items/${wid}`, { method: 'DELETE' });
}
export function bulkDeleteWorkItems(pid: string, body: { ids?: string[]; all?: boolean; include_bids?: boolean }) {
  return apiSubmit<{ ok: boolean; deleted: number; bids_cleared?: number }>(`/packages/${pid}/work-items/bulk-delete`, body);
}
export function reissueBoq(pid: string) {
  return apiSubmit<{ ok: boolean; boq_revision: number; changed_items: WorkItem[] }>(`/packages/${pid}/reissue`, {});
}
// 7/2 walkthrough: one-click "tell labor to re-quote" after a clear-bids re-import / 清空(含报价).
export function notifyReimport(pid: string) {
  return apiSubmit<{ ok: boolean; notified: number }>(`/packages/${pid}/notify-reimport`, {});
}

// ── WTL brain integration (INTEGRATIONS.md §1) ──────────────────────────────
export interface WtlStatus {
  enabled: boolean;
  configured: boolean;
  base_url: string | null;
  active_base: string | null;
  dept_code: string;
  tool_code: string;
  snapshot_interval_min: number;
  subscriptions: Array<{ code: string; label: string }>;
}

export interface WtlProject {
  uuid: string;
  name: string;
  address?: string;
  project_owner_name?: string;
  stage?: string;
  sign_date?: string;
}

export interface WtlProjectsResp {
  configured: boolean;
  projects: WtlProject[];
  has_more: boolean;
  error?: string;
}

export interface WtlEvent {
  id: string;
  event_id: string;
  kind: string;
  source_file_name: string;
  as_of: string;
  summary_md: string;
  applied: boolean;
  entity_writes: string[];
  stub_todos: string[];
  created_at: string;
}

export interface WtlPullResult {
  configured: boolean;
  events_synthesized: number;
  entity_writes: string[];
  stub_todos: string[];
  skipped_duplicates: number;
}

// v6 — the flat 9 fixed top-level folders + the configured publish top/sub.
export interface WtlCategoriesResp {
  configured: boolean;
  tops: string[];               // the 9 fixed category_top names
  selected: string | null;      // configured publish category_top
  selected_sub?: string | null; // configured publish category_sub (optional 2nd level)
  error?: string;
}

// G4 — semantic topic search (云存储 §7): analyzed facts ("claims") with provenance.
export interface WtlClaim {
  predicate: string;
  object_text: string;
  category_path?: string;
  confidence?: number;
  chunk?: { text_preview?: string; start_char?: number; end_char?: number };
}
export interface WtlTopicSearchResp {
  configured: boolean;
  query: string;
  matched_categories: { category_path: string; score: number }[];
  claims: WtlClaim[];
  total: number;
  error?: string;
}

// ── Labor-tender sync (labor-sync-build-spec-v3 §7) ─────────────────────────
// One ledger row per pulled -AI 招标文件 blob (dedup key = blob_sha256). status walks
// seen → downloaded → staged → committed (or skipped_404 / error).
export interface LaborTenderIngestRow {
  id: string;
  package_id: string | null;
  staging_id: string | null;                 // set once Stage 3 stages the workbook — drives 「一键填充」
  wtl_project_uuid: string | null;
  project_name: string | null;
  category_code: string | null;
  category_sub: string | null;               // verbatim 07/08/09-…招标文件
  file_name: string | null;
  status: 'seen' | 'downloaded' | 'skipped_404' | 'error' | 'staged' | 'committed';
  error_note: string | null;
  first_seen_at: string;
  last_seen_at: string;
}
export interface TenderSyncSettings {
  enabled: boolean;
  dry_run: boolean;
  pilot_project_uuids: string[];
  // 高强's canonical BOQ header list — PENDING. Empty ⇒ column show/hide stays inert (all columns shown).
  canonical_boq_headers?: string[];
}
/** Per-package ingest ledger (spec §7 item 2). */
export function packageTenderSync(pid: string) {
  return api<{ rows: LaborTenderIngestRow[] }>(`/packages/${pid}/tender-sync`);
}
/** Global sync snapshot — last-50 ledger + the settings (incl. the pending canonical_boq_headers). */
export function tenderSyncStatus() {
  return api<{ settings: TenderSyncSettings; rows: LaborTenderIngestRow[] }>(`/wtl/tender-sync/status`);
}
/** Rehydrate a staged import so XlsImportModal can open AT the review step (采购大脑「一键填充」). */
export function tenderStaging(sid: string) {
  return api<any>(`/wtl/tender-sync/staging/${sid}`);
}

export const WTL_KIND_LABEL: Record<string, string> = {
  boq_update: 'BOQ 同步', drawing_update: '图纸更新', scope_change: '范围变更',
  spec_revision: '技术条件', budget_revision: '预算修订', scope_confirmed: '范围确认',
};

export const STAGE_LABEL: Record<number, string> = {
  0: '立项 / Intake', 2: '触达 / Outreach', 6: '收标 / Submission', 7: '清标 / Cleaning',
  8: '对比 / Comparison', 9: '议价 / Negotiation', 10: '群评审 / Group Review',
  11: '入围 / Shortlist', 12: '定标 / Awarded',
};

export function rmb(n: number | null | undefined): string {
  if (n == null) return '—';
  return '¥' + Math.round(n).toLocaleString('zh-CN');
}
/** ¥ keeping the 分 (改改 9/21) — for a figure that must match a signed paper to the cent: the 合同额 amendment
 *  preview, its reachable-amount buttons and the 中标价 vs 合同额 line. rmb() rounds to the yuan, so two
 *  candidates a 分 apart (¥169,999.99 / ¥170,000.01) would both read ¥170,000. Same format as wiRate(). */
export function rmbCents(n: number | null | undefined): string {
  if (n == null) return '—';
  return '¥' + n.toLocaleString('zh-CN', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

// ── Stage 9 negotiation agent (Cycle 2) ────────────────────────────────────
export interface BidFeedback {
  id?: string;
  engagement_id?: string;
  round_number?: number;
  ai_suggestions_json?: string;
  negotiation_json?: string | null;
  draft_message_md?: string | null;
  sent_message_md?: string | null;
  outcome: string | null;
  status?: 'draft' | 'sent';
  session_status?: string;
  /** Set when no draft was produced (transient failure or awaiting_human). */
  note?: string;
  /** Phase 3 — populated by /bid_feedback/:fid/send: the WeCom push outcome of the sent message. */
  delivery_status?: 'pending' | 'success' | 'dev_mode' | 'failure';
  dev_mode?: boolean;
}
export function draftNegotiation(pid: string, eid: string, message?: string) {
  return api<BidFeedback>(`/packages/${pid}/engagements/${eid}/negotiate`, { method: 'POST', body: JSON.stringify({ message }) });
}
export function sendFeedback(fid: string, message: string) {
  return api<BidFeedback>(`/bid_feedback/${fid}/send`, { method: 'POST', body: JSON.stringify({ message }) });
}
// 议价最终价确认 · staff mark the negotiated final total and request the labor party's confirmation.
export interface FinalConfirmState { final_confirm_requested_at: string | null; final_total_rmb: number | null; final_confirmed_at: string | null; final_offer_bid_id?: string | null; delivery_status?: string; dev_mode?: boolean; }
export function requestFinalConfirm(pid: string, eid: string, finalTotalRmb?: number) {
  return apiSubmit<FinalConfirmState>(`/packages/${pid}/engagements/${eid}/request-final-confirm`, finalTotalRmb != null ? { final_total_rmb: finalTotalRmb } : {});
}
// Phase B · staff author the final-offer round line-by-line (议价后逐项定价) and request confirmation.
// fb_e5f31dc1: taxPct = the single 税金点 shown with the locked figure (不含税 · 税率 · 含税).
export function staffSubmitFinalOffer(pid: string, eid: string, lineItems: { work_item_id: string | null; unit_price_rmb: number; item_label?: string; qty?: number }[], taxPct?: number | null) {
  return apiSubmit<FinalConfirmState & { round_number: number }>(`/packages/${pid}/engagements/${eid}/final-offer`, { line_items: lineItems, ...(taxPct != null ? { tax_pct: taxPct } : {}) });
}

/** fb_e5f31dc1 · 「不含税 ¥X · 税率 y% · 含税 ¥Z」— the three-figure display for a negotiated final price.
 *  Falls back to the bare ex-tax figure when no 税率 was set. */
export function finalPriceLabel(totalExRmb: number | null | undefined, taxPct: number | null | undefined): string {
  if (totalExRmb == null) return '—';
  if (taxPct == null) return rmb(totalExRmb);
  const incl = Math.round(totalExRmb * (100 + taxPct)) / 100;
  return `不含税 ${rmb(totalExRmb)} · 税率 ${taxPct}% · 含税 ${rmb(incl)}`;
}
/** 改改 9/20 · 「含税 ¥Y（不含税 ¥X · 税率 N%）」— the staff-side reading order, 含税 first.
 *  Takes the two figures the server already computed rather than re-deriving one, so a rounding rule
 *  only ever lives in one place. Falls back to the ex-tax figure + 「税率未录入」 when there is no 税率. */
export function inclTaxLabel(exRmb: number | null | undefined, inclRmb: number | null | undefined, taxPct: number | null | undefined): string {
  if (taxPct == null || inclRmb == null) return exRmb == null ? '—' : `${rmb(exRmb)}（${TAX_PCT_MISSING_HINT}）`;
  return `含税 ${rmb(inclRmb)}（不含税 ${rmb(exRmb)} · 税率 ${taxPct}%）`;
}
/** 改改 9/20 · shown wherever a 含税 figure cannot be formed because the lock has no 税率. */
export const TAX_PCT_MISSING_HINT = '税率未录入 — 显示不含税 / no tax rate — showing pre-tax';
/** The friendly reading of the server's 409 `tax_pct_missing` on any money route. 改改 9/21: 撤销锁定 is refused
 *  once the subcontract is signed, so a signed contract's rate is filled with 「补录税率」 in the 分包合同 block. */
export const TAX_PCT_MISSING_ERROR = '该合同未录入税率 — 已签署的请在「分包合同」处「补录税率」，未签署的请在锁价处补录 / no tax rate on this contract — signed: use 「补录税率」 in the Subcontract block; unsigned: enter it on the price lock';
// Phase B · staff escape hatch — release a pending/locked final offer to keep negotiating.
export function reopenFinal(pid: string, eid: string) {
  return apiSubmit<{ ok: boolean }>(`/packages/${pid}/engagements/${eid}/reopen-final`, {});
}

// ── Execution & closeout console (V8/V9/V11) ────────────────────────────────
/** (华控 9/16) Metadata for the stored scan of the chopped, signed contract. */
export interface ExecutedScanMeta { id: string; file_name: string; size_bytes: number | null; mime_type: string | null; }
export interface SubcontractRow {
  contract_form?: 'unit_price' | 'lump_sum' | null;   // review 9/18: exposed by /packages/:pid/subcontracts
  id: string; contract_id: string | null; value_rmb: number | null;
  // 改改 9/20 · `value_rmb` is the LOCKED ex-tax price (it is what the 锁价 wrote). The 含税 twin is what
  // staff read and what payment now runs on; null when the lock carries no 税率.
  final_tax_pct?: number | null; value_incl_tax_rmb?: number | null;
  advance_pct: number | null; retention_pct: number | null; dlp_months: number | null;
  payment_linkage: string | null;   // 背靠背 back_to_back | independent | null
  status: string; signed_at: string | null; labor_co_name: string | null;
  // 华控 9/16 · the signature carries its evidence. signed_on = the date ON the paper (signed_at is
  // when the button was clicked); executed_file = the chopped scan that justifies status 'signed'.
  signed_on?: string | null;
  executed_file?: ExecutedScanMeta | null;
  standards_confirmed_at?: string | null;
  // 改改 9/21 · the 中标价 — the 议价 lock (engagements.final_total_rmb), 不含税 — and its server-computed 含税
  // twin (null when the lock carries no 税率), so a contract signed at a different amount than the award
  // reads as a difference. Optional: an older server (or a cached bundle mid-deploy) omits both.
  award_total_rmb?: number | null;
  award_total_incl_tax_rmb?: number | null;
  /** 改改 9/21 · the latest 合同额 amendment (中标价 ± 调整项 = 合同额), null when the value was never amended. */
  value_amendment?: ValueAmendment | null;
  /** 改改 9/21 (fb_9cf4aaea) · how the plan pays — see PaymentMode. Optional: an older server omits it (read as
   *  'recovery', and the mode switch is not offered). */
  payment_mode?: PaymentMode;
  /** The 结算 stage of an installment plan (「竣工验收合格后支付至结算额 95%」); null in recovery mode. */
  settlement_stage?: SettlementStage | null;
}
/**
 * 改改 9/21 (fb_9cf4aaea 「质保金前的这笔付款，是支付至项目结算额的95%」) · two ways a contract pays:
 * - 'recovery'    — the advance is recovered pro rata from each progress batch and the retention is withheld from
 *                   each; milestones[].pct is the GROSS 计量 share (the editor converts from the cash % she types).
 * - 'installment' — 按节点支付: the 首期款 (advance_pct) is paid on signing and NOT recovered, each stage pays
 *                   milestones[].pct of the contract AS IS, and the 结算 stage brings the total up to
 *                   settle_to_pct of the settled amount (estimated on the contract value until the settlement).
 */
export type PaymentMode = 'recovery' | 'installment';
export interface SettlementStage { label_zh: string; label_en: string | null; settle_to_pct: number }
/** One signed 调整项 of a 合同额 amendment, in the same basis (含税 / 不含税) as the amount it was sent with. */
export interface ContractValueAdjustment { label: string; amount_rmb: number }
/**
 * 改改 9/21 · the recorded 合同额 amendment. `input_amount` + `adjustments` are in `input_basis`; the stored
 * values (`from_value_rmb` / `to_value_rmb`) and `award_total_rmb` (the 中标价 at the time) are 不含税.
 */
export interface ValueAmendment {
  at: string;
  by: string | null;
  reason: string;
  input_basis: 'incl_tax' | 'excl_tax';
  input_amount: number | null;
  award_total_rmb: number | null;
  adjustments: ContractValueAdjustment[] | null;
  from_value_rmb: number | null;
  to_value_rmb: number | null;
}
// 改改 9/20 · PAYMENT runs on 含税 now: certified_amount_rmb, advance_amount_rmb, net_due_rmb,
// settlement_balance_rmb and retention_amount_rmb are all tax-inclusive as the server returns them.
export interface ExecPaymentCert {
  id: string; cert_kind: string; milestone_id: string | null; certified_amount_rmb: number | null; state: string; upstream_received_at: string | null;
  // 改改 9/21 (fb_8ef675e1) · the cert's 发票, filed in the 履约档案 as H2 「增值税专用发票」. Optional so an
  // older server (or a cached bundle mid-deploy) reads as 「未附发票」 instead of throwing.
  fapiao_item_id?: string | null;
  fapiao_note?: string | null;
  fapiao_files?: CertFapiaoFile[];
  /** The H2 slot's review state; null = no slot yet. Only 'confirmed' opens → 发票已附 / → 已付款. */
  fapiao_status?: CertFapiaoStatus | null;
  /** 退回理由 when the invoice was returned. */
  fapiao_review_note?: string | null;
  // 改改 9/22 (fb_49bb4f23 / fb_91161ab6) · the approval chain shown under each cert. Optional: an older server
  // sends neither, and the strip then falls back to the current state alone.
  /** Full transition trail, reverts included, oldest first. */
  state_history?: CertStateHistoryEntry[];
  /** Who recorded the 背靠背 甲方 receipt (beside upstream_received_at). */
  upstream_received_by?: string | null;
}
export interface CertStateHistoryEntry { from: string | null; to: string; actor: string | null; notes: string | null; at: string; reverted: boolean }
export type CertFapiaoStatus = 'missing' | 'uploaded' | 'confirmed' | 'returned' | 'waived';
/** One invoice file on a payment cert — downloads through staffComplianceFileUrl(id). */
export interface CertFapiaoFile { id: string; file_name: string; size_bytes: number | null; mime_type: string | null; created_at: string | null }
/** POST /payment_certs/:certId/fapiao result (改改 9/21 · fb_8ef675e1). `transitioned` is set only when
 *  advance=1 moved a 采购已核 cert on to 发票已附 in the same step. */
export interface UploadCertFapiaoResult {
  ok: boolean;
  cert: { id: string; state: string; fapiao_item_id: string | null; fapiao_note: string | null; fapiao_files: CertFapiaoFile[] };
  transitioned: { from: string; to: string } | null;
}
/** The server's per-upload caps for a cert's invoice (same as the 履约档案 slots): 10 files, 30 MB each. */
export const CERT_FAPIAO_MAX_FILES = 10;
export const CERT_FAPIAO_MAX_BYTES = 30 * 1024 * 1024;
/** The invoice formats the route accepts — PDF / OFD / images only (no Word/Excel). The picker's accept list. */
export const CERT_FAPIAO_ACCEPT = '.pdf,.ofd,.jpg,.jpeg,.png,.heic,.heif,.webp,.gif';
/** Roles the POST /payment_certs/:certId/fapiao route admits — the pickers are hidden for everyone else. */
const CERT_FAPIAO_ROLES = new Set(['gm', 'budget', 'procurement', 'commercial', 'finance', 'agent']);
export function canUploadCertFapiao(role: string | null | undefined): boolean {
  return CERT_FAPIAO_ROLES.has(role ?? '');
}
export interface ExecAdvanceRelease { id: string; advance_amount_rmb: number | null; recovered_to_date_rmb: number; recovery_basis: string | null; }
export interface ExecChangeOrder { id: string; seq: number; status: string; priced_amount_rmb: number | null; cumulative_variation_pct: number | null; }
export interface ExecSettlement { id: string; net_due_rmb: number | null; settlement_balance_rmb: number | null; audit_required: number; status: string; }
export interface ExecRetention { id: string; retention_amount_rmb: number | null; bd_check_favorable: number | null; gm_approved: number | null; dlp_ends_at: string | null; status: string; }
export interface ExecCalendarAlert { id: string; kind: string; due_at: string | null; fire_at: string | null; status: string; }
/**
 * 付款计划 / payment schedule (改改 9/20 · fb_3267bf3b). Mirrors server/src/payments/schedule.ts —
 * `planned_amount_rmb` is 含税 whenever the contract carries a 税率, and null whenever the server had
 * nothing to compute from (a null pct, or no value). NEVER re-derive a 含税 figure here: display what
 * the server sent, exactly as ContractValueBlock does.
 */
export type ScheduleKind = 'advance' | 'progress' | 'settlement' | 'retention';
export interface PaymentScheduleRow {
  id: string;
  kind: ScheduleKind;
  milestone_id: string | null;
  label_zh: string | null;
  label_en: string | null;
  planned_pct: number | null;
  planned_amount_rmb: number | null;
  retention_held_rmb: number | null;
  advance_recovery_rmb: number | null;
  due_basis: string | null;
  sort_order: number;
  /** 改改 9/21 (「几笔款总合计不能超合同额」) · what this row actually PAYS, 含税: the advance; a progress batch's
   *  计量 − 扣回预付 − 预扣质保; the 质保金 released after the DLP. Σ over the rows = the contract value.
   *  Optional so an older server degrades to 「—」. */
  net_payable_rmb?: number | null;
  /** net_payable as a % of the contract value — the 「占合同额 %」 column, server-computed. */
  payable_pct?: number | null;
  /** 改改 9/21 (fb_9cf4aaea) · the 'settlement' row once a settlement exists: what the 结算 actually pays (含税).
   *  planned_amount_rmb stays the estimate; summary.settlement_is_estimate turns false. */
  settlement_actual_rmb?: number | null;
}
export interface PaymentScheduleSummary {
  advance_amount_rmb: number | null;
  total_progress_gross_rmb: number;
  total_retention_held_rmb: number;
  total_advance_recovery_rmb: number;
  milestone_pct_sum: number;
  balanced: boolean;            // Σ milestone pct ≈ 100 (±0.5)
  retention_compliant: boolean; // retention_pct <= 3 (建质〔2017〕138号 §7) — NOT shown in the UI any more: 质保金 5% is the company standard (改改 9/22)
  has_unset_pcts: boolean;
  pcts_in_range: boolean;
  any_negative_net: boolean;
  /** 改改 9/21 · Σ net_payable_rmb over the rows, the 含税 contract value it must equal, and whether it does. */
  total_payable_rmb?: number | null;
  contract_value_basis_rmb?: number | null;
  payable_matches_contract?: boolean;
  /** 改改 9/21 (fb_9cf4aaea) · the plan's mode, the 结算 stage's 付至 %, and whether the settlement row is still an
   *  estimate (true until a settlement fixes the 结算额). Optional: an older server omits them. */
  payment_mode?: PaymentMode;
  settle_to_pct?: number | null;
  settlement_is_estimate?: boolean;
}
/** `summary` is null when the server could not read the subcontract back (payments/subcontract.ts). */
export interface PaymentScheduleView { rows: PaymentScheduleRow[]; summary: PaymentScheduleSummary | null }
/** One editable batch in 编辑付款批次. `id` absent = a new row the server will mint an id for. */
export interface PaymentPlanMilestoneInput { id?: string; label_zh: string; label_en?: string; pct: number; due_basis?: string }
/** PUT /subcontracts/:scId/payment-plan. `payment_mode` + `settlement_stage` (改改 9/21 · fb_9cf4aaea) are optional:
 *  omitted = the mode stays as it is; settlement_stage is required in 'installment' and null in 'recovery'. */
export interface SetPaymentPlanBody {
  milestones: PaymentPlanMilestoneInput[];
  reason?: string;
  payment_mode?: PaymentMode;
  settlement_stage?: SettlementStage | null;
}

/**
 * 改改 9/21 (fb_9bc4f320 「能否系统直接识别合同里的付款方式？」) · what the AI read off the executed scan. A PROPOSAL:
 * the route saves nothing, and advance_pct / retention_pct are reported, never applied. milestones[].pct is the
 * share of the contract paid at that stage (the paper's own figure).
 */
export interface PaymentTermsProposal {
  payment_mode: PaymentMode;
  advance_pct: number | null;
  advance_label: string | null;
  milestones: Array<{ label_zh: string; label_en: string | null; pct: number; acceptance_note: string | null }>;
  settlement_stage: SettlementStage | null;
  retention_pct: number | null;
  retention_release_note: string | null;
  clause_refs: string[];
  notes: string[];
}
export interface PaymentTermsExtractResult {
  ok: boolean;
  proposal: PaymentTermsProposal;
  source: { file_id: string; file_name: string };
  model: string;
}

export interface ExecutionSummary {
  subcontract: SubcontractRow & Record<string, unknown>;
  executed_file?: ExecutedScanMeta | null;   // 华控 9/16 · the executed-contract scan
  // 改改 9/20 (fb_3267bf3b) · optional so an older server (or a cached bundle mid-deploy) degrades to
  // 「付款计划尚未生成」 instead of throwing on `.rows` of undefined.
  payment_schedule?: PaymentScheduleView | null;
  payment_certs: ExecPaymentCert[];
  advance_release: ExecAdvanceRelease | null;
  change_orders: ExecChangeOrder[];
  settlements: ExecSettlement[];
  retention_releases: ExecRetention[];
  calendar_alerts: ExecCalendarAlert[];
}
export function getPackageSubcontracts(pid: string) { return api<SubcontractRow[]>(`/packages/${pid}/subcontracts`); }
export function getExecution(scId: string) { return api<ExecutionSummary>(`/subcontracts/${scId}/execution`); }

// ── st.15 acceptance scorecard (§15) ────────────────────────────────────────
export type CriterionStatus = 'pending' | 'in_remediation' | 'met' | 'waived';
export type AcceptanceOverall = 'in_progress' | 'passed' | 'escalated';
export interface AcceptanceCriterion { id: string; trade: string | null; label_zh: string | null; label_en: string | null; pass_rule_md: string | null; is_critical: boolean; sort_order: number; }
export interface CriterionState { criterion_id: string; status: CriterionStatus; iteration: number; escalated: boolean; met_at: string | null; }
export interface AcceptanceFinding { id: string; criterion_id: string; iteration: number; photo_path: string | null; notes_md: string | null; status: string; resolution_evidence_path: string | null; resolution_notes_md: string | null; found_at: string | null; resolution_at: string | null; }
export interface AcceptanceScorecard { id: string; acceptance_round: number; overall_status: AcceptanceOverall; max_acceptance_iterations: number; states: CriterionState[]; }
export interface AcceptanceSummary { criteria: AcceptanceCriterion[]; scorecard: AcceptanceScorecard | null; findings: AcceptanceFinding[]; }
export function getAcceptance(scId: string) { return api<AcceptanceSummary>(`/subcontracts/${scId}/acceptance`); }

export const acceptanceActions = {
  open: (scId: string) => apiSubmit(`/subcontracts/${scId}/acceptance/open`, {}),
  recordFinding: (sid: string, criterionId: string, notesMd?: string) => apiSubmit(`/acceptance/${sid}/findings`, { criterionId, notesMd }),
  remediate: (fid: string, notesMd?: string) => apiSubmit(`/acceptance/findings/${fid}/remediation`, { notesMd }),
  markMet: (sid: string, cid: string) => apiSubmit(`/acceptance/${sid}/criteria/${cid}/met`, {}),
  failReInspection: (sid: string, cid: string) => apiSubmit(`/acceptance/${sid}/criteria/${cid}/fail`, {}),
  waive: (sid: string, cid: string, note?: string) => apiSubmit(`/acceptance/${sid}/criteria/${cid}/waive`, { gmApproved: true, note }),
};
export const CRITERION_STATUS: Record<string, { label: string; pill: string }> = {
  pending: { label: '待检 / pending', pill: 'pill-muted' },
  in_remediation: { label: '整改中 / remediation', pill: 'pill-warn' },
  met: { label: '已合格 / met', pill: 'pill-good' },
  waived: { label: '已豁免 / waived', pill: 'pill-stage' },
};

// ── st.16 GM final inspection (终验) — deterministic, key-free manual path ────
export type FindingSeverity = 'minor' | 'major' | 'blocking';
export interface FinalInspectionFinding { label_md: string; severity: FindingSeverity; gm_missed_by_pm: boolean; related_criterion_id: string; }
export interface FinalInspectionRow { id: string; status: 'in_progress' | 'passed'; findings: FinalInspectionFinding[]; rationale_zh: string | null; gm_attendee: string | null; inspected_at: string | null; }
export interface FinalInspectionSummary { final_inspection: FinalInspectionRow | null; criteria: AcceptanceCriterion[]; }
export interface FinalInspectionResult { final_inspection_id: string; passed: boolean; gm_missed_count: number; blocking_count: number; three_way: { gmMissedCount: number; pmRecordWritten: boolean; crmRecordWritten: boolean; reputationCaptured: boolean }; }
export function getFinalInspection(scId: string) { return api<FinalInspectionSummary>(`/subcontracts/${scId}/final-inspection`); }
export const finalInspectionActions = {
  record: (scId: string, body: { gmAttendee?: string; pmName?: string; rationaleZh?: string; findings: FinalInspectionFinding[] }) =>
    apiSubmit<FinalInspectionResult>(`/subcontracts/${scId}/final-inspection`, body),
};
export const SEVERITY_LABEL: Record<FindingSeverity, { label: string; pill: string }> = {
  minor: { label: '轻微 / minor', pill: 'pill-muted' },
  major: { label: '一般 / major', pill: 'pill-warn' },
  blocking: { label: '阻断 / blocking', pill: 'pill-bad' },
};

export const CERT_STATES = ['created', 'pm_signed', 'procurement_approved', 'fapiao_attached', 'finance_released'] as const;
export function nextCertState(s: string): string | null { const i = CERT_STATES.indexOf(s as any); return i >= 0 && i < CERT_STATES.length - 1 ? CERT_STATES[i + 1] : null; }
export function prevCertState(s: string): string | null { const i = CERT_STATES.indexOf(s as any); return i > 0 ? CERT_STATES[i - 1] : null; }

/** 撤销签署 / revert-signature result (改改 9/20 · fb_3267bf3b). */
export interface UnsignSubcontractResult {
  ok: boolean;
  subcontract: SubcontractRow & Record<string, unknown>;
  removed: { payment_certs: number; advance_releases: number };
  package_stage: { from: number | null; to: number | null };
}
/** 编辑付款批次 / payment-plan save result (改改 9/20 · fb_3267bf3b). */
export interface SetPaymentPlanResult {
  ok: boolean;
  subcontract: SubcontractRow & Record<string, unknown>;
  payment_schedule: PaymentScheduleView;
}
/**
 * 调整合同额 / amend the contract value (改改 9/21) — on a DRAFT (before signing: the .docx then prints it) or a
 * SIGNED subcontract (the chopped paper governs; this record catches up to it). 「华控劳务合同已经签订了，只是合同
 * 额在中标价基础上有调整」. Exactly ONE amount — the 含税 figure the paper states (the usual case) or the 不含税
 * one; the union makes sending both, or neither, a compile error. `adjustments` are her own 调整项 (signed, in
 * the SAME basis as the amount) and are recorded, not summed: the amount is taken as typed. `dry_run: true`
 * answers the same shape and writes nothing.
 */
type AmendContractValueCommon = { reason: string; adjustments?: ContractValueAdjustment[]; dry_run?: boolean };
export type AmendContractValueBody =
  | ({ value_incl_tax_rmb: number; value_rmb?: never } & AmendContractValueCommon)
  | ({ value_rmb: number; value_incl_tax_rmb?: never } & AmendContractValueCommon);
/** One side (before / after) of a 合同额 amendment — every figure server-computed, 含税 off the lock's 税率. */
export interface ContractValueFigures {
  value_rmb: number | null;
  value_incl_tax_rmb: number | null;
  baseline_value_rmb: number | null;
  advance_amount_rmb: number | null;
}
export interface AmendContractValueResult {
  ok: boolean;
  dry_run: boolean;
  tax_pct: number;
  from: ContractValueFigures;
  to: ContractValueFigures;
  /** The un-released 预付款凭证, re-amounted with the advance; its state may step back so the new figure is re-approved. */
  advance_cert: {
    id: string; from_amount: number | null; to_amount: number | null; from_state: string; to_state: string;
    /** A 背靠背 甲方 receipt recorded for the OLD amount was cleared (review R4). */
    upstream_receipt_cleared?: boolean;
    /** 改改 9/21 (fb_8ef675e1) · the amount changed, so the invoice attached for the old figure is returned (the
     *  link is kept; it must be reissued for the new amount). */
    fapiao_returned?: boolean;
  } | null;
  payment_schedule: PaymentScheduleView;
  /** Present only on a real (non-dry-run) amendment. */
  subcontract?: SubcontractRow & Record<string, unknown>;
}
/** 补录税率 / fill a missing 税率 on a signed subcontract — result (改改 9/21). */
export interface SetTaxRateResult { ok: boolean; subcontract: SubcontractRow & Record<string, unknown> }
/** A reachable pair offered with 400 `incl_tax_unreachable` (body.candidates): no 2-decimal 不含税 figure × 税率
 *  lands exactly on the typed 含税, so the server names the nearest ones that do. */
export interface ContractValueCandidate { value_rmb: number; value_incl_tax_rmb: number }

/** Execution write actions (Increment B). apiSubmit surfaces the JSON error body — e.g. settlement approve
 *  throws with body.requiresAudit when the §16d gate blocks it. The server re-derives every gate. */
export const execActions = {
  generateSubcontract: (pid: string, body: { value_rmb?: number | null; advance_pct?: number | null; retention_pct?: number | null; dlp_months?: number | null; contract_id?: string }) => apiSubmit(`/packages/${pid}/subcontract`, body),
  // 华控 9/16 · 记录已签署 uploads the EXECUTED paper: the chopped/signed scan + the date on it (+ 合同编号
  // if the paper carries one). A bare click is refused 409 executed_scan_required — that is the point.
  // Also 409 standards_not_confirmed / contract_not_generated, 400 signed_on_invalid, 415 unsupported_scan_type.
  signSubcontract: (scId: string, body: { scan: File; signedOn: string; contractId?: string }) => {
    const fd = new FormData();
    fd.append('signed_on', body.signedOn);
    if (body.contractId?.trim()) fd.append('contract_id', body.contractId.trim());
    fd.append('scan', body.scan);
    return apiUpload<{ ok: boolean; signed: boolean; signed_on: string; executed_file_id: string }>(`/subcontracts/${scId}/sign`, fd);
  },
  // 改改 9/20 (fb_3267bf3b) · 「合同已下载但并未签订，就可以申请执行预付款」— a signature recorded against
  // paper that was never chopped used to need Eric to hand-run a script. gm/budget can now undo it in-app:
  // the subcontract drops back to draft, the advance cert + 预付款台账 are deleted and the package returns
  // to st.12. Refused 409 once the money actually moved (cert_released / advance_recovery_started /
  // settlement_exists / retention_exists / instructions_exist / acceptance_started / stage_advanced) —
  // each 409 carries a bilingual `detail` that says which one. The executed scan is KEPT as evidence.
  unsignSubcontract: (scId: string, body: { reason: string }) =>
    apiSubmit<UnsignSubcontractResult>(`/subcontracts/${scId}/unsign`, body),
  // 改改 9/20 (fb_3267bf3b) · 「每次的付款比例及金额…提交申请各批次款项」— replaces the single 竣工 100%
  // milestone with the real per-batch plan. Rows WITH an id keep it (their certs stay attached); rows
  // without get a server id. 409 milestone_certified (payload milestone_id) when a batch already has a
  // progress cert, 409 settlement_exists once the settlement is drafted.
  // 改改 9/21 (fb_9cf4aaea) · + payment_mode / settlement_stage. 400 settlement_stage_required /
  // settlement_stage_invalid / installments_exceed_settlement (+ sum, settle_to_pct) / settlement_stage_not_allowed;
  // 409 progress_certs_exist / advance_recovery_started on a mode switch — each with a bilingual `detail`.
  setPaymentPlan: (scId: string, body: SetPaymentPlanBody) =>
    apiSubmit<SetPaymentPlanResult>(`/subcontracts/${scId}/payment-plan`, body, 'PUT'),
  // 改改 9/21 (fb_9bc4f320) · 从签订版合同读取付款方式 — gm | budget | agent, no body, 1–3 minutes (an AI read of the
  // executed scan). Saves NOTHING: the proposal prefills the batch editor and 保存 goes through setPaymentPlan.
  // 409 no_executed_scan, 415 unsupported_scan_type, 413 scan_too_large_for_ai, 503 ai_unavailable.
  extractPaymentTerms: (scId: string) =>
    apiSubmit<PaymentTermsExtractResult>(`/subcontracts/${scId}/payment-terms/extract`, undefined),
  createProgressCert: (scId: string, body: { milestone_id: string; certified_amount_rmb: number; retention_held_rmb?: number; period_label?: string }) => apiSubmit(`/subcontracts/${scId}/payment_certs`, body),
  // 预付款 (改改 9/20 · fb_f81de749): the advance is certified against the advance ledger, not a milestone.
  // 409 no_advance_terms when the subcontract has no advance — set it with setAdvanceTerms first.
  createAdvanceCert: (scId: string, body: { certified_amount_rmb?: number } = {}) => apiSubmit<{ ok: boolean; cert_id: string; certified_amount_rmb: number }>(`/subcontracts/${scId}/payment_certs`, { cert_kind: 'advance', ...body }),
  // Set / amend the advance % — reason REQUIRED once signed (the paper governs; the record catches up).
  // 409 payment_certs_exist once any cert exists (the terms are frozen under certs), 400 advance_pct_invalid.
  setAdvanceTerms: (scId: string, body: { advance_pct: number | null; reason?: string }) => apiSubmit<{ ok: boolean; subcontract: SubcontractRow; advance_release: ExecAdvanceRelease | null }>(`/subcontracts/${scId}/advance-terms`, body),
  // 改改 9/21 · 调整合同额 (AmendContractValueBody) — gm | budget | agent, draft AND signed subcontracts; the
  // advance ledger, an un-released 预付款凭证 and the payment plan follow the new value. 400 reason_required /
  // value_invalid / adjustments_invalid / incl_tax_unreachable (+ body.candidates); 409 tax_pct_missing,
  // settlement_exists, retention_exists, change_orders_exist, progress_certs_exist, cert_in_review,
  // cert_released, advance_recovery_started, advance_cert_partial, value_unchanged — each with a bilingual `detail`.
  amendContractValue: (scId: string, body: AmendContractValueBody) =>
    apiSubmit<AmendContractValueResult>(`/subcontracts/${scId}/contract-value`, body),
  // 改改 9/21 · 补录税率 — a SIGNED contract whose lock never carried a 税金点 derives no 含税 figure (every money
  // action 409s tax_pct_missing), and 撤销锁定 is refused once signed, so this is the only fix: it fills the
  // missing rate once, reason required. 400 reason_required / tax_pct_invalid; 409 not_signed /
  // tax_pct_already_set — each with a bilingual `detail`.
  setTaxRate: (scId: string, body: { tax_pct: number; reason: string }) =>
    apiSubmit<SetTaxRateResult>(`/subcontracts/${scId}/tax-rate`, body),
  // The server stamps the actor from the signed-in account — a hard-coded '操作员 / op' here used to put every
  // cert step on prod under that one name. → 发票已附 and → 已付款 are refused 409 fapiao_required until a
  // CONFIRMED invoice is on file (the detail says 未附发票 or 发票已被退回：…).
  transitionCert: (certId: string, toState: string, notes?: string) => apiSubmit(`/payment_certs/${certId}/transition`, { toState, notes }),
  revertCert: (certId: string, notes: string) => apiSubmit(`/payment_certs/${certId}/revert`, { notes }),
  markUpstreamReceived: (certId: string) => apiSubmit(`/payment_certs/${certId}/upstream-received`, {}),  // 背靠背: confirm 甲方→WTL receipt
  // 改改 9/21 (fb_8ef675e1) · 「预付款的凭证应该是发票，这里上传不了」— attach the cert's invoice (filed in the 履约档案
  // as H2). `advance` also moves a 采购已核 cert to 发票已附 in the same step (ignored at other states); `replace`
  // swaps the files instead of adding (the two combine: re-upload a returned invoice and advance). Roles: gm /
  // budget / procurement / commercial / finance / agent. 400 no_file / too_many_files, 415 unsupported_file_type
  // (PDF / OFD / images only), 413|400
  // file_too_large, 404 cert_not_found, 409 cert_released (replace on a paid cert) / settlement_exists
  // (advance after 结算) — each with a bilingual `detail`. The caps are checked here first so a 300 MB pick
  // is refused before it is sent, and a proxy 413 with no JSON body still reads as file_too_large.
  uploadCertFapiao: async (certId: string, files: File[], opts: { advance?: boolean; replace?: boolean; note?: string } = {}) => {
    const refuse = (status: number, error: string): never => {
      const err = new Error(`${status} ${error}`) as ApiError;
      err.status = status; err.body = { error };
      throw err;
    };
    if (files.length === 0) refuse(400, 'no_file');
    if (files.length > CERT_FAPIAO_MAX_FILES) refuse(400, 'too_many_files');
    if (files.some((f) => f.size > CERT_FAPIAO_MAX_BYTES)) refuse(413, 'file_too_large');
    // the dialog's 「所有文件」 still lets a .docx through; ERR_COPY's generic copy names Word/Excel, so say it here
    const exts = CERT_FAPIAO_ACCEPT.split(',');
    const bad = files.find((f) => !exts.some((x) => f.name.toLowerCase().endsWith(x)));
    if (bad) {
      const err = new Error('415 unsupported_file_type') as ApiError;
      err.status = 415;
      err.body = { error: 'unsupported_file_type', detail: `「${bad.name}」的格式不支持，请上传发票 PDF/OFD 或照片（JPG/PNG/HEIC/WEBP）。/ ${bad.name} is not an accepted type — upload the invoice as a PDF or OFD, or a photo (JPG/PNG/HEIC/WEBP).` };
      throw err;
    }
    const fd = new FormData();
    // text fields first — the route reads req.parts() in any order, but nothing is lost if one ever reads file()
    if (opts.note?.trim()) fd.append('note', opts.note.trim().slice(0, 500));
    if (opts.advance) fd.append('advance', '1');
    if (opts.replace) fd.append('replace', '1');
    for (const f of files) fd.append('file', f);
    try {
      return await apiUpload<UploadCertFapiaoResult>(`/payment_certs/${certId}/fapiao`, fd);
    } catch (e) {
      const err = e as ApiError;
      if (err?.status === 413 && !(typeof err.body?.detail === 'string' && err.body.detail.trim())) err.body = { ...(err.body ?? {}), error: 'file_too_large' };
      throw err;
    }
  },
  draftSettlement: (scId: string, claimsRmb?: number | null) => apiSubmit(`/subcontracts/${scId}/settlement/draft`, { claimsRmb: claimsRmb ?? null }),
  reconcileSettlement: (sid: string) => apiSubmit(`/settlements/${sid}/reconcile`, {}),
  // the server stamps the actor from the signed-in account (as with the cert steps above)
  auditSettlement: (sid: string) => apiSubmit(`/settlements/${sid}/audit`, {}),
  approveSettlement: (sid: string) => apiSubmit(`/settlements/${sid}/approve`, {}),
  releaseSettlement: (sid: string) => apiSubmit(`/settlements/${sid}/release`, {}),
  // PR10b (9/18) retired the legacy change_orders mutators here — 工程指令 replaced them; the server routes
  // stay for 中信's read-only history. `incorporateChangeOrder` was the one that rewrote subcontracts.value_rmb.
  initRetention: (scId: string, dlpEndsAt?: string | null) => apiSubmit(`/subcontracts/${scId}/retention`, { dlpEndsAt: dlpEndsAt ?? null }),
  retentionBdCheck: (rid: string, favorable: boolean) => apiSubmit(`/retention/${rid}/bd-check`, { favorable }),
  releaseRetention: (rid: string, gmApproved: boolean, dlpElapsed: boolean) => apiSubmit(`/retention/${rid}/release`, { gmApproved, dlpElapsed }),
};

/** Payment-cert 5-state flow (EXECUTION §14c) → label + pill class. */
export const CERT_STATE: Record<string, { label: string; pill: string }> = {
  created: { label: '已创建 / created', pill: 'pill-muted' },
  pm_signed: { label: '项目经理已签 / pm_signed', pill: 'pill-stage' },
  procurement_approved: { label: '采购已核 / approved', pill: 'pill-stage' },
  fapiao_attached: { label: '发票已附 / fapiao', pill: 'pill-warn' },
  finance_released: { label: '已付款 / released', pill: 'pill-good' },
};
/** 「项目经理已签 / pm_signed」 → 「项目经理已签」 — the Chinese half of a cert-state label, for a sentence. */
export const certStateZh = (s: string | null | undefined): string => (s == null ? '—' : (CERT_STATE[s]?.label ?? s).split(' / ')[0]);
export const CERT_KIND_LABEL: Record<string, string> = { advance: '预付款', progress: '进度款', settlement: '结算款', retention: '质保金' };
export const ALERT_KIND_LABEL: Record<string, string> = {
  payment_due: '付款到期', retention_release: '质保金释放', advance_release: '预付款释放', settlement_release: '结算款释放', sla_timer: 'SLA 计时',
};

export interface ApiError extends Error { status?: number; body?: any; }

/** Fastify's own error shell: {statusCode, error:'<HTTP name>', message:'<the real cause>'}. When the
 *  `error` field is one of these it carries NO information — the cause is in `message`. */
const HTTP_SHELL = new Set([
  'Bad Request', 'Unauthorized', 'Forbidden', 'Not Found', 'Conflict', 'Payload Too Large',
  'Unprocessable Entity', 'Internal Server Error', 'Service Unavailable', 'Gateway Timeout',
]);
/** Codes the server sends without a detail string. */
const ERR_COPY: Record<string, string> = {
  forbidden: '您的账号没有此操作权限 — 请联系总经理调整角色。/ your role cannot perform this action.',
  not_found: '未找到对应记录 — 请刷新页面后重试。/ not found — refresh and retry.',
  // 华控 9/16 · 记录已签署 requires the executed paper
  executed_scan_required: '请上传已盖章并双方签字的合同扫描件（PDF 或照片），并填写签署日期。/ attach a scan of the chopped, signed contract and the date it was signed.',
  signed_on_invalid: '签署日期无效 — 需为真实日期，不能是将来的日期，也不能早于 2020 年。/ signed_on must be a real date, not in the future and not before 2020.',
  unsupported_scan_type: '扫描件只接受 PDF 或图片（JPG/PNG/HEIC/WEBP）。/ the scan must be a PDF or an image (JPG/PNG/HEIC/WEBP).',
  contract_not_generated: '请先生成合同 Word、打印盖章并双方签字，再记录签署。/ generate the contract .docx and execute it on paper first.',
  // Eric 9/18 (compliance file): fallbacks only — the 履约 routes send a bilingual `detail` and
  // apiErrText prefers it. These carry the day when a code arrives bare (e.g. an old cached bundle).
  compliance_not_open: '履约档案尚未开启 — 需先定标并生成合同。/ the compliance file is not open yet — award and contract first.',
  item_not_uploadable: '该项当前不可上传（已确认或已免除）。如需更换，请联系预算退回后重新上传。/ this item cannot accept an upload right now — ask WTL to return it first.',
  already_confirmed: '该项已确认，文件已作为凭证留档，不能再删除。/ already confirmed — the file is evidence now and cannot be removed.',
  no_file: '请先选择文件。/ attach at least one file.',
  too_many_files: '一次最多上传 10 个文件，请分批上传。/ at most 10 files per upload — send them in batches.',
  expires_at_required: '请填写有效期（该证件需要有效期）。/ the validity date is required for this document.',
  expires_at_invalid: '有效期格式须为 YYYY-MM-DD。/ expected YYYY-MM-DD.',
  file_too_large: '文件过大（单个上限 30MB），请压缩或拆分后再上传。/ file too large (30MB per file) — compress or split it.',
  unsupported_file_type: '格式不支持，请上传 PDF、照片、Word 或 Excel。/ unsupported format — upload a PDF, a photo, Word or Excel.',
  type_not_found: '未找到该记录类型，请刷新页面后重试。/ unknown record type — refresh and retry.',
  item_not_found: '未找到该档案项，请刷新页面后重试。/ compliance item not found — refresh and retry.',
  not_event_cadence: '该项不是按事件记录，不能新增。/ this item is not a per-event record.',
  // 改改 9/20 (fb_3267bf3b) · 撤销签署 + 编辑付款批次. Both routes send a bilingual `detail` and apiErrText
  // PREFERS it — these only speak when a bare code arrives (an old cached bundle, or a 400 with no detail).
  not_signed: '该合同当前不是「已签署」状态，无需撤销。/ this subcontract is not signed — nothing to revert.',
  cert_released: '已有付款凭证放款，不能撤销签署。请改为退回该凭证后再处理。/ a payment cert has already been released — revert the cert first.',
  advance_recovery_started: '预付款已开始抵扣，不能撤销签署，也不能切换付款方式。/ advance recovery has started — the signature cannot be reverted and the payment mode cannot switch.',
  settlement_exists: '已起草结算，不能再修改。/ a settlement exists — this can no longer be changed.',
  retention_exists: '质保金流程已启动，不能撤销签署。/ the retention release has started — the signature cannot be reverted.',
  instructions_exist: '已有工程指令挂在本合同下，不能撤销签署。/ work instructions exist on this subcontract — the signature cannot be reverted.',
  acceptance_started: '验收已开始，不能撤销签署。/ acceptance has started — the signature cannot be reverted.',
  stage_advanced: '采购包已推进到后续阶段，不能撤销签署。/ the package has advanced past execution — the signature cannot be reverted.',
  reason_required: '请填写原因。/ a reason is required.',
  milestones_invalid: '付款批次填写有误 — 每期需有名称且比例在 0–100 之间。/ invalid batches — each needs a name and a 0–100 %.',
  milestones_unbalanced: '各期比例合计须为 100%。/ the batch percentages must add up to 100%.',
  // 已申请进度款的批次是冻结的，连同它在 §14·0 里分摊到的预付款抵扣额 — 改动别的批次也可能把它的抵扣额挪走。
  milestone_certified: '已申请进度款的批次及其预付款抵扣不能变动（比例已冻结，也不能删除）。/ a certified batch and its advance-recovery share are frozen — its % cannot move and it cannot be removed.',
  change_orders_exist: '本合同有旧版变更记录，签署不能撤销。/ legacy change orders exist — the signature cannot be reverted.',
  cert_in_review: '已有付款凭证进入采购审批/发票阶段，签署不能撤销。/ a payment cert has reached procurement approval or fapiao — the signature cannot be reverted.',
  // 改改 9/21 · 调整合同额 + 撤销锁定 on a signed contract. Both routes send a bilingual `detail` that
  // apiErrText prefers — these speak only for a bare code.
  value_invalid: '请填写一个大于 0 的合同额（含税或不含税二选一）。/ give exactly one amount above zero — tax-inclusive or pre-tax.',
  incl_tax_unreachable: '按本合同税率，没有不含税金额能精确得出该含税金额 — 请选用相邻的可行金额。/ no pre-tax amount yields exactly this tax-inclusive figure at this rate — pick a nearby one.',
  value_unchanged: '金额与当前合同额相同，无需调整。/ same as the current contract value — nothing to change.',
  adjustments_invalid: '调整项填写有误 — 每项需有名称和非零金额。/ invalid adjustments — each needs a label and a non-zero amount.',
  progress_certs_exist: '已有进度款凭证，合同额与付款方式均不能再修改。/ progress certs exist — the contract value and the payment mode can no longer change.',
  advance_cert_partial: '预付款凭证为部分金额，无法随合同额自动调整。/ the advance cert is for a partial amount — it cannot follow the new value automatically.',
  tax_pct_invalid: '税率无效 — 须为 0–100 之间的数字。/ invalid tax rate — a number between 0 and 100.',
  tax_pct_already_set: '本合同已有税率，无需补录。/ this contract already carries a tax rate — nothing to fill in.',
  subcontract_signed: '分包合同已签署，锁定价不能撤销 — 合同额有调整请在「执行与结算」页按签订版调整。/ the subcontract is signed — the lock cannot be reopened; amend the contract value on the Execution tab.',
  // 改改 9/21 (fb_9cf4aaea) · 按节点支付 + the 结算 stage. The routes send a bilingual `detail` (with the actual
  // sum / 付至 % for installments_exceed_settlement) — these speak only for a bare code.
  settlement_stage_required: '按节点支付须填写结算节点（名称与「支付至结算额 %」）。/ an installment plan needs its settlement stage — a label and the settle-to %.',
  settlement_stage_invalid: '结算节点填写有误 — 需有名称，「支付至结算额 %」须大于 0 且不超过 100。/ invalid settlement stage — it needs a label and a settle-to % in (0, 100].',
  installments_exceed_settlement: '首期款与各期合计已超过「支付至结算额 %」— 请调整各期比例或结算节点。/ the first payment plus the stages exceed the settle-to % — adjust the stages or the settlement stage.',
  settlement_stage_not_allowed: '预付款按比例抵扣的付款计划不设结算节点。/ a recovery plan has no settlement stage.',
  payment_mode_invalid: '付款方式无效 — 只能是「按节点支付」或「预付款按比例抵扣」。/ invalid payment mode — installments or advance recovered pro rata.',
  certs_in_flight: '仍有付款凭证在审批中，请先办结（放款或退回）再起草结算。/ payment certs are still in flight — release or revert them before drafting the settlement.',
  // 改改 9/21 (fb_9bc4f320) · 从签订版合同读取付款方式
  no_executed_scan: '尚未上传签订版合同扫描件，无法读取 — 请先在「验收标准与合同」页记录签署并上传扫描件。/ no executed scan on file — record the signature with its scan on the Standards tab first.',
  scan_too_large_for_ai: '扫描件过大，无法自动读取 — 请手动填写付款批次。/ the scan is too large to read automatically — enter the batches by hand.',
  ai_unavailable: '自动读取服务暂不可用，请稍后再试或手动填写付款批次。/ the AI reader is unavailable — try again later or enter the batches by hand.',
  // 改改 9/21 (fb_8ef675e1) · the cert's 发票. The routes send a bilingual `detail` — these speak for a bare code.
  fapiao_required: '请先上传发票，再推进到「发票已附」。/ upload the invoice before moving to 发票已附.',
  cert_not_found: '未找到该付款凭证，请刷新页面后重试。/ payment cert not found — refresh and retry.',
};

/**
 * Human-readable text for a failed apiSubmit/api call.
 *
 * 改改 8/7 saw a bare English "Internal Server Error" when the Anthropic proxy was down: the old
 * `b?.detail ?? b?.error ?? e?.message` picked Fastify's useless `error` field and never reached
 * `message`, the only place the cause appeared. Order now: server detail → known code copy → a
 * meaningful code → the shell's message → the transport error.
 */
export function apiErrText(e: unknown): string {
  const err = e as ApiError;
  const b = err?.body;
  const detail = typeof b?.detail === 'string' ? b.detail.trim() : '';
  if (detail) return detail;
  // 401 AFTER the detail check — a 401 that carries a specific reason keeps it.
  if (err?.status === 401) return '登录已过期，请重新登录后再试。/ your session expired — sign in again.';
  const code = typeof b?.error === 'string' ? b.error.trim() : '';
  if (code && ERR_COPY[code]) return ERR_COPY[code];
  if (code && !HTTP_SHELL.has(code)) return code;
  const message = typeof b?.message === 'string' ? b.message.trim() : '';
  if (message) return `服务器错误 / server error: ${message}`;
  return code || err?.message || '失败 / failed';
}

/** POST that surfaces the parsed JSON error body (for field-level 400 rendering). */
export async function apiSubmit<T = any>(path: string, body: unknown, method: 'POST' | 'PATCH' | 'PUT' | 'DELETE' = 'POST'): Promise<T> {
  // a bodyless call (e.g. DELETE) must NOT send Content-Type: application/json — Fastify 400s on
  // an empty body with a JSON content-type (found live: the 删除模板 button fired but always 400'd)
  const res = await fetch(`/api${path}`, {
    method,
    ...(body === undefined
      ? {}
      : { headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body) }),
  });
  const text = await res.text();
  let json: any;
  try { json = text ? JSON.parse(text) : undefined; } catch { json = undefined; }
  if (!res.ok) {
    const err = new Error(`${res.status} ${res.statusText}`) as ApiError;
    err.status = res.status;
    err.body = json;
    throw err;
  }
  return json as T;
}

/** Multipart POST (file upload). Surfaces the parsed JSON error body like apiSubmit. Never sets Content-Type —
 *  the browser adds the multipart boundary. */
export async function apiUpload<T = any>(path: string, form: FormData): Promise<T> {
  const res = await fetch(`/api${path}`, { method: 'POST', body: form });
  const text = await res.text();
  let json: any;
  try { json = text ? JSON.parse(text) : undefined; } catch { json = undefined; }
  if (!res.ok) {
    const err = new Error(`${res.status} ${res.statusText}`) as ApiError;
    err.status = res.status;
    err.body = json;
    throw err;
  }
  return json as T;
}

/** Map a thrown ApiError's { errors:[{work_item_id, code, message}] } body to a per-field map keyed by work_item_id. */
export function fieldErrorsFrom(err: unknown): Record<string, string> {
  const body = (err as ApiError)?.body;
  const out: Record<string, string> = {};
  if (body && Array.isArray(body.errors)) {
    for (const e of body.errors) {
      if (e && e.work_item_id) out[e.work_item_id] = e.message ?? e.code ?? 'invalid';
    }
  }
  return out;
}

export interface SubmitBidTerms {
  tax_response_md?: string;
  warranty_response_md?: string;
  payment_terms_response_md?: string;
  validity_until?: string;
}
export interface BidFormPayload {
  // F3 · a line is either a package work_item (work_item_id set) or a 自行补充项 supplement (work_item_id null + item_label).
  line_items: { work_item_id: string | null; unit_price_rmb: number; remarks_md?: string; item_label?: string; qty?: number }[];
  terms?: SubmitBidTerms;
}
export interface PublicWorkItem {
  id: string; category_top: string | null; category_sub: string | null;
  item_label: string; scope_boundary_md: string | null; qty: number | null; unit: string | null;
  changed_in_revision?: number; revision_note?: string | null; // WS2 · re-quote-this-round markers
  scope_description_md?: string | null; measurement_rule?: string | null; brand_requirement?: string | null; // 工程量清单 spec shown to bidders
}
export interface PublicBidView {
  package: { id: string; name: string; project_name: string | null };
  work_items: PublicWorkItem[];
  engagement: { id: string; labor_co_name: string | null };
  last_round: { round_number: number; line_items: { work_item_id: string; unit_price_rmb: number | null; lead_time_days: number | null; remarks_md: string | null }[]; terms: SubmitBidTerms } | null;
  next_round?: number;
  closed: boolean;
  due?: string | null;      // B7/A7 · the submission deadline (tender_due_at)
  past_due?: boolean;       // B7/A7 · true once the deadline has passed
}

// ── Phase 0 identity / auth ─────────────────────────────────────────────────
export interface Account {
  account_id: string;
  org_id: string;
  role: string;
  subject_type: 'staff' | 'labor_co';
  subject_ref: string;
  display_name: string;
  view_only?: boolean;   // staff "view as labor" — read-only portal preview session
  contact_id?: string | null;    // named-contact phone login: WHO of the labor company is signed in
  contact_name?: string | null;
}
export interface MeResponse {
  org: { id: string; name: string };
  authenticated: boolean;
  account: Account | null;
  wecom_outbound_configured: boolean;
  wecom_inbound_configured?: boolean;
  /** wtl-chat bot push (MM_NOTIFY_URL/MM_NOTIFY_SECRET) — false ⇒ every 催办/完成 ping is audit-only. */
  mm_notify_configured?: boolean;
  /** §11 workbench tasks (VOCATASK_*) — false ⇒ no task lands in anyone's 工作台. */
  task_platform_configured?: boolean;
  sso_enabled?: boolean;   // WeCom (企业微信) SSO login available — gates the login button
  passcode_login_enabled?: boolean;   // false = WeCom-only cutover: hide the password form
  phone_sso_enabled?: boolean;   // partner phone+SMS login available — gates the portal login button
  // Org settings. Only the bits the client actually reads are typed — 供应商名录's pickable lists
  // (二级类型 / 城市) ride along here rather than on a second endpoint (改改 fb_c09bf7fd/fb_73269add).
  settings?: {
    registry?: {
      supplier_types?: { category: string; sub_types: string[] };
      cities?: string[];
    };
  };
}
export function getMe() { return api<MeResponse>('/me'); }
// Staff "view as labor" — read-only portal preview (writes are server-refused while previewing).
export function startPortalPreview(laborCoId: string) { return apiSubmit<{ ok: boolean; viewing: string; redirect: string }>(`/auth/view-as-labor`, { labor_co_id: laborCoId }); }
export function exitPortalPreview() { return apiSubmit<{ ok: boolean; redirect: string }>(`/auth/view-as-exit`, {}); }
export function devLogin(staffId: string) {
  return apiSubmit<{ account: Account; authenticated: boolean }>('/auth/dev-login', { staff_id: staffId });
}
export function logout() { return apiSubmit('/auth/logout', {}); }
// DR-A · simple passcode login (staff + labor) for the intranet dry run.
export function passcodeLogin(loginId: string, passcode: string) {
  return apiSubmit<{ account: Account; authenticated: boolean }>('/auth/login', { login_id: loginId, passcode });
}
export function claimLaborCo(token: string) {
  return apiSubmit<{ account: Account; authenticated: boolean }>('/auth/labor-co/claim', { token });
}
export function laborCoLogin(unifiedCreditCode: string) {
  return apiSubmit<{ account: Account; authenticated: boolean }>('/auth/labor-co/login', { unified_credit_code: unifiedCreditCode });
}
/** Admin (gm): mint a single-use claim link for a labor company. */
export function mintLaborClaimToken(laborCoId: string) {
  return apiSubmit<{ token: string; claim_url: string }>(`/registry/labor-cos/${laborCoId}/claim-token`, {});
}
/** Admin (gm): reissue a labor company's passcode. Eric 9/18 (reissue passcode): the passcode comes back
 *  ONCE — it is never stored client-side, never re-fetchable, and gone on the next render. */
export function reissueLaborPasscode(laborCoId: string) {
  return apiSubmit<{ ok: boolean; login_id: string; passcode: string; labor_co_id: string; display_name: string }>(
    `/registry/labor-cos/${laborCoId}/passcode`, {},
  );
}

// ── Phase 3 · engagement messaging + labor portal ───────────────────────────
export interface EngagementMessage {
  id: string; engagement_id: string; direction: 'outbound' | 'inbound'; channel: string;
  author_type: string; author_name: string | null; body_md: string;
  delivery_status: 'pending' | 'success' | 'dev_mode' | 'failure'; delivery_error: string | null;
  related_bid_feedback_id: string | null; related_qa_entry_id: string | null; created_at: string;
}
/** Staff-side read of an engagement's message thread (for NegotiationPanel). */
export function engagementMessages(pid: string, eid: string) {
  return api<EngagementMessage[]>(`/packages/${pid}/engagements/${eid}/messages`);
}
/** WS5a · staff composer — send a free-text message to the engagement's labor co (logged + WeCom push). */
export function staffSendMessage(pid: string, eid: string, bodyMd: string, authorName?: string) {
  return apiSubmit<{ ok: boolean; message_id: string; delivery_status: string; dev_mode: boolean }>(
    `/packages/${pid}/engagements/${eid}/messages`, { body_md: bodyMd, author_name: authorName ?? null },
  );
}

export interface PortalEngagementRow {
  id: string; package_id: string; package_name: string; current_stage: number;
  award_status: string | null; awarded_engagement_id: string | null; bid_rounds: number; last_round: number | null;
}
export interface PortalEngagementDetail {
  engagement: Record<string, any> & { id: string; package_id: string; package_name: string };
  work_items: PublicWorkItem[];
  last_bid: (Bid & { line_items: BidLineItem[] }) | null;
  // F2 · round gating: locked after submit, unlocked when staff send the round's feedback.
  can_bid?: boolean;
  bid_round?: number;
}
export interface PortalQaEntry { id: string; package_id: string; q_md: string; a_md: string | null; status: string; visibility: string; created_at: string; }

/** fb_07c3fe4a · set / change / clear the 投标截止时间 after creation (null clears). */
export function setTenderDue(pid: string, tenderDueAt: string | null) {
  return apiSubmit<{ ok: boolean; tender_due_at: string | null }>(`/packages/${pid}/tender-due`, { tender_due_at: tenderDueAt });
}

export function portalDashboard() { return api<{ engagements: PortalEngagementRow[] }>(`/portal/dashboard`); }
export function portalEngagement(eid: string) { return api<PortalEngagementDetail>(`/portal/engagements/${eid}`); }
export function portalSubmitBid(eid: string, payload: BidFormPayload) { return apiSubmit(`/portal/engagements/${eid}/bid`, payload); }
export function portalMessages(eid: string) { return api<EngagementMessage[]>(`/portal/engagements/${eid}/messages`); }
export function portalPostMessage(eid: string, bodyMd: string) { return apiSubmit(`/portal/engagements/${eid}/messages`, { body_md: bodyMd }); }
// 议价最终价确认 · the labor party confirms the negotiated final total the staff requested. Passing the total
// the page rendered binds the consent — a stale view 409s server-side instead of locking silently.
export function portalConfirmFinal(eid: string, expectedTotalRmb?: number) {
  return apiSubmit<{ ok: boolean; final_confirmed_at: string; final_total_rmb: number }>(`/portal/engagements/${eid}/confirm-final`, expectedTotalRmb != null ? { expected_total_rmb: expectedTotalRmb } : {});
}
export function portalQaList() { return api<PortalQaEntry[]>(`/portal/qa`); }
export function portalAskQa(question: string) { return apiSubmit<PortalQaEntry>(`/portal/qa`, { question }); }

// WS4 · per-trade 验收标准 the labor party confirms before signing (st.13d gate).
export interface AcceptanceCriterion { id: string; trade: string | null; label_zh: string | null; label_en: string | null; pass_rule_md: string | null; is_critical: boolean; sort_order: number; }
export interface PortalStandards { subcontract_id: string | null; status: string | null; confirmed_at: string | null; criteria: AcceptanceCriterion[]; }
export function portalStandards(eid: string) { return api<PortalStandards>(`/portal/engagements/${eid}/standards`); }
export function portalConfirmStandards(eid: string) { return apiSubmit<{ ok: boolean; confirmed_at: string }>(`/portal/engagements/${eid}/standards/confirm`, {}); }

// st.15 · acceptance findings on THIS company's subcontract + self-serve remediation (7/2 MED-5).
export interface PortalAcceptanceFinding { id: string; criterion_id: string; iteration: number; notes_md: string | null; status: string; resolution_notes_md: string | null; found_at: string; resolution_at: string | null; }
export interface PortalAcceptance { subcontract_id: string | null; criteria: AcceptanceCriterion[]; scorecard: { acceptance_round: number; overall_status: string } | null; findings: PortalAcceptanceFinding[]; }
export function portalAcceptance(eid: string) { return api<PortalAcceptance>(`/portal/engagements/${eid}/acceptance`); }
export function portalSubmitRemediation(eid: string, fid: string, notesMd: string) { return apiSubmit(`/portal/engagements/${eid}/acceptance/findings/${fid}/remediation`, { notes_md: notesMd }); }

// DR-B/DR-C · package files (tender requirements + labor documents).
export interface PackageFileMeta {
  id: string; package_id: string; engagement_id: string | null; labor_co_id: string | null;
  kind: 'requirement' | 'labor_doc' | 'standards_doc'; category: string | null; file_name: string;
  size_bytes: number | null; mime_type: string | null; uploaded_by: string | null; created_at: string;
  source?: string | null;   // 'wtl_pull' = synced from 采购大脑 (staff-only); null = manual upload
}
function fileForm(file: File, category?: string): FormData {
  const fd = new FormData(); if (category) fd.append('category', category); fd.append('file', file); return fd;
}
// staff (GM/estimate)
export function uploadPackageFile(pid: string, file: File, category?: string) { return apiUpload<{ ok: boolean; file: PackageFileMeta }>(`/packages/${pid}/files`, fileForm(file, category)); }
export function listPackageFiles(pid: string) { return api<PackageFileMeta[]>(`/packages/${pid}/files`); }
export function packageFileDownloadUrl(pid: string, fileId: string) { return `/api/packages/${pid}/files/${fileId}/download`; }
export function deletePackageFile(pid: string, fileId: string) { return api<{ ok: boolean; deleted: string }>(`/packages/${pid}/files/${fileId}`, { method: 'DELETE' }); }
export function bulkDeletePackageFiles(pid: string, body: { ids?: string[]; all?: boolean }) { return apiSubmit<{ ok: boolean; deleted: number }>(`/packages/${pid}/files/bulk-delete`, body); }
// 标准文件 · standards-workflow attachments (过程/竣工验收标准)
export function pmWorkflowDocs(wid: string) { return api<{ docs: PackageFileMeta[] }>(`/me/standards-workflows/${wid}/docs`); }
export function pmUploadWorkflowDoc(wid: string, file: File, category?: string) { return apiUpload<{ ok: boolean; file: PackageFileMeta }>(`/me/standards-workflows/${wid}/docs`, fileForm(file, category)); }
export function pmDeleteWorkflowDoc(wid: string, fid: string) { return api<{ ok: boolean; deleted: string }>(`/me/standards-workflows/${wid}/docs/${fid}`, { method: 'DELETE' }); }
export function pmWorkflowDocUrl(wid: string, fid: string) { return `/api/me/standards-workflows/${wid}/docs/${fid}/download`; }
export function staffUploadWorkflowDoc(wid: string, file: File, category?: string) { return apiUpload<{ ok: boolean; file: PackageFileMeta }>(`/standards-workflows/${wid}/docs`, fileForm(file, category)); }
export function staffDeleteWorkflowDoc(wid: string, fid: string) { return api<{ ok: boolean; deleted: string }>(`/standards-workflows/${wid}/docs/${fid}`, { method: 'DELETE' }); }
export function staffWorkflowDocUrl(wid: string, fid: string) { return `/api/standards-workflows/${wid}/docs/${fid}/download`; }
export function portalWorkflowDocUrl(eid: string, fid: string) { return `/api/portal/engagements/${eid}/standards-workflow/docs/${fid}/download`; }

// labor (portal)
export function portalUploadFile(eid: string, file: File, category?: string) { return apiUpload<{ ok: boolean; file: PackageFileMeta }>(`/portal/engagements/${eid}/files`, fileForm(file, category)); }
export function portalListFiles(eid: string) { return api<{ requirements: PackageFileMeta[]; mine: PackageFileMeta[] }>(`/portal/engagements/${eid}/files`); }
export function portalFileDownloadUrl(eid: string, fileId: string) { return `/api/portal/engagements/${eid}/files/${fileId}/download`; }
export function portalDeleteFile(eid: string, fileId: string) { return api<{ ok: boolean; deleted: string }>(`/portal/engagements/${eid}/files/${fileId}`, { method: 'DELETE' }); }
// offline pricing round-trip (改改 6/26): download the BOQ, fill prices, import to populate the bid form
export function bidTemplateUrl(eid: string) { return `/api/portal/engagements/${eid}/bid-template`; }
// 改改 6/26 (issue fb_27131366) · staff bid-comparison export (.xlsx).
export function comparisonExportUrl(pid: string) { return `/api/packages/${pid}/comparison-export`; }
export interface ImportedBidSheet { ok: boolean; line_items: { work_item_id: string | null; unit_price_rmb: number; remarks_md?: string; item_label?: string; qty?: number; unit?: string }[]; matched: number; unmatched: number; supplements?: number; total_work_items: number; tax_pct?: number | null; }
export function importBidSheet(eid: string, file: File) { const fd = new FormData(); fd.append('file', file); return apiUpload<ImportedBidSheet>(`/portal/engagements/${eid}/bid-import`, fd); }
// staff 代录 batch import (fb_5fcc8949): same export→fill→import round-trip, on the internal (staff-auth) routes.
export function staffBidTemplateUrl(pid: string, eid: string) { return `/api/packages/${pid}/engagements/${eid}/bid-template`; }
export function importStaffBidSheet(pid: string, eid: string, file: File) { const fd = new FormData(); fd.append('file', file); return apiUpload<ImportedBidSheet>(`/packages/${pid}/engagements/${eid}/bid-import`, fd); }
// delete a package (改改 6/29: remove an accidental duplicate). force=true also clears a package that has bids.
export async function deletePackage(pid: string, force = false): Promise<{ ok: boolean; deleted: string; work_items: number; engagements: number; bids: number }> {
  const res = await fetch(`/api/packages/${pid}`, { method: 'DELETE', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(force ? { force: true } : {}) });
  const text = await res.text(); let json: any; try { json = text ? JSON.parse(text) : undefined; } catch { json = undefined; }
  if (!res.ok) { const err = new Error(`${res.status} ${res.statusText}`) as ApiError; err.status = res.status; err.body = json; throw err; }
  return json;
}
// 参考价 batch round-trip (fb_929ef6ef): export the BOQ with a fillable 参考价 column, fill, re-import to set ref prices in bulk.
export function boqRefTemplateUrl(pid: string) { return `/api/packages/${pid}/boq-ref-template`; }
export function importBoqRefSheet(pid: string, file: File) { const fd = new FormData(); fd.append('file', file); return apiUpload<{ ok: boolean; updated: number; unmatched: number; total: number }>(`/packages/${pid}/boq-ref-import`, fd); }

// Supplier registry list (bid-entry picker + future registry UI).
export interface LaborCoContact {
  id: string; labor_co_id: string; name: string; phone: string; role_note: string | null; is_primary: number;
}
export interface RegistryLaborCo {
  id: string; name: string; internal_code: string | null; region: string | null;
  status: string; trades_json: string | null; primary_contact: string | null; phone: string | null;
  contacts: LaborCoContact[];
  category: string;          // 一级分类 — '劳务' for now
  sub_types: string[];       // 二级类型, 多选 (装修/拆除/…)
  city: string | null;       // 城市 (region stays as the legacy 地区 hint)
  // 新/老供应商 — counts only, no prices (this payload is on the site_pm allowlist).
  history: { past_engagements: number; awarded: number };
}
export function listRegistryLaborCos() { return api<RegistryLaborCo[]>('/registry/labor-cos'); }
// Named-contact roster (spec 2026-07-07): up to 5 phone-login people per supplier, staff-managed.
// Content-Type only when a body is actually sent — Fastify 400s an EMPTY body that carries
// `Content-Type: application/json` (the DELETE-contact "can't delete" bug, 改改 7/7).
async function contactCall<T>(url: string, method: string, body?: unknown): Promise<T> {
  const res = await fetch(url, {
    method,
    ...(body === undefined ? {} : { headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body) }),
  });
  const text = await res.text(); let json: any; try { json = text ? JSON.parse(text) : undefined; } catch { json = undefined; }
  if (!res.ok) { const e = new Error(`${res.status}`) as ApiError; e.status = res.status; e.body = json; throw e; }
  return json as T;
}
export function addLaborCoContact(lcId: string, body: { name: string; phone: string; role_note?: string | null; is_primary?: boolean }) {
  return contactCall<{ ok: boolean; contact: LaborCoContact }>(`/api/registry/labor-cos/${lcId}/contacts`, 'POST', body);
}
export function updateLaborCoContact(cid: string, body: { name?: string; phone?: string; role_note?: string | null; is_primary?: boolean }) {
  return contactCall<{ ok: boolean; contact: LaborCoContact }>(`/api/registry/contacts/${cid}`, 'PATCH', body);
}
export function deleteLaborCoContact(cid: string) {
  return contactCall<{ ok: boolean }>(`/api/registry/contacts/${cid}`, 'DELETE');
}
/** Go-live prep · fix a supplier's phone / contact from the roster page (server validates + dedups),
 *  plus its 类型/城市 (an unlisted city 400s unless the caller is 采购/估价/GM, who extend the list). */
export async function updateLaborCo(
  lcId: string,
  body: { phone?: string | null; primary_contact?: string | null; sub_types?: string[]; city?: string | null },
) {
  const res = await fetch(`/api/registry/labor-cos/${lcId}`, { method: 'PATCH', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body) });
  const text = await res.text(); let json: any; try { json = text ? JSON.parse(text) : undefined; } catch { json = undefined; }
  if (!res.ok) { const e = new Error(`${res.status}`) as ApiError; e.status = res.status; e.body = json; throw e; }
  return json as {
    ok: boolean; id: string; name: string; phone: string | null; primary_contact: string | null;
    category: string; sub_types: string[]; city: string | null;
  };
}
/** Client mirror of normalizeCnPhone — classify a roster phone for the readiness view (no network). */
export function phoneHealth(phone: string | null | undefined): 'ok' | 'missing' | 'invalid' {
  const raw = (phone ?? '').trim();
  if (!raw) return 'missing';
  const digits = raw.replace(/\D/g, '');
  const p = digits.length === 13 && digits.startsWith('86') ? digits.slice(2) : digits;
  return /^1\d{10}$/.test(p) ? 'ok' : 'invalid';
}

export const DELIVERY_STATUS: Record<string, { label: string; pill: string }> = {
  pending: { label: '发送中 / sending', pill: 'pill-muted' },
  success: { label: '已发送 / sent', pill: 'pill-good' },
  dev_mode: { label: '开发态（未实际发送）/ dev-mode', pill: 'pill-warn' },
  failure: { label: '发送失败 / failed', pill: 'pill-bad' },
};

export interface StaffRow { id: string; name: string; role: string; dept: string | null; wecom_userid: string | null; last_login_at: string | null; active: number; is_placeholder: number; }
/** Can this person actually be reached? Unbound AND never-signed-in ⇒ a vote assigned to them may hang. */
export function staffReachable(s: StaffRow): boolean { return !!s.wecom_userid || !!s.last_login_at; }
export function listStaff(role?: string, opts?: { excludePlaceholders?: boolean }) {
  const q = new URLSearchParams();
  if (role) q.set('role', role);
  if (opts?.excludePlaceholders) q.set('exclude_placeholders', '1');
  const qs = q.toString();
  return api<StaffRow[]>(`/staff${qs ? `?${qs}` : ''}`);
}

// ── §11 Task delegation (My Tasks inbox/outbox) ─────────────────────────────
export interface TaskRow {
  id: string;
  vocatask_uuid: string;
  title: string;
  description: string | null;
  type: string;
  priority: string;
  delegator_account_id: string | null;
  delegator_name: string | null;
  assignee_staff_id: string | null;
  assignee_name: string | null;
  status: string | null;
  display_status: string | null;
  package_id: string | null;
  due_date: string | null;
  // Screen-context for deep-link-back ("open the screen this came from"). page_url is a same-origin
  // relative path (pathname + ?tab=…); list views return has_screenshot (1/0) instead of the blob —
  // the full screenshot loads lazily via getTask(id).
  page_url: string | null;
  view_tab: string | null;
  context_json: string | null;
  has_screenshot?: number;
  screenshot?: string | null; // only present from getTask(id) detail
  created_at: string;
  updated_at: string;
}
/** Structured task context the floating button, contextual buttons, and the agent all build. */
export type TaskContextKind = 'package' | 'finding' | 'bid_row' | 'screen';
export interface TaskContext {
  kind: TaskContextKind;
  label: string;
  package_id?: string;
  engagement_id?: string;
  finding_id?: string;
  view_tab?: string;
}
export interface TaskAssignee { id: string; name: string; role: string; dept: string | null; assignable: boolean; }
export interface TaskPlatformStatus { configured: boolean; base_url: string | null; }
/** A §11 platform-directory user — the primary delegate target (delegate to anyone, by uuid). */
export interface DirectoryUser { uuid: string; username: string; employee_id: string | null; }

export function getTaskPlatformStatus() { return api<TaskPlatformStatus>('/tasks/status'); }
export function getTaskAssignees() { return api<TaskAssignee[]>('/tasks/assignees'); }
/** Full company directory (~160) for the searchable delegate picker. */
export function getTaskDirectory() { return api<DirectoryUser[]>('/tasks/directory'); }
export function getTaskInbox() { return api<TaskRow[]>('/tasks/inbox'); }
export function getTaskOutbox() { return api<TaskRow[]>('/tasks/outbox'); }
/** Full org board (GM / agent only) — backs the in-app task panel's "all" view. */
export function getAllTasks() { return api<TaskRow[]>('/tasks/all'); }
/** One task incl. the full screenshot data: URL (lazy thumbnail load). */
export function getTask(taskId: string) { return api<TaskRow>(`/tasks/${taskId}`); }
export function createTask(body: {
  title: string;
  assignee_user_id?: string; assignee_name?: string;  // PRIMARY: delegate to any §11 directory user
  assignee_staff_id?: string;                          // legacy: a mapped local staff
  description?: string; priority?: string; due_date?: string; type?: string;
  package_id?: string; page_url?: string; view_tab?: string; screenshot?: string; context_json?: string; on_behalf_of?: string;
}) {
  return apiSubmit<TaskRow>('/tasks', body);
}
export function setTaskStatus(taskId: string, status: string) {
  return api<TaskRow>(`/tasks/${taskId}/status`, { method: 'PUT', body: JSON.stringify({ status }) });
}
/** Delete a task (delegator/assignee/agent). Local soft-delete always succeeds; platform delete is best-effort. */
export function deleteTask(taskId: string) {
  return api<{ ok: boolean; deleted: boolean; platform?: string; already?: boolean }>(`/tasks/${taskId}`, { method: 'DELETE' });
}
export interface SyncAssigneesResult {
  ok: boolean; total_users: number; matched: number; unmatched: number;
  unmatched_list: { id: string; name: string }[];
}
export function syncTaskAssignees() { return apiSubmit<SyncAssigneesResult>('/tasks/sync-assignees', {}); }
export const TASK_STATUS_LABEL: Record<string, string> = {
  backlog: '待规划 / Backlog', todo: '待办 / To-do', in_progress: '进行中 / In progress',
  in_review: '待复核 / In review', done: '已完成 / Done', cancelled: '已取消 / Cancelled',
};
export const TASK_PRIORITY_LABEL: Record<string, string> = {
  low: '低 / Low', normal: '普通 / Normal', medium: '中 / Medium', high: '高 / High', critical: '紧急 / Critical',
};

export const ROLE_LABEL: Record<string, string> = {
  gm: '总经理 / GM', procurement: '采购 / Procurement', budget: '预算 / Budget',
  finance: '财务 / Finance', site_pm: '项目经理 / Site PM',
  audit: '内审 / Audit', commercial: '商务 / Commercial', design: '设计 / Design',
  exec: '终审 / Exec (Lilian)', labor_co: '劳务公司 / Labor co',
};

// ── GM access dashboard (spec 2026-07-08): members / pending queue / WeCom department rules ──
export interface AccessStaff {
  id: string; name: string; dept: string | null; position: string | null; role: string;
  active: number; wecom_userid: string | null; last_login_at: string | null; login_id: string | null;
}
export interface RoleRule { keywords: string[]; not_keywords?: string[]; role: string }
export function adminAccess() {
  return api<{ members: AccessStaff[]; pending: AccessStaff[]; assignable_roles: string[] }>('/admin/access');
}
export function adminAddStaff(body: { name: string; dept?: string; role: string }) {
  return apiSubmit<{ ok: boolean; staff: AccessStaff }>('/admin/access/staff', body);
}
export function adminPatchStaff(sid: string, body: { role?: string; active?: boolean }) {
  return apiSubmit<{ ok: boolean; staff: AccessStaff }>(`/admin/access/staff/${sid}`, body, 'PATCH');
}
export function adminSsoRules() {
  return api<{ rules: RoleRule[]; defaults: RoleRule[]; assignable_roles: string[] }>('/admin/sso-rules');
}
export function adminPutSsoRules(rules: RoleRule[]) {
  return apiSubmit<{ ok: boolean; rules: RoleRule[] }>('/admin/sso-rules', { rules }, 'PUT');
}
/** 供应商分类 & 城市 — the pickable lists behind 供应商名录's 类型/城市 columns (gm-only). */
export function adminRegistryLists() {
  return api<{ category: string; sub_types: string[]; cities: string[]; defaults: { sub_types: string[]; cities: string[] } }>('/admin/registry-lists');
}
export function adminPutRegistryLists(body: { sub_types: string[]; cities: string[] }) {
  return apiSubmit<{ ok: boolean; sub_types: string[]; cities: string[] }>('/admin/registry-lists', body, 'PUT');
}

// ── st.16 GM→PM remediation loop (Feature A) ────────────────────────────────
export type FindingLoopStatus =
  | 'open' | 'assigned' | 'pm_acknowledged' | 'in_remediation' | 'pm_resolved' | 'gm_verified' | 'closed' | 'waived';
export interface LoopFinding {
  id: string;
  final_inspection_id: string;
  subcontract_id: string;
  label_md: string;
  severity: FindingSeverity;
  related_criterion_id: string | null;
  gm_missed_by_pm: number;
  status: FindingLoopStatus;
  assigned_pm_staff_id: string | null;
  assigned_at: string | null;
  promised_remediation_date: string | null;
  found_at: string;
  finding_photo_path: string;
  resolution_evidence_path: string | null;
  resolution_notes_md: string | null;
  resolved_at: string | null;
  gm_proof_path: string | null;
  gm_verified_at: string | null;
  gm_satisfaction: 'satisfactory' | 'unsatisfactory' | null;
  recorded_to_labor_record: number;
  waived_note: string | null;
}
export interface FinalInspectionLoop { final_inspection_id: string | null; status: string | null; findings: LoopFinding[]; }
export function getFinalInspectionLoop(scId: string) { return api<FinalInspectionLoop>(`/subcontracts/${scId}/final-inspection/loop`); }
export function getAssignedFindings() { return api<LoopFinding[]>('/me/assigned-findings'); }
/** Authed photo URL (the route streams it; not a static mount). */
export function loopPhotoUrl(fid: string, which: 'finding' | 'pm' | 'gm') { return `/api/files/final-inspection/${fid}/${which}`; }

export const finalInspectionLoopActions = {
  logFinding: (scId: string, form: FormData) => apiUpload<{ findingId: string; finalInspectionId: string }>(`/subcontracts/${scId}/final-inspection/findings`, form),
  assign: (fid: string, pmStaffId: string) => apiSubmit(`/final-inspection/findings/${fid}/assign`, { pm_staff_id: pmStaffId }),
  acknowledge: (fid: string, date: string) => apiSubmit(`/final-inspection/findings/${fid}/acknowledge`, { promised_remediation_date: date }),
  resolve: (fid: string, form: FormData) => apiUpload(`/final-inspection/findings/${fid}/resolution`, form),
  verify: (fid: string, form: FormData) => apiUpload<{ ok: boolean; inspection_closed: boolean }>(`/final-inspection/findings/${fid}/verify`, form),
  waive: (fid: string, note?: string) => apiSubmit(`/final-inspection/findings/${fid}/waive`, { note }),
};

export const LOOP_STATUS: Record<FindingLoopStatus, { label: string; pill: string }> = {
  open: { label: '待指派 / open', pill: 'pill-muted' },
  assigned: { label: '已指派 / assigned', pill: 'pill-stage' },
  pm_acknowledged: { label: 'PM 已确认 / acknowledged', pill: 'pill-stage' },
  in_remediation: { label: '整改中 / in remediation', pill: 'pill-warn' },
  pm_resolved: { label: '待复核 / pm resolved', pill: 'pill-warn' },
  gm_verified: { label: 'GM 已复核 / verified', pill: 'pill-good' },
  closed: { label: '已闭环 / closed', pill: 'pill-good' },
  waived: { label: '已豁免 / waived', pill: 'pill-stage' },
};

// ── Phase 2 · labor master DB / quality context ─────────────────────────────
export interface QualityEligibility {
  eligible: boolean;
  triggered_rules: string[];
  mode: 'advisory' | 'gate';
  reason_zh: string;
  reason_en: string;
}
export interface QualityContextCompany {
  labor_co_id: string;
  name: string | null;
  status: string | null;
  quality_score: number | null;
  metrics: Record<string, number | boolean> | null;
  narrative_flags: string[];
  eligibility: QualityEligibility;
}
export interface QualityContext { package_id: string; companies: QualityContextCompany[]; }
export function getQualityContext(pid: string) { return api<QualityContext>(`/packages/${pid}/quality-context`); }

// ── 🐞 In-app tool feedback (Issues queue) ────────────────────────────────────
export interface ToolFeedback {
  id: string;
  kind: 'bug' | 'feature';
  body: string;
  page_url: string | null;
  app_version: string | null;
  screenshot: string | null;          // data: URL or null
  status: 'open' | 'resolved' | 'declined';
  created_by: string | null;
  created_by_name: string | null;
  resolved_by: string | null;
  resolved_by_name: string | null;
  resolved_at: string | null;
  disposition_note: string | null;
  created_at: string;
}
export function listToolFeedback(status: 'open' | 'resolved' | 'declined' = 'open') {
  return api<ToolFeedback[]>(`/tool-feedback?status=${status}`);
}
export function toolFeedbackOpenCount() {
  return api<{ count: number }>('/tool-feedback/open-count');
}
export function resolveToolFeedback(id: string, disposition: 'resolved' | 'declined', note?: string) {
  return api<{ ok: boolean; status: string }>(`/tool-feedback/${id}/resolve`, {
    method: 'POST',
    body: JSON.stringify({ disposition, note }),
  });
}

// ── 验收标准确认书 · pre-contract standards workflow (验收标准确认 → 验收标准确认书 → 合同生成) ──
export type WorkflowStatus =
  | 'pm_drafting' | 'submitted' | 'returned' | 'released' | 'labor_clear'
  | 'annexure_review' | 'revision_requested' | 'finalized';

export const WF_STATUS: Record<WorkflowStatus, { label: string; pill: string }> = {
  pm_drafting: { label: 'PM 拟定中 / drafting', pill: 'pill-stage' },
  submitted: { label: '待估价审核 / submitted', pill: 'pill-warn' },
  returned: { label: '已退回 / returned', pill: 'pill-bad' },
  released: { label: '劳务答疑中 / released', pill: 'pill-stage' },
  labor_clear: { label: '劳务已确认清楚 / clear', pill: 'pill-good' },
  annexure_review: { label: '验收标准确认书待确认 / review', pill: 'pill-warn' },
  revision_requested: { label: '要求修改 / revision', pill: 'pill-bad' },
  finalized: { label: '已定稿 ✓ / finalized', pill: 'pill-good' },
};
/** The stepper's main path (return/revision loops render as badges, not steps). */
export const WF_STEPS: WorkflowStatus[] = ['pm_drafting', 'submitted', 'released', 'labor_clear', 'annexure_review', 'finalized'];

export interface WorkflowEvent { from_status: string | null; to_status: string; actor_type: string; actor_role: string | null; actor_name: string | null; comment_md: string | null; created_at: string; }
export interface WorkflowRow {
  id: string; status: WorkflowStatus; package_id: string; engagement_id: string;
  assigned_pm_staff_id: string; assigned_by_name: string | null;
  submitted_at: string | null; released_at: string | null; labor_clear_at: string | null;
  meeting_date: string | null; meeting_location: string | null; meeting_attendees_md: string | null; meeting_notes_md: string | null;
  final_version: number | null; finalized_at: string | null; created_at: string; updated_at: string;
}
export interface AnnexureView {
  id: string; version: number; status: 'draft' | 'confirmed' | 'superseded'; content_md: string; generated_by: string | null;
  pm_confirmed_at: string | null; pm_confirmed_by: string | null;
  labor_confirmed_at: string | null; labor_confirmed_by: string | null; created_at: string;
}
export interface WorkflowMessage { id: string; direction: 'inbound' | 'outbound'; author_type: string; author_name: string | null; body_md: string; created_at: string; }

// staff surface
export interface StaffWorkflowView { workflow: WorkflowRow | null; events?: WorkflowEvent[]; criteria?: AcceptanceCriterion[]; pm?: { id: string; name: string } | null; labor_co?: { id: string; name: string } | null; docs?: PackageFileMeta[]; }
export function getStandardsWorkflow(pid: string) { return api<StaffWorkflowView>(`/packages/${pid}/standards-workflow`); }
export function getWorkflowAnnexure(wid: string) { return api<{ current: AnnexureView | null; history: AnnexureView[] }>(`/standards-workflows/${wid}/annexure`); }
export const standardsWorkflowActions = {
  assign: (pid: string, pmStaffId: string) => apiSubmit<{ workflow: WorkflowRow }>(`/packages/${pid}/standards-workflow`, { pm_staff_id: pmStaffId }),
  reassign: (wid: string, pmStaffId: string) => apiSubmit<{ workflow: WorkflowRow }>(`/standards-workflows/${wid}/reassign-pm`, { pm_staff_id: pmStaffId }),
  returnDraft: (wid: string, commentMd: string) => apiSubmit<{ workflow: WorkflowRow }>(`/standards-workflows/${wid}/return`, { comment_md: commentMd }),
  release: (wid: string) => apiSubmit<{ workflow: WorkflowRow }>(`/standards-workflows/${wid}/release`, {}),
  /** 催办 — re-send the 确认清楚 reminder while the workflow sits in `released`. */
  nudgeLabor: (wid: string) => apiSubmit<{ ok: boolean; status: WorkflowStatus }>(`/standards-workflows/${wid}/nudge-labor`, {}),
  /** 代确认清楚 — record an OFFLINE confirmation (phone/WeChat) so the meeting can be entered. */
  laborClearOnBehalf: (wid: string, commentMd: string) =>
    apiSubmit<{ workflow: WorkflowRow }>(`/standards-workflows/${wid}/labor-clear-on-behalf`, { comment_md: commentMd }),
  meeting: (wid: string, body: { meeting_date: string; location?: string; attendees_md?: string; notes_md: string }) =>
    apiSubmit<{ workflow: WorkflowRow; annexure: AnnexureView }>(`/standards-workflows/${wid}/meeting`, body),
  regenerate: (wid: string, notesMd?: string) => apiSubmit<{ workflow: WorkflowRow; annexure: AnnexureView }>(`/standards-workflows/${wid}/annexure/regenerate`, { notes_md: notesMd }),
};

// PM surface (/me)
export interface PmWorkflowListItem { id: string; status: WorkflowStatus; package_id: string; package_name: string; project_name: string | null; created_at: string; updated_at: string; }
export interface PmWorkflowDetail {
  workflow: {
    id: string; status: WorkflowStatus; submitted_at: string | null; released_at: string | null; labor_clear_at: string | null;
    meeting_date: string | null; meeting_location: string | null; meeting_attendees_md: string | null; meeting_notes_md: string | null;
    final_version: number | null; finalized_at: string | null; created_at: string; updated_at: string;
    latest_return_comment: string | null; criteria_editable: boolean;
  };
  package: { id: string; name: string; project_name: string | null; scope_summary_md: string | null; brief_md: string | null; trades: string[] };
  criteria: AcceptanceCriterion[];
  labor_co: { id: string; name: string } | null;
  annexure: AnnexureView | null;
  events: WorkflowEvent[];
}
export interface LibraryTradeView { trade_code: string; trade_zh: string; trade_en: string; aliases_zh: string[]; standards: { label_zh: string; label_en?: string; pass_rule_md: string; is_critical: boolean }[]; tolerances: { check_item_zh: string; op: string; tolerance: number; unit?: string }[]; process_md: string | null; image_count: number; }
export function pmListWorkflows() { return api<{ workflows: PmWorkflowListItem[] }>('/me/standards-workflows'); }
export function pmWorkflowDetail(wid: string) { return api<PmWorkflowDetail>(`/me/standards-workflows/${wid}`); }
export function pmStandardsLibrary() { return api<{ trades: LibraryTradeView[] }>('/me/standards-library'); }
export function pmWorkflowMessages(wid: string) { return api<{ messages: WorkflowMessage[] }>(`/me/standards-workflows/${wid}/messages`); }
export const pmWorkflowActions = {
  addCriterion: (wid: string, body: { trade?: string; label_zh?: string; label_en?: string; pass_rule_md?: string; is_critical?: boolean; sort_order?: number }) =>
    apiSubmit<{ ok: boolean; criterion_id: string }>(`/me/standards-workflows/${wid}/criteria`, body),
  patchCriterion: (cid: string, patch: { label_zh?: string; label_en?: string; pass_rule_md?: string; is_critical?: boolean; sort_order?: number }) =>
    apiSubmit(`/me/standards-criteria/${cid}`, patch, 'PATCH'),
  deleteCriterion: (cid: string) => api(`/me/standards-criteria/${cid}`, { method: 'DELETE' }),
  seedFromLibrary: (wid: string) => apiSubmit<{ ok: boolean; seeded: number }>(`/me/standards-workflows/${wid}/seed-from-library`, {}),
  submit: (wid: string) => apiSubmit<{ workflow: WorkflowRow }>(`/me/standards-workflows/${wid}/submit`, {}),
  sendMessage: (wid: string, bodyMd: string) => apiSubmit(`/me/standards-workflows/${wid}/messages`, { body_md: bodyMd }),
  meeting: (wid: string, body: { meeting_date: string; location?: string; attendees_md?: string; notes_md: string }) =>
    apiSubmit<{ workflow: WorkflowRow; annexure: AnnexureView }>(`/standards-workflows/${wid}/meeting`, body),
  confirmAnnexure: (wid: string) => apiSubmit<{ workflow: WorkflowRow; annexure: AnnexureView }>(`/me/standards-workflows/${wid}/annexure/confirm`, {}),
  requestRevision: (wid: string, commentMd: string) => apiSubmit<{ workflow: WorkflowRow }>(`/me/standards-workflows/${wid}/annexure/request-revision`, { comment_md: commentMd }),
};

// labor portal surface
export interface PortalWorkflowView { workflow_id?: string; status: WorkflowStatus | null; labor_clear_at?: string | null; criteria: AcceptanceCriterion[]; messages: WorkflowMessage[]; annexure?: AnnexureView | null; docs?: PackageFileMeta[]; }
export function portalStandardsWorkflow(eid: string) { return api<PortalWorkflowView>(`/portal/engagements/${eid}/standards-workflow`); }
export const portalWorkflowActions = {
  ask: (eid: string, bodyMd: string) => apiSubmit(`/portal/engagements/${eid}/standards-workflow/questions`, { body_md: bodyMd }),
  clear: (eid: string) => apiSubmit<{ ok: boolean; status: WorkflowStatus }>(`/portal/engagements/${eid}/standards-workflow/clear`, {}),
  confirmAnnexure: (eid: string) => apiSubmit<{ ok: boolean; status: WorkflowStatus; annexure: AnnexureView }>(`/portal/engagements/${eid}/standards-workflow/annexure/confirm`, {}),
  requestRevision: (eid: string, commentMd: string) => apiSubmit<{ ok: boolean; status: WorkflowStatus }>(`/portal/engagements/${eid}/standards-workflow/annexure/request-revision`, { comment_md: commentMd }),
};

// docs: downloads + contract generation + admin templates
export function annexureDownloadUrl(vid: string) { return `/api/annexure/${vid}/download`; }
export function contractDownloadUrl(cgId: string) { return `/api/contract-generations/${cgId}/download`; }
export interface ContractGeneration { id: string; annexure_version_id: string; annexure_version: number; template_id: string; file_id: string | null; fill_report: { nulls: string[] }; generated_by: string | null; created_at: string; }
export function listContractGenerations(scId: string) { return api<{ generations: ContractGeneration[] }>(`/subcontracts/${scId}/contract-generations`); }
/** templateId lets the caller pick a specific contract template (e.g. 固定单价 vs 固定总价) instead of
 *  falling back to whichever one is marked "active" — the picker in ContractSection drives this. */
export function generateContract(scId: string, opts?: { annexureVersionId?: string; templateId?: string }) {
  return apiSubmit<{ ok: boolean; generation_id: string; template_id: string; file_id: string; fill_report: { nulls: string[] } }>(
    `/subcontracts/${scId}/contract/generate`,
    { ...(opts?.annexureVersionId ? { annexure_version_id: opts.annexureVersionId } : {}), ...(opts?.templateId ? { template_id: opts.templateId } : {}) });
}

export interface DocTemplate { id: string; kind: 'contract' | 'annexure'; file_name: string; active: boolean; archived?: boolean; archived_at?: string | null; uploaded_by: string | null; created_at: string; placeholders: string[]; validation: { unknown: string[]; missing: string[]; errors: { tag: string | null; explanation: string }[]; no_tags?: boolean }; }
export interface PlaceholderSpec { name: string; required?: boolean; kind?: string; children?: string[]; zh: string; sample: string; }
export function listDocTemplates() { return api<{ templates: DocTemplate[]; default_annexure_builtin: boolean }>('/admin/doc-templates'); }
/** Contract-generation-time template picker (budget/procurement/gm — broader than the admin-only
 *  upload/activate surface). Lists ALL validated templates of a kind, not just the active one. */
export function listTemplatesForGeneration(kind: 'contract' | 'annexure') {
  return api<{ templates: DocTemplate[] }>(`/doc-templates?kind=${kind}`);
}
export function docTemplatePlaceholders() { return api<{ contract: PlaceholderSpec[]; annexure: PlaceholderSpec[] }>('/admin/doc-templates/placeholders'); }
/** Junk/duplicate cleanup — 409 template_in_use when contract generations reference it (audit trail). */
export function deleteDocTemplate(tid: string) {
  return apiSubmit<{ ok: boolean; deleted: string; file_name: string }>(`/admin/doc-templates/${tid}`, undefined, 'DELETE');
}
export function uploadDocTemplate(kind: 'contract' | 'annexure', fileName: string, fileB64: string) {
  return apiSubmit<{ ok: boolean; duplicate?: boolean; template: { id: string; kind: string; file_name: string; validation: DocTemplate['validation']; placeholders: string[] } }>(
    '/admin/doc-templates', { kind, file_name: fileName, file_b64: fileB64 });
}
export function activateDocTemplate(tid: string) { return apiSubmit<{ ok: boolean }>(`/admin/doc-templates/${tid}/activate`, {}); }
/** Eric 9/18: a template referenced by contract generations can't be deleted — archive hides it from the picker instead. */
export function archiveDocTemplate(tid: string, archived: boolean) { return apiSubmit<{ ok: boolean }>(`/admin/doc-templates/${tid}/${archived ? 'archive' : 'unarchive'}`, {}); }
export function validateDocTemplate(tid: string) { return apiSubmit<{ ok: boolean; validation: DocTemplate['validation'] }>(`/admin/doc-templates/${tid}/validate`, {}); }

// ── 定标投票 / labor-recommendation vote round (2026-07 meeting, explainer §6) ─────────────────
// The pre-invite recommendation vote. Payloads are PRICE-FREE on purpose — 项目经理 is on the panel
// and is locked out of every pricing surface (0f3f0c7), so PMs vote from 我的任务, never the compare tab.
export type VoteRoundStatus = 'open' | 'completed' | 'cancelled';
/** 推荐 (the panel proposes the slate) → 表决 (the panel votes on it). Legacy rounds are born 'voting'. */
export type VoteRoundPhase = 'nominating' | 'voting';
export interface VoteRound {
  id: string; org_id: string; package_id: string; status: VoteRoundStatus; phase: VoteRoundPhase; note: string | null;
  created_by_staff_id: string | null; created_by_name: string | null; created_at: string; completed_at: string | null;
  /** last 催一下 — the server rate-limits to one per 30 分钟 PER ROUND (creator and GM share the clock). */
  last_nudged_at?: string | null;
}
/** 验收 outcome the PM signed off (null = the job never reached acceptance). */
export type VoteAcceptanceOutcome = 'in_progress' | 'passed' | 'escalated';
export interface VoteCandidatePastPackage {
  name: string; status: string;
  acceptance: VoteAcceptanceOutcome | null;   // PM 验收
  final_inspection: string | null;            // GM 终验 (in_progress | passed)
}
/** Track record with us — counts + package NAMES + 验收/终验 outcomes only (no value; a PM reads this). */
export interface VoteCandidateHistory {
  past_engagements: number; awarded: number;
  acceptance_passed: number; acceptance_escalated: number; final_passed: number;
  packages: VoteCandidatePastPackage[];
}
/** Bilingual label for a past package's 验收 outcome — reuses the shared pill classes, no new colours. */
export const VOTE_ACCEPTANCE_LABEL: Record<VoteAcceptanceOutcome, { label: string; pill: string }> = {
  passed: { label: '验收通过 / accepted', pill: 'pill-good' },
  escalated: { label: '验收整改升级 / escalated', pill: 'pill-warn' },
  in_progress: { label: '验收中 / accepting', pill: 'pill-muted' },
};
/** 预算 phones the candidate and records what they said (改改 fb_828ed0af). 不可参与 drops the company
 *  out of every ballot without deleting it — the record of who was asked survives. */
export type VoteAvailability = 'unknown' | 'available' | 'unavailable';
export const VOTE_AVAILABILITY_LABEL: Record<VoteAvailability, { label: string; pill: string }> = {
  unknown: { label: '未联系 / not contacted', pill: 'pill-muted' },
  available: { label: '可参与 / available', pill: 'pill-good' },
  unavailable: { label: '不可参与 / unavailable', pill: 'pill-bad' },
};
export interface VoteCandidate {
  id: string; round_id: string; labor_co_id: string | null; labor_co_name: string; sort_order: number;
  history: VoteCandidateHistory;
  availability?: VoteAvailability;          // absent on a pre-8/17 payload ⇒ treat as 未联系
  availability_note?: string | null;
  availability_by?: string | null;          // display-name snapshot of whoever phoned
  availability_at?: string | null;
}
/** reachable = 企业微信-bound OR has signed in at least once. false ⇒ this person may never see the round.
 *  task_reachable = mapped to the §11 task platform ⇒ a workbench task lands in their 任务 widget when
 *  the round opens; false ⇒ they still need an individual heads-up (改改 8/5); null/absent ⇒ the task
 *  platform is unconfigured, so mapping is moot and no warning should fire. */
export interface VoteVoter {
  id: string; round_id: string; staff_id: string | null; staff_name: string; role: string;
  voted_at: string | null; nominated_at: string | null; reachable: boolean; task_reachable?: boolean | null;
  /** the creator cut 推荐 short and stamped this member as 无补充 — they never submitted (提前进入表决). */
  nominated_forced?: boolean;
}
export interface VoteBallot { id: string; round_id: string; candidate_id: string; staff_id: string | null; staff_name: string | null; verdict: 'approve' | 'object'; note: string | null; created_at: string; }
/** One §11 platform-task mint attempt per (round, phase, panel member) — 改改 8/5: a failed row means that
 *  member's workbench will NOT show a task, silently, unless surfaced to a human. */
export interface VoteTaskRow {
  id: string; round_id: string; phase: 'nominating' | 'voting'; staff_id: string;
  task_uuid: string | null; status: 'minted' | 'completed' | 'cancelled' | 'failed'; error: string | null;
}
/** One panel member's 推荐 in the nominating phase, with their optional reason. */
export interface VoteNomination {
  id: string; round_id: string; staff_id: string | null; staff_name: string | null;
  labor_co_id: string; labor_co_name: string | null; note: string | null; created_at: string;
}
export interface VoteRoundView {
  round: VoteRound | null; candidates: VoteCandidate[]; voters: VoteVoter[]; ballots: VoteBallot[];
  nominations?: VoteNomination[];
  /** everyone submitted their 推荐 and nobody named anyone — the creator must add names or cancel. */
  nomination_stalled?: boolean;
  /** the package's most recent COMPLETED round — so a cancelled/new round never hides the decision (V4). */
  last_completed?: VoteRoundView | null;
  package?: { id: string; name: string; project_name: string | null } | null;
  /** §11 platform-task mint bookkeeping for this round — a 'failed' row means that member's workbench
   *  will not show a task at all (改改 8/5); surfaced so a mint failure is never silent behind a toast. */
  vote_tasks?: VoteTaskRow[];
  /** candidates still IN PLAY (未联系 counts — the shortage is about who said no, not who we've yet to
   *  phone), the 不少于三家 threshold, and the gap. Advisory: the invite gate reads STATUS only. */
  available_count?: number;
  min_bidders?: number;
  short_by?: number;
  /** MY remaining ballots on this round — a top-up asks for the additions only, never the whole slate. */
  my_pending_candidate_ids?: string[];
  /** The PACKAGE-level slate: one row per company across every non-cancelled round (the later round's
   *  联系结果 wins). Once a round closes the only way to add names is another round, and a one-round
   *  payload made round 1's confirmed company vanish (杨丽刚 fb_712b4d4b). On the `latest` payload
   *  `available_count/min_bidders/short_by` are these pool figures. null = the package has no round. */
  pool?: VotePool | null;
}
export interface VotePoolCandidate {
  id: string; round_id: string; round_seq: number; labor_co_id: string | null; labor_co_name: string;
  availability: VoteAvailability; availability_note: string | null; availability_by: string | null; availability_at: string | null;
  approve: number; object: number;
}
export interface VotePool { rounds: number; candidates: VotePoolCandidate[]; available_count: number; min_bidders: number; short_by: number }
/** What 我的任务 owes me on this round: recommend, vote, or just read the outcome (read-only). */
export type MyVoteKind = 'nominate' | 'vote' | 'result';
export interface MyVoteRound extends VoteRoundView { kind: MyVoteKind; round: VoteRound; package: { id: string; name: string; project_name: string | null } | null; }
export interface BallotInput { candidate_id: string; verdict: 'approve' | 'object'; note?: string }
export interface NominationInput { labor_co_id: string; note?: string }
/** One row of the panel's registry browser — price-free, with the same track record the ballot shows. */
export interface VoteRegistryCo {
  id: string; name: string; internal_code: string | null; region: string | null; status: string | null;
  trades_json: string | null; disabled: boolean; disabled_reason: string | null; history: VoteCandidateHistory;
}

export function getLatestVoteRound(pid: string) { return api<VoteRoundView>(`/packages/${pid}/vote-rounds/latest`); }
/** Omit (or empty) candidate_labor_co_ids to open a 推荐-phase round: the panel proposes the slate. */
export function createVoteRound(pid: string, body: { candidate_labor_co_ids?: string[]; voter_staff_ids: string[]; note?: string }) {
  return apiSubmit<VoteRoundView>(`/packages/${pid}/vote-rounds`, body);
}
/** The full org registry for the 推荐 phase (voter-only, nominating-only). */
export function voteRoundRegistry(rid: string) { return api<{ round_id: string; labor_cos: VoteRegistryCo[] }>(`/vote-rounds/${rid}/registry`); }
/** My 推荐 — one submission per voter; an EMPTY list is a valid 无补充 submission. */
export function submitVoteNominations(rid: string, nominations: NominationInput[]) {
  return apiSubmit<VoteRoundView & { ok: boolean; advanced: boolean }>(`/vote-rounds/${rid}/nominations`, { nominations });
}
/** Creator/GM drop a panel member from an open round (quorum-checked server-side). */
export function removeVoteVoter(rid: string, vid: string, reason?: string) {
  return apiSubmit<VoteRoundView & { ok: boolean }>(`/vote-rounds/${rid}/voters/${vid}/remove`, { reason });
}
/** Creator/GM add panel members to an open round — the newcomer owes the CURRENT phase's act. */
export function addVoteVoters(rid: string, staffIds: string[], reason?: string) {
  return apiSubmit<VoteRoundView & { ok: boolean; added: number }>(`/vote-rounds/${rid}/voters`, { staff_ids: staffIds, reason });
}
/** FULL SLATE — exactly one ballot per candidate, 反对 must carry a note (server-enforced too). */
export function submitVoteBallots(rid: string, ballots: BallotInput[]) {
  return apiSubmit<VoteRoundView & { ok: boolean; completed: boolean }>(`/vote-rounds/${rid}/ballots`, { ballots });
}
export function cancelVoteRound(rid: string, reason?: string) { return apiSubmit<VoteRoundView>(`/vote-rounds/${rid}/cancel`, { reason }); }
/** 记录联系结果 (估价/采购/GM). 不可参与 leaves the candidate visible but out of everyone's ballot; a
 *  candidate that comes BACK 可参与 reopens whoever never balloted it. Allowed on a completed round too
 *  (informational — it never reopens one). */
export function setVoteCandidateAvailability(rid: string, cid: string, body: { availability: VoteAvailability; note?: string }) {
  return apiSubmit<VoteRoundView & { ok: boolean; completed: boolean }>(`/vote-rounds/${rid}/candidates/${cid}/availability`, body);
}
/** 补充候选 (creator/GM, OPEN 表决 round only) — everyone then owes a ballot on exactly the additions. */
export function addVoteCandidates(rid: string, body: { labor_co_ids: string[]; availability?: 'unknown' | 'available'; note?: string }) {
  return apiSubmit<VoteRoundView & { ok: boolean; added: number }>(`/vote-rounds/${rid}/candidates`, body);
}
/** 提前进入表决 — stamp everyone who has not recommended as 无补充 and flip. `skipped` names them. */
export function advanceVoteRound(rid: string) {
  return apiSubmit<VoteRoundView & { ok: boolean; advanced: boolean; skipped: string[] }>(`/vote-rounds/${rid}/advance`, {});
}
/** 催一下 — re-ping whoever still owes THIS phase. `channels` reports which pipes are CONFIGURED (not
 *  delivered): both false ⇒ the nudge was only written to the audit trail. 429 nudge_too_soon carries
 *  the minutes left in `detail`. */
export function nudgeVoteRound(rid: string) {
  return apiSubmit<VoteRoundView & { ok: boolean; nudged: number; channels: { wecom: boolean; chat: boolean } }>(`/vote-rounds/${rid}/nudge`, {});
}
/** My pending votes — powers the 我的任务 section (the PM's only voting surface). */
export function myVoteRounds() { return api<{ rounds: MyVoteRound[] }>('/me/vote-rounds'); }

export const VOTE_STATUS_LABEL: Record<VoteRoundStatus, { label: string; pill: string }> = {
  open: { label: '投票中 / voting', pill: 'pill-warn' },
  completed: { label: '已完成 / completed', pill: 'pill-good' },
  cancelled: { label: '已取消 / cancelled', pill: 'pill-muted' },
};
/** The open round's current step. 推荐 and 表决 both block 邀请劳务 — the gate reads status, not phase. */
export const VOTE_PHASE_LABEL: Record<VoteRoundPhase, { label: string; pill: string }> = {
  nominating: { label: '推荐中 / nominating', pill: 'pill-stage' },
  voting: { label: '表决中 / voting', pill: 'pill-warn' },
};
/** Rounds that need me to DO something — completed results ride along read-only and never badge. */
export function pendingVoteCount(rounds: MyVoteRound[] | undefined): number {
  return (rounds ?? []).filter((r) => r.kind !== 'result').length;
}
/** 定标投票 is opt-in per package: only an OPEN round blocks 邀请劳务. */
export function voteBlocksInvite(v: VoteRoundView | undefined): boolean { return v?.round?.status === 'open'; }

// ══ 履约档案 / compliance file (Layer 1 · Eric 9/18) ═══════════════════════════════════════════════
// Eric 9/18 (compliance file): the shapes below MIRROR server/src/lib/compliance.ts — ComplianceItemView
// etc. are re-declared here rather than imported, because client and server are separate tsconfigs.
// Every field is money-free by construction (the server guarantees it), which is what lets the SAME
// types serve the contractor portal (4A) and the staff panel (4B). The label maps live here on purpose:
// one translation of 状态/类别/频次 for both sides, so 改改 and 江卫春 never read different words.

export type ComplianceCadence =
  | 'once' | 'pre_mobilization' | 'monthly' | 'per_event' | 'at_completion' | 'at_settlement' | 'warranty';
export type StoredComplianceStatus = 'missing' | 'uploaded' | 'confirmed' | 'returned' | 'waived';
/** What the UI renders — the last three are derived on read and never stored. */
export type EffectiveComplianceStatus = StoredComplianceStatus | 'expiring' | 'expired' | 'lapsed';
export type ContractForm = 'unit_price' | 'lump_sum';

export interface ComplianceFileView {
  id: string; file_id: string; file_name: string; size_bytes: number | null; mime_type: string | null;
  uploaded_side: 'labor' | 'wtl'; uploaded_by: string | null; created_at: string;
}

/** One checklist slot, joined with its type + files and the derived status. NO money fields, ever. */
export interface ComplianceItemView {
  id: string; org_id: string; subcontract_id: string; engagement_id: string | null; package_id: string;
  labor_co_id: string | null; item_type_id: string;
  item_code: string; category: string; required: number; policy_only: number; cadence: ComplianceCadence;
  status: StoredComplianceStatus; period_key: string | null;
  due_at: string | null; expires_at: string | null; hard_deadline_at: string | null;
  submitted_at: string | null; submitted_by: string | null;
  reviewed_at: string | null; reviewed_by: string | null; review_note_md: string | null;
  confirmed_at: string | null; confirmed_by: string | null;
  waived_at: string | null; waived_by: string | null; waive_reason_md: string | null;
  /** 劳务申请免除 (江卫春 9/20): the contractor's "we do not hold this" — a request, never a status. */
  waiver_requested_at: string | null; waiver_requested_by: string | null; waiver_request_md: string | null;
  copied_from_item_id: string | null; note_md: string | null;
  on_behalf_of_labor: number; last_nudged_at: string | null; created_at: string; updated_at: string;
  label_zh: string; label_en: string; owner_side: 'labor' | 'wtl' | 'both'; owner_role: string | null;
  expiry_tracked: number; rights_extinguishing: number; deadline_days: number | null;
  wtl_countersign: number; scope: 'subcontract' | 'supplier';
  evidence_hint_zh: string | null; evidence_hint_en: string | null; clause_ref: string | null;
  sort_order: number;
  effective_status: EffectiveComplianceStatus;
  files: ComplianceFileView[];
}

export interface ComplianceCounts {
  total: number;
  missing: number; uploaded: number; confirmed: number; returned: number;
  expiring: number; expired: number; lapsed: number; waived: number;
  /** required items only — the 完成度 x/21 a header shows. */
  required_total: number; required_done: number; outstanding: number;
  awaiting_review: number;
}

/** A per-event type the contractor may open a new record for (＋新增记录). */
export interface ComplianceEventType {
  code: string; category: string; label_zh: string; label_en: string;
  cadence: ComplianceCadence; required: number; policy_only: number;
  owner_side: 'labor' | 'wtl' | 'both';
  expiry_tracked: number; rights_extinguishing: number; deadline_days: number | null;
  wtl_countersign: number;
  evidence_hint_zh: string | null; evidence_hint_en: string | null; clause_ref: string | null;
  sort_order: number;
}

export interface ComplianceTodoView {
  total: number; missing: number; returned: number; expiring: number; expired: number; lapsed: number;
  items: ComplianceItemView[];
  by_engagement: Record<string, number>;
}

export interface PortalCompliance {
  open: boolean;
  /** why the 履约 tab is not open yet — the tab is HIDDEN then, and the bid tab is untouched */
  reason?: 'not_awarded' | 'no_subcontract' | 'not_signed';
  detail?: string;
  subcontract_id: string | null;
  contract_form: ContractForm | null;
  executed_contract: { file_id: string; file_name: string; signed_on: string | null } | null;
  items: ComplianceItemView[];
  event_types: ComplianceEventType[];
  counts: ComplianceCounts;
  todo: ComplianceTodoView;
}

/** The portal nav badge 「待办 N」 — required outstanding work across every OPEN engagement. */
export interface PortalTodoCount {
  total: number; missing: number; returned: number; expiring: number; expired: number; lapsed: number;
  by_engagement: Array<{ engagement_id: string; package_name: string | null; n: number }>;
}

export function portalCompliance(eid: string) { return api<PortalCompliance>(`/portal/engagements/${eid}/compliance`); }
/**
 * File documents into one slot. Multi-append `file` — the server reads `file`×N with req.parts(), and a
 * phone camera roll routinely hands over several at once. `expires_at` is REQUIRED when the type tracks
 * 有效期 (the server 400s `expires_at_required`; the UI asks for it before letting 确认上传 fire).
 */
export function portalComplianceUpload(eid: string, itemId: string, files: File[], fields: { expires_at?: string; note_md?: string } = {}) {
  const fd = new FormData();
  for (const f of files) fd.append('file', f);
  if (fields.expires_at) fd.append('expires_at', fields.expires_at);
  if (fields.note_md) fd.append('note_md', fields.note_md);
  return apiUpload<{ ok: boolean; item: ComplianceItemView; files: ComplianceFileView[] }>(`/portal/engagements/${eid}/compliance/items/${itemId}/files`, fd);
}
export function portalComplianceAddItem(eid: string, itemCode: string) {
  return apiSubmit<{ ok: boolean; item: ComplianceItemView }>(`/portal/engagements/${eid}/compliance/items`, { item_code: itemCode });
}
/** 「无此资质 · 申请免除」— asks WTL to waive a required slot; the slot stays 缺失 until gm/预算 decide.
 *  409 waiver_not_requestable on optional / uploaded / confirmed / already-waived slots, 400 reason_required. */
export function portalComplianceRequestWaiver(eid: string, itemId: string, reason_md: string) {
  return apiSubmit<{ ok: boolean; item: ComplianceItemView }>(`/portal/engagements/${eid}/compliance/items/${itemId}/waiver-request`, { reason_md });
}
/** apiSubmit (not api) so a 409 already_confirmed arrives as a parsed body apiErrText can speak. */
export function portalComplianceDeleteFile(eid: string, fileId: string) {
  return apiSubmit<{ ok: boolean; deleted: string; item: ComplianceItemView }>(`/portal/engagements/${eid}/compliance/files/${fileId}`, undefined, 'DELETE');
}
export function portalComplianceFileUrl(eid: string, fileId: string) { return `/api/portal/engagements/${eid}/compliance/files/${fileId}/download`; }
export function portalTodoCount() { return api<PortalTodoCount>('/portal/todo-count'); }

/** 状态 — the plan's copy table, verbatim, with the pill class each one wears. */
export const COMPLIANCE_STATUS: Record<EffectiveComplianceStatus, { label: string; pill: string }> = {
  missing: { label: '缺失 / Missing', pill: 'pill-bad' },
  uploaded: { label: '已上传待审 / Uploaded · in review', pill: 'pill-warn' },
  confirmed: { label: '已确认 / Confirmed', pill: 'pill-good' },
  returned: { label: '已退回 / Returned', pill: 'pill-bad' },
  expiring: { label: '即将过期 / Expiring', pill: 'pill-warn' },
  expired: { label: '已过期 / Expired', pill: 'pill-bad' },
  lapsed: { label: '逾期失权 / Rights lapsed', pill: 'pill-bad' },
  waived: { label: '已免除 / Waived', pill: 'pill-muted' },
};
/** 类别 A–H. The letter is part of the label because the contract cites its own clauses that way. */
export const COMPLIANCE_CATEGORY: Record<string, string> = {
  A: 'A 主体与资质 / Entity & qualifications',
  B: 'B 合同与附件 / Contract & annexes',
  C: 'C 进场与人员 / Mobilization & workforce',
  D: 'D 安全与保险 / Safety & insurance',
  E: 'E 指令与变更 / Instructions & variations',
  F: 'F 计量与认价 / Measurement & pricing',
  G: 'G 质量与验收 / Quality & acceptance',
  H: 'H 工资付款结算 / Wages, payment & settlement',
};
export const COMPLIANCE_CADENCE: Record<ComplianceCadence, string> = {
  once: '签约时一次 / Once at signing',
  pre_mobilization: '进场前 / Before mobilization',
  monthly: '每月 / Monthly',
  per_event: '按事件 / Per event',
  at_completion: '竣工时 / At completion',
  at_settlement: '结算时 / At settlement',
  warranty: '质保期 / Warranty',
};
export const CONTRACT_FORM_LABEL: Record<ContractForm, string> = {
  unit_price: '固定单价 / Fixed unit price',
  lump_sum: '固定总价 / Lump sum',
};
/** policy_only copy, verbatim from the plan — D4/G5 are daily contract duties collected monthly. */
export const COMPLIANCE_POLICY_ONLY_LABEL = '公司要求，非合同条款 / WTL policy — not a contract clause';
/** 已退回 line, verbatim from the plan's L1.3 copy table. */
export function complianceReturnLine(reason: string | null): string {
  return `已退回：${reason?.trim() || '—'} — 请修正后重新上传 / Returned: ${reason?.trim() || '—'} — fix and re-upload`;
}
/** 尚未定标 / 合同未生成 — what the portal says instead of showing an empty 履约 tab. */
export const COMPLIANCE_NOT_OPEN_REASON: Record<'not_awarded' | 'no_subcontract' | 'not_signed', string> = {
  not_awarded: '尚未定标，履约档案未开启 / Not awarded yet — the compliance file opens after award',
  no_subcontract: '合同尚未生成，履约档案未开启 / No subcontract yet — the compliance file opens once the contract exists',
  // review 9/18: the paper follows the signature — a draft subcontract does not open the file
  not_signed: '合同尚未签署，记录已签署后履约档案自动开启 / Not signed yet — the compliance file opens when the signature is recorded',
};

// ── Layer 1 · staff compliance (4B) ── (block start; everything below is 4B-only)

/** 复用 source: a supplier-level record already CONFIRMED on another job of the same labor company. */
export interface ComplianceReusable {
  item_id: string; item_code: string; label_zh: string; label_en: string;
  subcontract_id: string; package_id: string; package_name: string | null; project_name: string | null;
  confirmed_at: string | null; expires_at: string | null; expiry_tracked: number;
  files: ComplianceFileView[];
}

/**
 * Why the 履约档案 is closed. The gate is SIGNED-only on BOTH halves: the staff read says
 * no_subcontract or not_signed, the portal read adds not_awarded. Named here so both panels key the
 * SHARED COMPLIANCE_NOT_OPEN_REASON map (declared just above, with 4A) off one union.
 */
export type ComplianceNotOpenReason = 'not_awarded' | 'no_subcontract' | 'not_signed';

/** The payload BOTH staff reads return. `open:false` is not an error — the tab still renders the reason. */
export interface StaffComplianceView {
  open: boolean;
  /** why the file is closed: no subcontract at all, or one whose signature is not recorded yet */
  reason: ComplianceNotOpenReason | null;
  package_id: string;
  subcontract_id: string | null;
  subcontract_status: string | null;
  contract_id: string | null;
  contract_form: ContractForm | null;
  engagement_id: string | null;
  labor_co_id: string | null;
  labor_co_name: string | null;
  executed_file: { id: string; file_name: string; size_bytes: number | null; mime_type: string | null } | null;
  items: ComplianceItemView[];
  counts: ComplianceCounts;
  event_types: ComplianceEventType[];
  reusable: ComplianceReusable[];
  settings?: { expiry_warn_days?: number; nudge_cooldown_min?: number; monthly_due_day?: number; sweep_enabled?: boolean };
}

export interface ComplianceQueueRow extends ComplianceItemView {
  package_name: string | null; project_name: string | null; labor_co_name: string | null;
  subcontract_status: string; assigned_pm_staff_id: string | null;
}
export interface ComplianceQueueView {
  rows: ComplianceQueueRow[]; counts: ComplianceCounts; mine: boolean;
  status: string | null; category: string | null;
}

/** Which pipes are CONFIGURED (not delivered) — voting.ts precedent: an unconfigured channel is recorded only. */
export interface ComplianceChannels { in_app: boolean; wecom: boolean }
export interface ComplianceMutation {
  ok: boolean; item: ComplianceItemView;
  settlement_deadline?: { item_id: string; hard_deadline_at: string } | null;
  uploaded?: number; copied?: number;
  channels?: ComplianceChannels;
  delivery_status?: 'success' | 'dev_mode' | 'failure' | 'no_thread';
}

// reads
export function staffCompliancePackage(pid: string) { return api<StaffComplianceView>(`/packages/${pid}/compliance`); }
export function staffComplianceSubcontract(scId: string) { return api<StaffComplianceView>(`/subcontracts/${scId}/compliance`); }
export function staffComplianceQueue(opts: { status?: string; category?: string; mine?: boolean } = {}) {
  const q = new URLSearchParams();
  if (opts.status) q.set('status', opts.status);
  if (opts.category) q.set('category', opts.category);
  if (opts.mine) q.set('mine', '1');
  const qs = q.toString();
  return api<ComplianceQueueView>(`/compliance/queue${qs ? `?${qs}` : ''}`);
}
/** Scoped through compliance_item_files server-side — a bare package_files id is NOT readable here. */
export function staffComplianceFileUrl(fileId: string) { return `/api/compliance/files/${fileId}/download`; }

// subcontract-level writes
export function staffComplianceInstantiate(scId: string) {
  return apiSubmit<{ ok: boolean; created?: number; items: ComplianceItemView[]; counts: ComplianceCounts }>(
    `/subcontracts/${scId}/compliance/instantiate`, {});
}
export function staffComplianceSetForm(scId: string, contract_form: ContractForm) {
  return apiSubmit<StaffComplianceView & { ok: boolean }>(`/subcontracts/${scId}/compliance/form`, { contract_form });
}
export function staffComplianceAddItem(scId: string, item_code: string, note_md?: string) {
  return apiSubmit<ComplianceMutation>(`/subcontracts/${scId}/compliance/items`, { item_code, note_md });
}

// item-level writes
/** 代上传 — multipart `file`×N (≤10, 30 MB each) plus expires_at / note_md / on_behalf. */
export function staffComplianceUpload(itemId: string, form: FormData) {
  return apiUpload<ComplianceMutation>(`/compliance/items/${itemId}/files`, form);
}
export function staffComplianceConfirm(itemId: string, note_md?: string) {
  return apiSubmit<ComplianceMutation>(`/compliance/items/${itemId}/confirm`, { note_md });
}
export function staffComplianceReturn(itemId: string, reason_md: string) {
  return apiSubmit<ComplianceMutation>(`/compliance/items/${itemId}/return`, { reason_md });
}
export function staffComplianceWaive(itemId: string, reason_md: string) {
  return apiSubmit<ComplianceMutation>(`/compliance/items/${itemId}/waive`, { reason_md });
}
export function staffComplianceUnwaive(itemId: string) {
  return apiSubmit<ComplianceMutation>(`/compliance/items/${itemId}/unwaive`, {});
}
/** Keys you omit are untouched; an explicit null clears one. */
export function staffComplianceDates(itemId: string, body: { due_at?: string | null; expires_at?: string | null; hard_deadline_at?: string | null }) {
  return apiSubmit<ComplianceMutation>(`/compliance/items/${itemId}/dates`, body);
}
export function staffComplianceReuse(itemId: string, source_item_id: string) {
  return apiSubmit<ComplianceMutation>(`/compliance/items/${itemId}/reuse`, { source_item_id });
}
export function staffComplianceNudge(itemId: string, message_md?: string) {
  return apiSubmit<ComplianceMutation>(`/compliance/items/${itemId}/nudge`, { message_md });
}

/** 「已记录提醒：站内 ✓ · 企业微信 {✓|✗}（未配置的通道只记录，不会真正发出）」 — config, NOT delivery. */
export function complianceChannelLine(ch: ComplianceChannels | undefined): string {
  const tick = (b: boolean | undefined) => (b ? '✓' : '✗');
  return `已记录提醒：站内 ${tick(ch?.in_app)} · 企业微信 ${tick(ch?.wecom)}（未配置的通道只记录，不会真正发出）`
    + ` / Reminder recorded: in-app ${tick(ch?.in_app)} · WeCom ${tick(ch?.wecom)} (an unconfigured channel is logged, not sent)`;
}

// ── admin catalog (gm) ──
export interface ComplianceItemTypeRow {
  id: string; org_id: string; code: string; category: string; label_zh: string; label_en: string;
  owner_side: 'labor' | 'wtl' | 'both'; owner_role: string | null; cadence: ComplianceCadence;
  required: number; policy_only: number; form_applicability: 'both' | 'unit_price' | 'lump_sum';
  expiry_tracked: number; rights_extinguishing: number; deadline_days: number | null;
  wtl_countersign: number; scope: 'subcontract' | 'supplier';
  evidence_hint_zh: string | null; evidence_hint_en: string | null; clause_ref: string | null;
  sort_order: number; active: number; created_at: string; updated_at: string;
}
export interface ComplianceCatalogView {
  /** EVERY row, inactive included — 停用 is how you retire an item; there is no delete. */
  item_types: ComplianceItemTypeRow[];
  defaults: Array<Partial<ComplianceItemTypeRow> & { code: string }>;
  cadences: string[]; owner_sides: string[]; form_applicability: string[]; scopes: string[];
}
export function adminComplianceItemTypes() { return api<ComplianceCatalogView>('/admin/compliance-item-types'); }
export function adminComplianceCreateItemType(body: Record<string, unknown> & { code: string; cadence: string }) {
  return apiSubmit<{ ok: boolean; item_type: ComplianceItemTypeRow }>('/admin/compliance-item-types', body);
}
/** 409 `cadence_locked` once any slot snapshotted this code's cadence — the UI then disables the select. */
export function adminComplianceUpdateItemType(code: string, body: Record<string, unknown>) {
  return apiSubmit<{ ok: boolean; item_type: ComplianceItemTypeRow }>(`/admin/compliance-item-types/${code}`, body, 'PUT');
}
export function adminComplianceReseed() {
  return apiSubmit<{ ok: boolean; inserted: number; total: number; item_types: ComplianceItemTypeRow[] }>(
    '/admin/compliance-item-types/reseed', {});
}

/** 编号 regex the server enforces (A–H plus one or two digits). */
export const COMPLIANCE_CODE_RE = /^[A-H][0-9]{1,2}$/;

/** File size for the 文件 column — raw bytes are meaningless to a reviewer, "2.4 MB" is not. */
export function fileSizeText(bytes: number | null | undefined): string {
  if (bytes == null) return '';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

/**
 * The STAFF-ONLY refusal codes → bilingual copy, merged into ERR_COPY (declared next to apiErrText,
 * where 4A's contractor-side codes already live). Object.assign rather than an inline edit up there so
 * the whole 4B surface stays in one block; the codes 4A already spells (compliance_not_open,
 * item_not_uploadable, already_confirmed, no_file, too_many_files, expires_at_*, file_too_large,
 * unsupported_file_type, type_not_found, item_not_found, not_event_cadence) are deliberately NOT
 * repeated — one wording per code, for both audiences. The server's own `detail` still wins inside
 * apiErrText; these only cover a bare code that arrives with no detail string.
 */
Object.assign(ERR_COPY, {
  nothing_to_review: '该项没有待审内容 / nothing has been submitted for review on this item.',
  cannot_waive_confirmed: '已确认的项不能免除 — 如需作废请先退回。/ a confirmed item cannot be waived — return it first.',
  nothing_to_chase: '该项当前无需催办 / nothing is owed by the contractor on this item right now.',
  nudge_too_soon: '刚刚已催过，请稍后再试 / you nudged this item a moment ago — try again later.',
  not_supplier_scope: '该记录是本合同专有的，不能从其他项目复用 / this record is specific to this subcontract and cannot be copied forward.',
  source_not_found: '找不到可复用的已确认资料 / no confirmed record of this company is available to copy.',
  no_files: '源记录没有可用文件 / the source record has no readable files.',
  file_missing: '文件已丢失，请重新上传 / the stored file is missing — upload it again.',
  reason_required: '请填写理由 / a reason is required.',
  date_invalid: '日期格式须为 YYYY-MM-DD / expected a YYYY-MM-DD date.',
  invalid_form: '合同形式只能是固定单价或固定总价 / the contract form must be 固定单价 or 固定总价.',
  item_code_required: '请选择记录类型 / pick which record this is.',
  type_inactive: '该档案项目已停用 / that compliance item type is switched off.',
  cadence_locked: '该项目已在合同档案中使用，频次不能再改；请停用本项并新增一个编号。/ this item is already in use — deactivate it and add a new code instead of changing its cadence.',
  code_exists: '该编号已存在 / that code already exists.',
  invalid_code: '编号须为一个字母 A–H 加 1–2 位数字，如 H9 / the code must be a letter A–H plus one or two digits, e.g. H9.',
  subcontract_not_found: '合同不存在 / subcontract not found.',
  package_not_found: '工程包不存在 / package not found.',
});
// ── Layer 1 · staff compliance (4B) ── (block end)

// ══ Layer 2 · staff instructions (PR8) ═══════════════════════════════════════════════════════════
/**
 * 工程指令 / work instructions — the STAFF client half (Layer 2 PR8, Eric 9/18 (工程指令)).
 *
 * Two server halves feed this block and they are NOT interchangeable:
 *   · price-free (`routes/workInstructions.ts`, PR2) — `requireInternalOrAgent`, 项目经理 INCLUDED.
 *   · money (`routes/workInstructionsMoney.ts`, PR3) — `requireInternalNonPm`, site_pm 403 forbidden_site_pm.
 * Every money fetcher below is named `staffWi*` for exactly that reason: a site_pm code path must never
 * call one. The server refuses them anyway, but 改改 7/14 watched a real PM open a page with prices on
 * it, and a refused request still tells the PM a price exists. So the components branch on
 * `me.account.role === 'site_pm'` BEFORE the query, not after.
 *
 * Shapes marked "PR3/PR5/PR6 contract" are typed from the plan (§2.3), not from code that exists yet —
 * this PR was built types-first against the same table PR3 was handed.
 */

export type WorkInstructionKind = 'variation' | 'daywork';
export type WorkInstructionStateKey =
  | 'draft' | 'pending_signatures' | 'effective' | 'served' | 'quoted'
  | 'priced' | 'approved' | 'confirmed' | 'disputed' | 'void';
export type InstructionSeatKey = 'pm' | 'engineer' | 'cost';
export type PricingTierKey = 'identical' | 'similar' | 'negotiated' | 'buildup';
export type CommercialRoute = 'bill_client' | 'absorb';
export type DayworkStateKey = 'open' | 'submitted' | 'verified' | 'forfeited';

/**
 * One instruction as the server serialises it. EVERY money field is optional because
 * `instructions/view.ts` drops it wholesale for `audience:'pm'` — an optional field is the type-level
 * statement that a site_pm payload simply does not carry it.
 */
export interface WorkInstruction {
  id: string;
  package_id: string;
  subcontract_id: string;
  engagement_id?: string | null;
  seq: number;
  doc_no: string | null;
  kind: WorkInstructionKind;
  contract_form: ContractForm | null;
  variation_standard: string | null;
  title: string;
  reason_md: string | null;
  scope_md: string | null;
  location_md: string | null;
  stage_md: string | null;
  origin: string | null;
  occurred_on: string;
  drawing_ref: string | null;
  daywork_category: string | null;
  schedule_impact_days: number | null;
  state: WorkInstructionStateKey;
  state_history?: { at: string; from: string; to: string; by?: string | null; comment_md?: string | null }[];
  issued_at: string | null;
  issued_by_name: string | null;
  issued_by_staff_id?: string | null;
  effective_at: string | null;
  served_at: string | null;
  read_at: string | null;
  read_by?: string | null;
  quote_due_at: string | null;
  late_service: boolean;
  quote_submitted_at: string | null;
  quote_forfeited_at: string | null;
  pricing_due_at?: string | null;
  priced_at?: string | null;
  priced_by?: string | null;
  approved_at: string | null;
  approved_by?: string | null;
  confirmed_at: string | null;
  confirmed_by?: string | null;
  disputed_at: string | null;
  dispute_note_md: string | null;
  void_at: string | null;
  void_by?: string | null;
  void_reason_md: string | null;
  rework_required: boolean;
  rework_notified_at: string | null;
  rework_confirm_due_at: string | null;
  rework_confirmed_at: string | null;
  rework_confirmed_by?: string | null;
  doc_file_id: string | null;
  doc_rendered_at: string | null;
  supplement_file_id?: string | null;
  wet_signature_file_id?: string | null;
  created_at: string;
  updated_at: string;
  // ── money-only (absent for site_pm) ──
  estimate_rmb?: number | null;
  pre_approved_by?: string | null;
  pre_approved_at?: string | null;
  pre_approval_note_md?: string | null;
  commercial_route?: CommercialRoute | null;
  approved_amount_rmb?: number | null;
}

/**
 * Who is late on this order, BOTH ways round (PR7 `overdueFlags()`), derived server-side on every read —
 * a stored chip can desync from the clock, a derived one cannot. G6 is explicit that WTL's own blown SLA
 * is shown to both sides, so `wtl_overdue` is rendered as loudly as `labor_overdue`.
 */
export interface InstructionOverdue {
  quote_overdue: boolean;
  quote_forfeited: boolean;
  /** Hours until 报价期限; negative once past, null when no quote is owed (every 零星 order). */
  quote_hours_left: number | null;
  late_service: boolean;
  pricing_overdue: boolean;
  pricing_hours_left: number | null;
  rework_overdue: boolean;
  labor_overdue: boolean;
  wtl_overdue: boolean;
}

/** What the §2.3 ledger actually sends: `overdueFlags()` plus the per-day-sheet 核实 aggregate. */
export interface LedgerOverdue extends InstructionOverdue {
  /** G6: at least one submitted 计日工日报 is past its 2-工作日 verification window. */
  verify_overdue: boolean;
  /** Any clock, either side — the one flag a row's red chip hangs off. */
  any: boolean;
}

/** The 计日工 half of the same contract (PR7 `dayworkOverdueFlags()`). */
export interface DayworkOverdue {
  submission_overdue: boolean;
  hard_bar_passed: boolean;
  hours_to_bar: number | null;
  forfeited: boolean;
  verify_overdue: boolean;
  labor_overdue: boolean;
  wtl_overdue: boolean;
}

/** `pricing_json` v1 (§1.4). Written by the server; the sheet renders `steps[]` + `components`. */
export interface PricingComponent {
  kind: 'labor' | 'material' | 'machinery';
  label: string;
  qty: number;
  unit?: string | null;
  unit_price_rmb?: number | null;
  source?: 'zaojia_info' | 'quotation_median' | 'contract' | 'standard';
  note?: string | null;
  amount_rmb?: number;
  median_of?: number[];
  quotations?: { party: 'wtl' | 'labor'; quoted_unit_price_rmb: number }[];
  employer_confirmed?: boolean;
}

export interface PricingJson {
  v: 1;
  tier: PricingTierKey;
  unit_rate_rmb: number;
  qty: number;
  amount_rmb: number;
  steps: string[];
  source_work_item_id?: string | null;
  original_unit_rate_rmb?: number;
  adjustment_pct?: number;
  adjustment_rmb?: number;
  adjustment_reason?: string;
  negotiated_with?: string | null;
  negotiated_on?: string | null;
  minutes_md?: string | null;
  standard?: string;
  price_month?: string;
  price_month_override_reason?: string;
  components?: PricingComponent[];
  base_rmb?: number;
  discount_pct?: number;
  after_discount_rmb?: number;
  overhead_profit_pct?: number;
  overhead_profit_rmb?: number;
}

export interface WorkInstructionLine {
  id: string;
  seq: number;
  work_item_id?: string | null;
  item_label: string;
  spec_md: string | null;
  unit: string | null;
  qty: number | null;
  measurement_calc_md: string | null;
  created_at?: string;
  updated_at?: string;
  // ── money-only ──
  declared_unit_rate_rmb?: number | null;
  declared_amount_rmb?: number | null;
  declared_note_md?: string | null;
  tier?: PricingTierKey | null;
  source_work_item_id?: string | null;
  adjustment_note_md?: string | null;
  audited_unit_rate_rmb?: number | null;
  audited_amount_rmb?: number | null;
  /** Raw JSON string as stored — parse with `parsePricingJson`. */
  pricing_json?: string | null;
  /** ⚠ 不对劳务显示 — staff audience only; the portal serializer strips it. */
  internal_note_md?: string | null;
  priced_at?: string | null;
  priced_by?: string | null;
}

export interface WorkInstructionSignature {
  seat: InstructionSeatKey;
  seat_staff_id?: string | null;
  seat_staff_name?: string | null;
  signed_by_staff_id?: string | null;
  signed_by_name: string | null;
  signed_by_role?: string | null;
  on_behalf_of: string | null;
  note_md?: string | null;
  signed_at: string | null;
}

export interface WorkInstructionPhoto {
  id: string;
  kind: 'quote_evidence' | 'daywork' | 'rework_before' | 'rework_after' | 'onsite_qty';
  file_id: string;
  daywork_record_id: string | null;
  uploaded_by_type: 'labor_co' | 'staff';
  uploaded_by_name: string | null;
  taken_on: string | null;
  caption: string | null;
  created_at: string;
}

/** §1.11 — the header block. `payment_ceiling_basis` is always 'baseline'; `advisory` always true.
 *
 *  改改 9/20 (budget): every contract-value figure staff read must be 含税 first — the 不含税 figure and the
 *  税率 stay beside it, small, because the ledger is still kept ex-tax. The `*_incl_tax_rmb` twins are
 *  computed server-side off the SAME 税率 the lock carries and are ALL null when `tax_pct` is null; the
 *  client then shows the ex-tax figure with 「税率未录入」 rather than silently equating the two. */
export interface ContractValueView {
  baseline_rmb: number | null;
  additions_rmb: number;
  omissions_rmb: number;
  daywork_rmb: number;
  current_approved_rmb: number | null;
  pending_rmb: number;
  legacy_incorporated_rmb: number;
  payment_ceiling_basis: 'baseline';
  advisory: true;
  baseline_includes_legacy: boolean;
  /** 税金点 on the locked price — null when the lock never carried one. */
  tax_pct: number | null;
  baseline_incl_tax_rmb: number | null;
  additions_incl_tax_rmb: number | null;
  omissions_incl_tax_rmb: number | null;
  daywork_incl_tax_rmb: number | null;
  current_approved_incl_tax_rmb: number | null;
  pending_incl_tax_rmb: number | null;
  legacy_incorporated_incl_tax_rmb: number | null;
}

/** §1.8 认价单 — versioned; a re-price supersedes the old row and opens v(n+1). (PR3 contract.) */
export interface PriceConfirmation {
  id: string;
  version: number;
  total_declared_rmb: number | null;
  total_audited_rmb: number | null;
  additions_rmb: number | null;
  omissions_rmb: number | null;
  policy_label: string;
  approved_by: string | null;
  approved_at: string | null;
  commercial_route: CommercialRoute | null;
  labor_confirmed_by: string | null;
  labor_confirmed_at: string | null;
  labor_disputed_at: string | null;
  dispute_note_md: string | null;
  docx_file_id: string | null;
  status: 'open' | 'confirmed' | 'disputed' | 'superseded';
  created_at: string;
}

/** §1.7 计日工日报 (PR5 contract). `priced_amount_rmb` is money — absent for site_pm. */
export interface DayworkRecord {
  id: string;
  instruction_id: string;
  work_date: string;
  reason_md: string | null;
  work_content_md: string | null;
  work_qty_md: string | null;
  /** Raw TEXT columns AND the parsed arrays beside them — `instructions/view.ts` sends both. Read the
   *  parsed ones (`persons`/`materials`/`equipment`); a corrupt blob degrades to [] server-side. */
  persons_json: string | null;
  materials_json: string | null;
  equipment_json: string | null;
  persons: { name?: string; trade?: string; grade?: string; hours?: number }[];
  materials: { name?: string; category?: string; spec?: string; brand?: string; qty?: number; unit?: string }[];
  equipment: { model?: string; qty?: number; shifts?: number }[];
  other_vouchers_md: string | null;
  state: DayworkStateKey;
  due_at: string | null;
  hard_bar_at: string | null;
  verify_due_at: string | null;
  submitted_at: string | null;
  verified_at: string | null;
  verified_by: string | null;
  verify_note_md: string | null;
  forfeited_at: string | null;
  forfeit_reason: 'late_submission' | 'never_submitted' | null;
  forfeited: boolean;
  /** 9.2.11, verbatim, attached by the server whenever the record is forfeited. */
  contract_text_zh?: string;
  submitted_snapshot_json?: string | null;
  /** PR7 derived chips, computed on every read (never stored, so they cannot desync from the clock). */
  submission_overdue?: boolean;
  hard_bar_passed?: boolean;
  hours_to_bar?: number | null;
  verify_overdue?: boolean;
  labor_overdue?: boolean;
  wtl_overdue?: boolean;
  hours_left?: number | null;
  photos?: WorkInstructionPhoto[];
  /** money-only — absent for site_pm and for 乙方. */
  priced_amount_rmb?: number | null;
  pricing_json?: string | null;
  created_at: string;
  updated_at: string;
}

/** `GET /work-instructions/:wiid/daywork` and `GET /daywork-records/:dwid` share this envelope. */
export interface DayworkBrief {
  id: string;
  doc_no: string | null;
  title: string;
  state: WorkInstructionStateKey;
  kind: WorkInstructionKind;
  daywork_category: string | null;
  subcontract_id: string;
  engagement_id: string | null;
  package_id: string;
  effective_at: string | null;
}

export interface MaterialQuotation {
  id: string;
  line_id: string | null;
  material_label: string;
  spec: string | null;
  unit: string | null;
  party: 'wtl' | 'labor';
  supplier_name: string | null;
  quoted_unit_price_rmb: number;
  quoted_on: string | null;
  employer_confirmed_by: string | null;
  employer_confirmed_at: string | null;
  created_at: string;
}

export interface ResolvedSeat { staff_id: string | null; name: string | null; source: 'explicit' | 'default' | 'none' }
export type ResolvedSeats = Record<InstructionSeatKey, ResolvedSeat>;

/** What every instruction read returns (`instructions/view.ts`). `lines`/`photos` ride along. */
export interface InstructionDetail {
  instruction: WorkInstruction;
  lines: WorkInstructionLine[];
  signatures: WorkInstructionSignature[];
  signatures_signed: number;
  signatures_required: number;
  /** Present on every read EXCEPT the money ones that pass `includePhotos:false`. */
  photos?: WorkInstructionPhoto[];
  seats?: ResolvedSeats;
  warnings?: string[];
  already_signed?: boolean;
  /** §2.3 money reads only — absent from every price-free payload. */
  totals?: InstructionTotals;
  overdue?: LedgerOverdue;
  clocks?: InstructionClocks;
  subcontract?: InstructionSubcontractBrief;
  contract_value?: ContractValueView;
  /** Ledger row: the OPEN 认价单 only, trimmed. Detail: every version, with its parsed snapshot. */
  price_confirmation?: PriceConfirmationBrief | null;
  price_confirmations?: PriceConfirmation[];
  quotations?: QuotationsView;
  now?: string;
}

/** §2.3 `instructionTotals()` — 减项 are negative in `audited_rmb`, reported positive in `omissions_rmb`. */
export interface InstructionTotals {
  declared_rmb: number;
  audited_rmb: number;
  additions_rmb: number;
  omissions_rmb: number;
  lines_total: number;
  lines_priced: number;
}

/** The clock columns, grouped, so a row can render a deadline without reaching into the instruction. */
export interface InstructionClocks {
  occurred_on: string;
  quote_due_at: string | null;
  quote_submitted_at: string | null;
  quote_forfeited_at: string | null;
  late_service: boolean;
  pricing_due_at: string | null;
  priced_at: string | null;
  approved_at: string | null;
  confirmed_at: string | null;
}

export interface InstructionSubcontractBrief {
  id: string;
  package_id: string;
  engagement_id: string | null;
  status: string;
  contract_form: ContractForm | null;
  variation_standard: string | null;
}

/** What a ledger row carries of the 认价单 — enough for a chip, not the whole snapshot. */
export interface PriceConfirmationBrief {
  id: string;
  version: number;
  status: 'open' | 'confirmed' | 'disputed' | 'superseded';
  total_audited_rmb: number | null;
  approved_at: string | null;
}

/** §2.3 tier-④ 材料询价: the raw rows plus one summary per material (4.1.2's ≥3-per-party + 发包人确认). */
export interface QuotationsView {
  quotations: MaterialQuotation[];
  materials: MaterialSummary[];
}

export interface MaterialSummary {
  material_label: string;
  wtl_count: number;
  labor_count: number;
  min_quotations: number;
  employer_confirmed: boolean;
  employer_confirmed_at: string | null;
  median_rmb: number | null;
  median_of: number[] | null;
  complete: boolean;
  /** Why the median may not be used yet — already bilingual from the server. */
  blocked_reason: string | null;
}

/** §2.2 `GET /me/work-instructions` — one row per reason, so the same order can appear twice. */
// PR10b: `pricing_owed` / `approval_owed` come from the server so 待我定价 / 待我批准 are queue rows the
// nav badge counts, not something the client infers from a state + a role guess.
export type MyInstructionReason =
  'signature_owed' | 'issued_by_me' | 'rework_due' | 'daywork_to_verify' | 'pricing_owed' | 'approval_owed';
export interface MyInstructionItem extends InstructionDetail {
  reason: MyInstructionReason;
  seat: InstructionSeatKey | null;
  daywork_record_id: string | null;
}

/** §2.3 ledger — the money list plus the contract-value header. */
export interface InstructionLedger {
  subcontract: InstructionSubcontractBrief;
  contract_value: ContractValueView;
  pricing_defaults?: { discount_pct: number; overhead_profit_pct: number };
  seats: ResolvedSeats;
  /** seq DESC — newest order first, which is the one somebody is arguing about. */
  items: InstructionDetail[];
  now: string;
}

export interface InstructionLineInput {
  item_label: string;
  unit?: string | null;
  qty?: number | null;
  work_item_id?: string | null;
  spec_md?: string | null;
  measurement_calc_md?: string | null;
}

export interface InstructionDraftInput {
  kind: WorkInstructionKind;
  title: string;
  reason_md?: string | null;
  scope_md?: string | null;
  location_md?: string | null;
  stage_md?: string | null;
  origin?: string | null;
  occurred_on: string;
  drawing_ref?: string | null;
  daywork_category?: string | null;
  schedule_impact_days?: number | null;
  /** 预算/商务 only — the PM never types money (the 容错额度 estimate is a 预算/商务 field). */
  estimate_rmb?: number | null;
  rework_required?: boolean;
  lines: InstructionLineInput[];
}

export interface PriceLineInput {
  tier: PricingTierKey;
  source_work_item_id?: string | null;
  adjustment_pct?: number | null;
  adjustment_rmb?: number | null;
  adjustment_reason?: string | null;
  negotiated_unit_rate_rmb?: number | null;
  negotiated_with?: string | null;
  negotiated_on?: string | null;
  minutes_md?: string | null;
  buildup?: { standard?: string | null; price_month?: string | null; components?: PricingComponent[] } | null;
  internal_note_md?: string | null;
  override_reason?: string | null;
}

// ── labels (VERBATIM from the plan §3 — do not paraphrase; 改改 reads these against the contract) ──

export const WI_STATE: Record<WorkInstructionStateKey, { label: string; pill: string; note?: string }> = {
  draft: { label: '草稿 / Draft', pill: 'pill-muted' },
  pending_signatures: {
    label: '待签字（未生效）/ Pending signatures',
    pill: 'pill-warn',
    note: '签字不全或未经签字，不作为结算办理依据。未经书面指令擅自施工的，发包人不予计量、不予支付。',
  },
  effective: { label: '已生效 / Effective', pill: 'pill-good' },
  served: { label: '已送达 / Served', pill: 'pill-good' },
  quoted: { label: '已报价，待审核 / Quoted', pill: 'pill-stage' },
  priced: { label: '已审核，待商务批准 / Priced', pill: 'pill-stage' },
  approved: { label: '已批准，待劳务认价 / Approved', pill: 'pill-stage' },
  confirmed: { label: '已认价 / Confirmed', pill: 'pill-good' },
  disputed: { label: '劳务有异议 / Disputed', pill: 'pill-bad' },
  void: { label: '已作废 / Void', pill: 'pill-muted' },
};

/** 4.1.2's four-step waterfall, in the contract's own words. The radio labels ARE the clause. */
export const TIER_LABEL: Record<PricingTierKey, string> = {
  identical: '① 项目特征相同 → 执行原合同固定综合单价',
  similar: '② 项目特征类似 → 按原单价调整',
  negotiated: '③ 无类似项目 → 双方协商确定',
  buildup: '④ 协商不成 → 按北京市消耗量标准组价（造价信息价·人工中位数·下浮10%·管理费利润3%）',
};

export const DAYWORK_STATE: Record<DayworkStateKey, { label: string; pill: string; note?: string }> = {
  open: { label: '未提交 / Not submitted', pill: 'pill-warn' },
  submitted: { label: '已上报，待核实 / Submitted', pill: 'pill-stage' },
  verified: { label: '已核实 / Verified', pill: 'pill-good' },
  forfeited: { label: '逾期失权 / Time-barred', pill: 'pill-bad', note: '如48小时未上报，视为不产生费用，后续补报不予认可。' },
};

export const WI_KIND_LABEL: Record<WorkInstructionKind, string> = {
  variation: '变更 / Variation',
  daywork: '零星（计日工）/ Daywork',
};

export const WI_ORIGIN_LABEL: Record<string, string> = {
  client: '甲方提出 / Client',
  site: '现场 / Site',
  design: '设计 / Design',
  labor: '劳务提出 / Contractor',
  wtl: '我方 / WTL',
};

export const INSTRUCTION_SEAT_LABEL: Record<InstructionSeatKey, string> = {
  pm: '项目经理 / Project manager',
  engineer: '专业工程师 / Discipline engineer',
  cost: '成本负责人 / Cost lead',
};

export const COMMERCIAL_ROUTE_LABEL: Record<CommercialRoute, string> = {
  bill_client: '跟甲方签洽商 / Bill it to the client',
  absorb: '自行消化 / WTL absorbs it',
};

/**
 * 通用合同 9.2.1–9.2.10 — the ten cases that may be ordered as 计日工. The VALUE is what gets stored and
 * what the 指令单 .docx prints, so the select sends the Chinese text, not a code; the 9.2.x key is only
 * how the option is labelled and how an already-stored code (should one ever appear) resolves back.
 */
export const DAYWORK_CATEGORY_LABEL: Record<string, string> = {
  '9.2.1': '无法按合同计量的零星增项',
  '9.2.2': '短期小范围作业',
  '9.2.3': '极端条件下的非常规作业',
  '9.2.4': '应急抢险后的修复',
  '9.2.5': '隐蔽工程剥露检查（检查合格）',
  '9.2.6': '其他承包人施工后的相邻修复',
  '9.2.7': '甲方暂停导致的材料更换',
  '9.2.8': '范围外清理清运',
  '9.2.9': '范围外检测调试',
  '9.2.10': '非乙方责任的轻微损坏修复',
};

/** A stored 零星类别 → display text: the label when it is one of the 9.2.x keys, otherwise verbatim. */
export function dayworkCategoryText(v: string | null | undefined): string {
  if (!v) return '—';
  return DAYWORK_CATEGORY_LABEL[v] ?? v;
}

/** `pricing_json` arrives as a TEXT column. One place parses it, so a corrupt blob degrades, never throws. */
export function parsePricingJson(raw: string | null | undefined): PricingJson | null {
  if (!raw) return null;
  try { return JSON.parse(raw) as PricingJson; } catch { return null; }
}

/** persons/materials/equipment are TEXT columns holding a JSON array — same tolerance. */
export function parseJsonArray<T = Record<string, unknown>>(raw: unknown): T[] {
  if (Array.isArray(raw)) return raw as T[];
  if (typeof raw !== 'string' || !raw.trim()) return [];
  try { const v = JSON.parse(raw); return Array.isArray(v) ? (v as T[]) : []; } catch { return []; }
}

/**
 * A deadline rendered the way a site reads one: how long is left, is it tight, has it gone.
 *
 * `urgent` fires inside 48 h because that is the bar the contract itself uses for 计日工 (9.2.11) and the
 * red band 改改 asked for on the 7-day quote clock. `now` is injectable so a test (and a screenshot rig)
 * can pin it.
 */
export function countdown(iso: string | null | undefined, now: number = Date.now()): { text: string; urgent: boolean; expired: boolean } {
  if (!iso) return { text: '—', urgent: false, expired: false };
  const t = new Date(iso).getTime();
  if (!Number.isFinite(t)) return { text: '—', urgent: false, expired: false };
  const ms = t - now;
  if (ms <= 0) {
    const d = Math.floor(-ms / 86400000);
    return { text: d >= 1 ? `已逾期 ${d} 天 / ${d}d overdue` : '已逾期 / overdue', urgent: true, expired: true };
  }
  const h = Math.floor(ms / 3600000);
  const d = Math.floor(h / 24);
  const text = d >= 1 ? `剩 ${d} 天 ${h % 24} 小时 / ${d}d ${h % 24}h left` : `剩 ${h} 小时 / ${h}h left`;
  return { text, urgent: ms <= 48 * 3600000, expired: false };
}

/** The 4.2.3/4.2.4 caveat that has to sit under every 当前核定合同价 figure we show. */
export const CONTRACT_VALUE_CEILING_NOTE =
  '付款上限仍按基准合同价计算（合同 4.2.3/4.2.4）；核定价为参考值 / ceilings stay on the baseline — the approved figure is advisory';
/** 中信: the backfilled baseline ALREADY contains the legacy incorporated 变更 (see lib/contractValue.ts). */
export const CONTRACT_VALUE_LEGACY_CAVEAT =
  '本合同的基准价中已包含旧版「变更」并入的金额，核定价不会重复计入。/ this baseline already absorbed legacy change orders — they are reported, never re-added.';
/** 9.1.1, verbatim, under a part-signed order. */
export const WI_PART_SIGNED_NOTE = '签字不全或未经签字，不作为结算办理依据';
/** 9.2.11, verbatim, on a forfeited 计日工 row. */
export const DAYWORK_FORFEIT_NOTE = '如48小时未上报，视为不产生费用，后续补报不予认可。';

/** create/update warnings (§2.2) — advisory banners, never blockers. */
export const WI_WARNING_COPY: Record<string, string> = {
  lump_sum_daywork_needs_supplement: '本合同无计日工条款，需补充协议确认计价方式 — 商务批准前必须上传。/ lump-sum contract with no daywork clause — a 补充协议 must be uploaded before approval.',
  lump_sum_variation_needs_drawing_ref: '固定总价合同的变更建议填写图纸变更号，便于与甲方核对。/ on a lump-sum contract, record the drawing revision number.',
  tolerance_exceeded_advisory: '预估金额超过容错额度 — 建议先取得商务/公司确认（当前为提示，不拦截）。/ the estimate exceeds the tolerance — get 商务 sign-off first (advisory for now).',
};

// ── price-free fetchers (site_pm SAFE) ──────────────────────────────────────────────────────────

export function myWorkInstructions() {
  return api<{ count: number; items: MyInstructionItem[] }>('/me/work-instructions');
}
/** The nav badge. `?count=1` skips the payload entirely — it is polled every 60 s. */
export function myWorkInstructionCount() {
  return api<{ count: number }>('/me/work-instructions?count=1');
}
export function myWorkInstruction(wiid: string) {
  return api<InstructionDetail>(`/me/work-instructions/${wiid}`);
}

/**
 * PR10b · 「我能下指令的合同」 — the 项目经理's door.
 *
 * Eric 9/18 (工程指令): a site_pm could not reach a package page at all (every route on it is money-
 * guarded), so the person the contract names as the one who writes the order (9.1.1) had no way to
 * write one. This route is price-free by construction: it lists the SIGNED subcontracts the caller
 * holds a 席位 on, or is the standards-workflow assigned PM for, and carries no amount at all.
 * For non-PM staff it returns every signed subcontract.
 */
export interface MyActionableSubcontract {
  subcontract_id: string;
  package_id: string;
  package_name: string | null;
  project_name: string | null;
  labor_co_name: string | null;
  status: string;
  contract_form: ContractForm | null;
  /** Why this row is visible: the seat held, or null when it is the assigned-PM / all-staff path. */
  seat: InstructionSeatKey | null;
  is_assigned_pm: boolean;
}
export function myActionableSubcontracts() {
  return api<{ items: MyActionableSubcontract[] }>('/me/subcontracts');
}

export function createWorkInstruction(scId: string, body: InstructionDraftInput) {
  return apiSubmit<InstructionDetail>(`/subcontracts/${scId}/work-instructions`, body);
}
export function updateWorkInstructionDraft(wiid: string, body: InstructionDraftInput) {
  return apiSubmit<InstructionDetail>(`/work-instructions/${wiid}`, body, 'PUT');
}
export function preApproveWorkInstruction(wiid: string, noteMd?: string) {
  return apiSubmit<InstructionDetail>(`/work-instructions/${wiid}/pre-approve`, { note_md: noteMd ?? null });
}
export function issueWorkInstruction(wiid: string) {
  return apiSubmit<InstructionDetail>(`/work-instructions/${wiid}/issue`, {});
}
export function voidWorkInstruction(wiid: string, reasonMd: string) {
  return apiSubmit<InstructionDetail>(`/work-instructions/${wiid}/void`, { reason_md: reasonMd });
}

export interface SeatPatch { pm_staff_id?: string | null; engineer_staff_id?: string | null; cost_staff_id?: string | null }
export function setInstructionSeats(wiid: string, body: SeatPatch) {
  return apiSubmit<{ seats: ResolvedSeats }>(`/work-instructions/${wiid}/seats`, body, 'PUT');
}
export function getSubcontractInstructionSeats(scId: string) {
  return api<{ seats: ResolvedSeats }>(`/subcontracts/${scId}/instruction-seats`);
}
export function setSubcontractInstructionSeats(scId: string, body: SeatPatch) {
  return apiSubmit<{ seats: ResolvedSeats }>(`/subcontracts/${scId}/instruction-seats`, body, 'PUT');
}

/** JSON sign. Use `signWorkInstructionWithScan` when the signer also photographed the wet-ink page. */
export function signWorkInstruction(wiid: string, body: { seat: InstructionSeatKey; note_md?: string; on_behalf_note_md?: string }) {
  return apiSubmit<InstructionDetail>(`/work-instructions/${wiid}/sign`, body);
}
export function signWorkInstructionWithScan(wiid: string, form: FormData) {
  return apiUpload<InstructionDetail>(`/work-instructions/${wiid}/sign`, form);
}
export function attachInstructionWetSignature(wiid: string, form: FormData) {
  return apiUpload<InstructionDetail & { file: { id: string; file_name: string; size_bytes: number } }>(`/work-instructions/${wiid}/wet-signature`, form);
}
export function confirmInstructionRework(wiid: string, form: FormData) {
  return apiUpload<InstructionDetail>(`/work-instructions/${wiid}/rework/confirm`, form);
}

/**
 * PR5. Price-free read of one 计日工日报 (site_pm allowed — they confirm 实际消耗 under 9.2.11); the
 * amount 预算 put on the day is stripped for them by the same serializer that strips the instruction's.
 */
export function getDayworkRecord(dwid: string) {
  return api<{ record: DayworkRecord; instruction: DayworkBrief; contract_form: ContractForm | null; banner_zh?: string }>(
    `/daywork-records/${dwid}`,
  );
}
/** 核实 is `money` — budget/gm only. Corrections overwrite the live fields; 乙方's原报 survives in the snapshot. */
export function verifyDayworkRecord(dwid: string, body: {
  verify_note_md: string;
  persons_json?: unknown[]; materials_json?: unknown[]; equipment_json?: unknown[];
  priced_amount_rmb?: number | null; pricing_basis?: string | null; pricing_note?: string | null;
}) {
  return apiSubmit<{ record: DayworkRecord; corrected: boolean }>(`/daywork-records/${dwid}/verify`, body);
}

/**
 * 指令单 / 认价单 .docx (PR6). `variant=instruction` is price-free and a site_pm may open it;
 * `variant=price_confirmation` is money and 403s for them — never render that link for a PM.
 */
export function instructionDocumentUrl(wiid: string, variant: 'instruction' | 'price_confirmation' = 'instruction') {
  return `/api/work-instructions/${wiid}/document?variant=${variant}`;
}
export function instructionPhotoUrl(photoId: string) {
  return `/api/files/instruction-photos/${photoId}`;
}

// ── money fetchers (PR3 · NEVER call as site_pm — the server answers 403 forbidden_site_pm) ──────

export function staffWiLedger(scId: string) {
  return api<InstructionLedger>(`/subcontracts/${scId}/work-instructions`);
}
export function staffWiDetail(wiid: string) {
  return api<InstructionDetail>(`/work-instructions/${wiid}`);
}
export function staffWiPriceLine(wiid: string, lineId: string, body: PriceLineInput) {
  return apiSubmit<InstructionDetail & { line: WorkInstructionLine; pricing_json: PricingJson | null; totals: InstructionTotals }>(
    `/work-instructions/${wiid}/lines/${lineId}/price`, body,
  );
}
export function staffWiPriceComplete(wiid: string) {
  return apiSubmit<InstructionDetail & { totals: InstructionTotals; price_confirmation: PriceConfirmation; contract_value: ContractValueView }>(
    `/work-instructions/${wiid}/price-complete`, {},
  );
}
/** `expected_total_rmb` is REQUIRED — omitting it is 409 total_changed, not a silent approval. */
export function staffWiApprove(wiid: string, body: { commercial_route: CommercialRoute; note_md?: string; expected_total_rmb: number }) {
  return apiSubmit<InstructionDetail & { totals: InstructionTotals; price_confirmation: PriceConfirmation; contract_value: ContractValueView }>(
    `/work-instructions/${wiid}/approve`, body,
  );
}
/** multipart; the file part is `supplement`. */
export function staffWiSupplement(wiid: string, form: FormData) {
  return apiUpload<InstructionDetail & { file: { id: string; file_name: string; size_bytes: number } }>(`/work-instructions/${wiid}/supplement`, form);
}
export function staffWiQuotations(wiid: string) {
  return api<QuotationsView>(`/work-instructions/${wiid}/quotations`);
}
export function staffWiAddQuotation(wiid: string, body: {
  material_label: string; spec?: string | null; unit?: string | null;
  party: 'wtl' | 'labor'; supplier_name?: string | null; quoted_unit_price_rmb: number; quoted_on?: string | null; line_id?: string | null;
}) {
  return apiSubmit<QuotationsView & { quotation: MaterialQuotation }>(`/work-instructions/${wiid}/quotations`, body);
}
export function staffWiConfirmQuotations(wiid: string, materialLabel: string) {
  return apiSubmit<QuotationsView & { material: MaterialSummary }>(`/work-instructions/${wiid}/quotations/confirm`, { material_label: materialLabel });
}
export function staffWiDayworkRecords(wiid: string) {
  return api<{ instruction: DayworkBrief; contract_form: ContractForm | null; records: DayworkRecord[]; banner_zh?: string }>(
    `/work-instructions/${wiid}/daywork`,
  );
}
export function setSubcontractContractForm(scId: string, body: { contract_form: ContractForm; variation_standard?: string }) {
  return apiSubmit<{ subcontract: { id: string; contract_form: ContractForm; variation_standard: string | null } }>(
    `/subcontracts/${scId}/contract-form`, body, 'PATCH',
  );
}

/** The 4.1.2 enums, mirrored from `instructions/money.ts` so the pickers cannot drift from the CHECKs. */
export const VARIATION_STANDARD_LABEL: Record<string, string> = {
  '2021_xiushan': '2021 北京市房屋修缮工程消耗量标准 / 2021 Beijing refurbishment',
  db11t2382_2024: 'DB11/T 2382—2024',
};

/**
 * The G9 arithmetic, mirrored client-side for the LIVE PREVIEW only (base → −10% → +3% → × qty).
 * `instructions/pricing.ts` is the authority — this exists so 预算 sees the number move while typing,
 * and every surface that shows it also says 「以服务器重算为准」.
 */
export function previewBuildup(
  components: { qty?: number | null; unit_price_rmb?: number | null }[],
  qty: number,
  discountPct = 10,
  overheadProfitPct = 3,
): { base_rmb: number; after_discount_rmb: number; unit_rate_rmb: number; amount_rmb: number; steps: string[] } {
  const r2 = (n: number) => Math.round(n * 100) / 100;
  const base = r2(components.reduce((s, c) => s + (Number(c.qty) || 0) * (Number(c.unit_price_rmb) || 0), 0));
  const afterDiscount = r2(base * (1 - discountPct / 100));
  const rate = r2(afterDiscount * (1 + overheadProfitPct / 100));
  const amount = r2(rate * (Number(qty) || 0));
  return {
    base_rmb: base, after_discount_rmb: afterDiscount, unit_rate_rmb: rate, amount_rmb: amount,
    steps: [
      `基价 base ${base.toFixed(2)}`,
      `下浮 ${discountPct}% → ${afterDiscount.toFixed(2)}`,
      `管理费+利润 ${overheadProfitPct}% → ${rate.toFixed(2)}`,
      `× 数量 ${qty} = ${amount.toFixed(2)}`,
    ],
  };
}

/**
 * The 工程指令 refusal codes → bilingual copy (`instructions/state.ts` WI_ERROR_STATUS). The server sends
 * a bilingual `detail` on every one of these and apiErrText PREFERS it — these are the fallback for a
 * bare code (a stale bundle, an older build of the money half). Codes the compliance blocks already
 * spell (forbidden, not_found, reason_required, photo_required, unsupported_scan_type,
 * subcontract_not_found, file_too_large) are not repeated: one wording per code.
 */
Object.assign(ERR_COPY, {
  instruction_not_found: '未找到该工程指令，请刷新后重试。/ work instruction not found — refresh and retry.',
  line_not_found: '未找到该明细行，请刷新后重试。/ instruction line not found — refresh and retry.',
  daywork_not_found: '未找到该计日工日报。/ daywork record not found.',
  photo_not_found: '未找到该照片。/ photo not found.',
  engagement_not_found: '未找到对应的合作关系。/ engagement not found.',
  subcontract_not_signed: '合同尚未签署，不能下达工程指令。请先在「验收标准与合同」页记录签署。/ the subcontract is not signed yet — record the executed contract first.',
  occurred_on_invalid: '发生日期格式须为 YYYY-MM-DD。/ expected a YYYY-MM-DD date.',
  occurred_on_future: '变更产生日不能是将来的日期。/ the occurrence date cannot be in the future.',
  lines_required: '请至少填写一条明细行。/ at least one line is required.',
  rates_not_allowed_here: '项目经理不能填写单价 — 单价由预算在定价环节审核确定。/ the PM may not enter rates; 预算 prices the order.',
  daywork_category_required: '零星用工须选择用工类别（合同 9.2.1–9.2.10）。/ pick a daywork category.',
  contract_form_unknown: '本合同的计价形式（固定单价/固定总价）未确定，无法下达零星用工指令。/ the contract form is unknown — set it first.',
  commercial_ack_required: '来源为「甲方提出」的指令需先经商务确认。/ a client-raised instruction needs 商务 acknowledgement first.',
  commercial_preapproval_required: '预估金额超过容错额度，需商务/公司预先批准。/ the estimate exceeds the tolerance — 商务 pre-approval is required.',
  already_issued: '指令已下达，不能再修改。/ already issued — it can no longer be edited.',
  not_draft: '只有草稿可以修改。/ only a draft can be edited.',
  seats_incomplete: '签字席位未指派齐全，请先设置三位签字人。/ assign all three signature seats first.',
  seat_placeholder: '共用测试账号不能担任签字人，请选择实名员工。/ a placeholder login cannot hold a signature seat.',
  seat_inactive: '该员工已停用，不能担任签字人。/ inactive staff cannot hold a signature seat.',
  seat_invalid: '签字席位无效。/ unknown signature seat.',
  seat_not_found: '未找到该签字人。/ staff row not found for that seat.',
  not_seat_holder: '您不是该席位的签字人 — 如需代签请由总经理操作并填写代签说明。/ you do not hold this seat; only the GM may sign on behalf, with a note.',
  on_behalf_note_required: '代签必须填写代签说明。/ signing on behalf requires a note.',
  not_pending_signatures: '该指令当前不在待签字状态。/ this instruction is not awaiting signatures.',
  scan_required: '请上传签字扫描件。/ attach the signed scan.',
  scan_too_large: '扫描件过大（上限 30MB），请压缩后再上传。/ the scan exceeds the 30MB limit.',
  cannot_void_confirmed: '已批准或已认价的指令不能作废 — 请另开一条减项指令冲抵。/ an approved or confirmed order cannot be voided — offset it with a 减项 instruction.',
  void_forbidden_after_service: '指令已送达劳务，只有总经理或商务可以作废。/ once served, only gm/商务 may void.',
  rework_not_notified: '该指令未标记需要返工，无需确认。/ no rework was flagged on this instruction.',
  rework_already_confirmed: '返工已确认过了。/ the rework confirmation is already recorded.',
  not_issued: '指令尚未下达，暂无可生成的文件。/ the instruction has not been issued yet.',
  template_render_failed: '文件生成失败 — 模板与数据不匹配，请联系管理员。/ the document template failed to render.',
  tier_required: '请先选择定价档次（①–④）。/ pick a pricing tier first.',
  qty_required: '该行缺少工程量，无法定价。/ the line has no quantity — pricing needs one.',
  no_original_rate: '原合同清单中找不到可用单价 — 请改选「②按原单价调整」或「③协商确定」。/ no usable original contract rate — use tier ② or ③.',
  buildup_incomplete: '组价资料不完整：请填写标准、造价信息月份和人工/材料/机械明细。/ the build-up needs a standard, a price month and its components.',
  price_month_mismatch: '造价信息月份与变更产生月份不一致 — 请填写变更理由说明。/ the price month differs from the occurrence month — a written reason is required.',
  quotations_incomplete: '材料询价不足：每方至少 3 家，且需发包人书面确认。/ at least 3 quotations per party plus the employer written confirmation.',
  arithmetic_mismatch: '金额与服务器重算结果不一致，请以服务器结果为准。/ the amount does not survive recomputation — the server figure wins.',
  bad_state: '该指令当前状态不允许此操作。/ this action is not allowed in the current state.',
  lines_unpriced: '还有明细行未定价，不能完成定价。/ some lines are still unpriced.',
  not_priced: '该指令尚未完成定价，不能批准。/ the instruction has not been priced yet.',
  route_required: '请选择商务处理方式（跟甲方签洽商 / 自行消化）。/ pick a commercial route.',
  total_changed: '认价金额已更新，请刷新后再确认。/ the amount changed — refresh and confirm again.',
  lump_sum_supplement_required: '本合同无计日工条款，需先上传补充协议才能批准。/ a 补充协议 must be uploaded before approving daywork on a lump-sum contract.',
  party_invalid: '询价方只能是我方或劳务方。/ the quoting party must be 我方 or 劳务方.',
  below_min_quotations: '询价数量不足，无法确认中位数。/ not enough quotations to confirm a median.',
  contract_form_invalid: '合同形式只能是固定单价或固定总价。/ the contract form must be 固定单价 or 固定总价.',
  adjustment_reason_required: '调整单价必须填写调整理由（项目特征差异）。/ an adjustment needs a written reason.',
  not_served: '指令尚未送达劳务。/ the instruction has not been served yet.',
  quote_forfeited: '报价期限已过，该项已失权。/ the quotation window closed — the right has lapsed.',
  lines_mismatch: '报价明细与指令明细不一致，请刷新后重试。/ the quoted lines do not match the instruction.',
  rate_invalid: '单价无效。/ the unit rate is not a valid number.',
  not_a_daywork_instruction: '该指令不是零星用工指令。/ this is not a daywork instruction.',
  // review 9/18 (PR2 fixes) — draft validation now refuses these before anything is written
  kind_invalid: '指令类型只能是「变更」或「零星（计日工）」。/ the kind must be 变更 or 零星（计日工）.',
  origin_invalid: '来源无效，请从列表中选择。/ pick an origin from the list.',
  title_required: '请填写标题。/ a title is required.',
  work_date_invalid: '作业日期格式须为 YYYY-MM-DD。/ expected a YYYY-MM-DD work date.',
  work_date_future: '作业日期不能是将来的日期。/ the work date cannot be in the future.',
  work_date_before_effective: '作业日期早于指令生效日。/ the work date precedes the instruction.',
  persons_required: '请填写上工人员（姓名/工种/工时）。/ the crew list is required.',
  record_verified: '该日报已核实，不能再修改。/ this record is verified and can no longer change.',
  forfeited: '该记录已逾期失权，不能再处理。/ this record is time-barred.',
  not_submitted: '该日报尚未上报，无法核实。/ the record has not been submitted yet.',
  rework_not_required: '该指令无需返工确认。/ no rework confirmation is due on this instruction.',
  not_approved: '该指令尚未批准。/ the instruction is not approved yet.',
  note_required: '请填写说明。/ a written note is required.',
  invalid_transition: '该操作在当前状态下不允许。/ that transition is not legal from the current state.',
  comment_required: '该操作必须填写说明。/ this action requires a written comment.',
  forbidden_site_pm: '项目经理不可查看金额相关内容。/ 项目经理 accounts cannot see pricing.',
});
// ── Layer 2 · staff instructions (PR8) ── (block end)

// ── Layer 2 · 工程指令设置 (gm-only, PR8) ─────────────────────────────────────────────────────────
/**
 * `app_settings.instructions` — every clock, the 4.1.2 arithmetic, the 容错额度 and the holiday list.
 *
 * These are the numbers the whole layer hangs off, and three of them are company decisions that were
 * NOT settled when the code shipped: `tolerance_rmb` (谢工 7/24 — 公司拍板), `holidays` (no CN holiday
 * calendar in the box, so 工作日 is weekends-only until somebody pastes one) and `daywork_rate_caps`
 * (谢工's 零星统一限价表 has not arrived). Making them editable is how the build stops waiting on a
 * meeting — the code ships inert and the GM switches it on.
 */
export interface InstructionSettings {
  quote_days: number;
  quote_warn_days: number[];
  daywork_due_days: number;
  daywork_hard_bar_days: number;
  daywork_warn_hours: number[];
  rework_confirm_working_days: number;
  wtl_pricing_days: number;
  wtl_daywork_verify_working_days: number;
  claim_ack_days: number;
  variation_standard: string;
  discount_pct: number;
  overhead_profit_pct: number;
  arithmetic_order: string;
  min_quotations: number;
  /** null = 未设定. Until the company names a figure the gate stays inert and the UI only advises. */
  tolerance_rmb: number | null;
  tolerance_enforce_at: 'advisory' | 'gate';
  client_origin_requires_commercial_ack: boolean;
  working_days_exclude_weekends: boolean;
  holidays: string[];
  photo_required_on_quote: boolean;
  photo_required_on_daywork: boolean;
  daywork_rate_caps: Record<string, number>;
  sweep_interval_min: number;
}

export function getInstructionSettings() {
  return api<{ instructions: InstructionSettings }>('/admin/instruction-settings');
}
/** PARTIAL patch — send only the keys that changed; an unknown key is refused with 400 invalid_setting. */
export function saveInstructionSettings(patch: Partial<InstructionSettings>) {
  return apiSubmit<{ ok: boolean; instructions: InstructionSettings }>('/admin/instruction-settings', patch, 'PUT');
}

Object.assign(ERR_COPY, {
  invalid_setting: '设置项无效，请检查填写的数值。/ that setting value is not valid — check the field.',
});
// ── Layer 2 · 工程指令设置 (gm-only, PR8) ── (block end)

// NOTE (merge 9/18): the three label maps and countdown() are PR8's single copy above — PR9's identical
// declarations were removed on merge; PortalWi* types are kept as the portal's own names for the same unions.
// ── Layer 2 · portal instructions (PR9) ──────────────────────────────────────────────────────────────
// Eric 9/18 (工程指令): the CONTRACTOR half of §3. Deliberately ONE contiguous block at the END of this
// file so the staff half (PR8, landing in parallel) and this one merge mechanically — the three label
// maps and countdown() are declared in BOTH blocks with the plan's VERBATIM values, so whichever side
// git keeps means the same thing. Every fetcher here is prefixed `portalWi` for the same reason.
//
// Server contract: PR4a (routes/portal.ts ~:904) list / detail / read / quote / rework-complete /
// todo-count · PR5 (routes/portalDaywork.ts) the 计日工 daily form · PR6 (routes/instructionDocs.ts)
// the two .docx variants · PR4b (§2.4, building) confirm / dispute. Only confirm+dispute are still
// typed ahead of their route; a call into one that has not shipped fails as an ordinary 404 through
// apiErrText, never as a crash.

export type PortalWiKind = 'variation' | 'daywork';
export type PortalWiState =
  | 'draft' | 'pending_signatures' | 'effective' | 'served' | 'quoted'
  | 'priced' | 'approved' | 'confirmed' | 'disputed' | 'void';
export type PortalWiTier = 'identical' | 'similar' | 'negotiated' | 'buildup';
export type PortalWiDayworkState = 'open' | 'submitted' | 'verified' | 'forfeited';





/** 9.1.5 tier-④ 组价 component, as re-projected for 乙方 (view.ts LABOR_COMPONENT_KEYS). */
export interface PortalWiComponent {
  kind?: string; label?: string; qty?: number | null; unit?: string | null;
  unit_price_rmb?: number | null; amount_rmb?: number | null;
  source?: string | null; median_of?: number[] | null; note?: string | null;
}
/** G9: the STEPS are the point — 乙方 has to be able to check our arithmetic line by line. */
export interface PortalWiPricing { tier: PortalWiTier | string | null; steps: string[]; components: PortalWiComponent[] }

/** One scope line: 申报 (theirs) beside 审核 (ours). internal_note_md is dropped by the serializer. */
export interface PortalWiLine {
  id: string; seq: number; item_label: string; spec_md: string | null;
  unit: string | null; qty: number | null; measurement_calc_md: string | null;
  declared_unit_rate_rmb: number | null; declared_amount_rmb: number | null; declared_note_md: string | null;
  tier: PortalWiTier | null; adjustment_note_md: string | null;
  audited_unit_rate_rmb: number | null; audited_amount_rmb: number | null; priced_at: string | null;
  pricing: PortalWiPricing | null;
}

/** 9.1.1 三方签字 — 乙方 sees THAT the three seats signed and when, not our seat ids or internal notes. */
export interface PortalWiSignature {
  seat: 'pm' | 'engineer' | 'cost'; signed_by_name: string | null;
  signed_at: string | null; on_behalf_of: string | null;
}

export interface PortalWiPhoto {
  id: string; kind: string; file_id: string; daywork_record_id?: string | null;
  uploaded_by_type: 'labor_co' | 'staff'; uploaded_by_name: string | null;
  taken_on: string | null; caption: string | null; created_at: string;
}

/** One 人员 row of 9.2.11 — 姓名 / 工种 / 级别 / 工时, all four mandated by the clause. */
export interface DayworkPerson { name: string; trade?: string; grade?: string; hours?: number | string }
export interface DayworkMaterial { name: string; category?: string; spec?: string; brand?: string; qty?: number | string; unit?: string }
export interface DayworkEquipment { model: string; qty?: number | string; shifts?: number | string }

/**
 * One 计日工 日报, `audience:'labor'` (view.ts DAYWORK_FIELDS.labor). NO money: `priced_amount_rmb`,
 * `pricing_json`, `verify_note_md` and `verified_by` are all off the labor projection.
 *
 * `hours_left` / `submission_overdue` / `hard_bar_passed` are derived and arrive only from PR5's
 * `GET …/daywork`; on the PR4a detail payload they are absent and the client counts down from
 * `hard_bar_at` itself.
 */
export interface PortalWiDayworkRecord {
  id: string; instruction_id?: string; work_date: string; state: PortalWiDayworkState;
  reason_md?: string | null; work_content_md?: string | null; work_qty_md?: string | null;
  persons_json?: string | null; materials_json?: string | null; equipment_json?: string | null;
  other_vouchers_md?: string | null;
  /** parsed beside the raw columns by dayworkRecordView, so no client ever JSON.parses a DB column */
  persons?: DayworkPerson[]; materials?: DayworkMaterial[]; equipment?: DayworkEquipment[];
  due_at: string | null; hard_bar_at: string | null;
  submitted_at: string | null; verified_at: string | null;
  forfeited_at: string | null; forfeit_reason: string | null;
  created_at?: string; updated_at?: string;
  forfeited?: boolean; contract_text_zh?: string | null;
  hours_left?: number | null; submission_overdue?: boolean; hard_bar_passed?: boolean;
  photos?: PortalWiPhoto[];
}

/** 认价单 v(n), as 乙方 is shown it. `snapshot` is already stripped of internal keys server-side. */
export interface PortalWiPriceConfirmation {
  id: string; version: number; snapshot: unknown;
  total_declared_rmb: number | null; total_audited_rmb: number | null;
  additions_rmb: number | null; omissions_rmb: number | null;
  policy_label: string; approved_at: string | null;
  status: 'open' | 'confirmed' | 'disputed' | 'superseded';
  labor_confirmed_at: string | null; labor_disputed_at: string | null;
  dispute_note_md: string | null; docx_file_id: string | null; created_at: string;
}
/** The shape PR3 writes into `snapshot_json`. Read defensively — it is a stored blob, not a typed row. */
export interface PortalWiSnapshotLine {
  line_id?: string; id?: string; seq?: number; item_label?: string; spec_md?: string | null;
  unit?: string | null; qty?: number | null;
  declared_unit_rate_rmb?: number | null; declared_amount_rmb?: number | null;
  audited_unit_rate_rmb?: number | null; audited_amount_rmb?: number | null;
  tier?: PortalWiTier | null; adjustment_note_md?: string | null; measurement_calc_md?: string | null;
  steps?: string[]; components?: PortalWiComponent[];
  /** PR3 nests the G9 arithmetic here on the stored snapshot; the flat `steps`/`components` are accepted too. */
  pricing?: PortalWiPricing | null;
}

/** The instruction row itself (view.ts `labor` allow-list). Booleans arrive as booleans. */
export interface PortalWiInstruction {
  id: string; package_id: string; subcontract_id: string; seq: number; doc_no: string | null;
  kind: PortalWiKind; contract_form: ContractForm | null; variation_standard: string | null;
  title: string; reason_md: string | null; scope_md: string | null; location_md: string | null;
  stage_md: string | null; origin: string | null; occurred_on: string;
  drawing_ref: string | null; daywork_category: string | null; schedule_impact_days: number | null;
  state: PortalWiState; issued_at: string | null; issued_by_name: string | null;
  effective_at: string | null; served_at: string | null; read_at: string | null;
  quote_due_at: string | null; late_service: boolean;
  quote_submitted_at: string | null; quote_forfeited_at: string | null;
  approved_at: string | null; confirmed_at: string | null;
  disputed_at: string | null; dispute_note_md: string | null;
  void_at: string | null; void_reason_md: string | null;
  rework_required: boolean; rework_notified_at: string | null;
  rework_confirm_due_at: string | null; rework_confirmed_at: string | null;
  supplement_file_id: string | null;
  doc_file_id: string | null; doc_rendered_at: string | null;
  created_at: string; updated_at: string;
}

/** A list row: the order, its signatures and its clocks. Lines and photos live on the detail read only. */
export interface PortalWiRow {
  instruction: PortalWiInstruction;
  signatures: PortalWiSignature[];
  signatures_signed: number;
  signatures_required: number;
  quote_due_at: string | null;
  /** null = no quote owed (零星, already quoted, or 失权) — do NOT draw a 7-day countdown on it. */
  hours_left: number | null;
  forfeited: boolean;
  quote_overdue: boolean;
  late_service: boolean;
  /** The contract's OWN sentence for this state — rendered verbatim, never paraphrased. */
  contract_text_zh: string | null;
}

export interface PortalWiDetail extends PortalWiRow {
  lines: PortalWiLine[];
  photos: PortalWiPhoto[];
  daywork_records: PortalWiDayworkRecord[];
  price_confirmation: PortalWiPriceConfirmation | null;
  photo_required_on_quote: boolean;
}

/** The nav badge's 工程指令 half (PR4a extended `GET /portal/todo-count`). Declaration-merged into the
 *  Layer 1 interface above, so `portalTodoCount()` keeps ONE return type and neither block edits the other. */
export interface PortalTodoCount {
  instructions_unread: number;
  quotes_due_soon: number;
  quotes_overdue: number;
  daywork_due_today: number;
  confirmations_pending: number;
  /** The 工程指令 tab's own badge — Layer 1's `total` stays the 履约档案 number it has always been. */
  instructions_total: number;
}

// ── reads ──
export function portalWiList(eid: string) {
  return api<{ items: PortalWiRow[] }>(`/portal/engagements/${eid}/work-instructions`);
}
export function portalWiDetail(eid: string, wiid: string) {
  return api<PortalWiDetail>(`/portal/engagements/${eid}/work-instructions/${wiid}`);
}
export function portalWiPhotoUrl(eid: string, wiid: string, photoId: string) {
  return `/api/portal/engagements/${eid}/work-instructions/${wiid}/photos/${photoId}`;
}
/**
 * PR6 · 指令单 / 认价单 .docx. A LINK only — nothing here parses a .docx. The 认价单 variant 409s
 * (`not_priced`) until the order is approved/confirmed/disputed, so the UI offers it only in those states
 * (instructionDocs.ts PORTAL_PRICE_CONFIRMATION_STATES).
 */
export function portalWiDocumentUrl(eid: string, wiid: string, variant: 'instruction' | 'price_confirmation') {
  return `/api/portal/engagements/${eid}/work-instructions/${wiid}/document?variant=${variant}`;
}
/** The states in which 乙方 may pull the 认价单 — mirrors the server list rather than guessing at it. */
export const WI_PRICE_CONFIRMATION_STATES: PortalWiState[] = ['approved', 'confirmed', 'disputed'];

// ── writes ──
/** G1 查阅回执 — fired ONCE when a detail opens. Idempotent server-side; a failure is never shown. */
export function portalWiRead(eid: string, wiid: string) {
  return apiSubmit<{ ok: boolean; recorded: boolean; read_at: string | null; instruction_id: string }>(
    `/portal/engagements/${eid}/work-instructions/${wiid}/read`, {});
}

export interface PortalWiQuoteLine {
  line_id: string; declared_unit_rate_rmb: number;
  declared_amount_rmb?: number | null; declared_note_md?: string | null;
}
/** 9.1.3 报价 — every line of the order, or none of it, plus the site photos (≤10, 30 MB each). */
export function portalWiQuote(eid: string, wiid: string, lines: PortalWiQuoteLine[], photos: File[]) {
  const fd = new FormData();
  fd.append('lines_json', JSON.stringify(lines));
  for (const f of photos) fd.append('photo', f);
  return apiUpload<{ ok: boolean } & PortalWiDetail>(`/portal/engagements/${eid}/work-instructions/${wiid}/quote`, fd);
}

/** 9.1.4 返工完成 — the photo evidence starts WTL's own 3-工作日 confirmation clock (G3). */
export function portalWiReworkComplete(eid: string, wiid: string, photos: File[], notesMd?: string) {
  const fd = new FormData();
  for (const f of photos) fd.append('photo', f);
  if (notesMd) fd.append('notes_md', notesMd);
  return apiUpload<{ ok: boolean } & PortalWiDetail>(`/portal/engagements/${eid}/work-instructions/${wiid}/rework-complete`, fd);
}

/** PR5 · one day's 计日工 日报 (9.2.11's six groups). Arrays travel as JSON strings in multipart fields. */
export interface PortalWiDayworkPayload {
  work_date: string;
  reason_md?: string; work_content_md?: string; work_qty_md?: string;
  persons_json: string; materials_json?: string; equipment_json?: string;
  other_vouchers_md?: string;
}
export interface PortalWiDayworkPostResult {
  record: PortalWiDayworkRecord;
  created: boolean;
  forfeited: boolean;
  /** present only on a refusal — 'late_submission' | 'never_submitted' */
  forfeit_reason?: string | null;
  /** UP 9.2.11 verbatim; the ONLY place the contractor is told the day was lost. Show it prominently. */
  contract_text?: string | null;
  banner_zh?: string | null;
}
/**
 * `?draft=1` parks the day as `open` (暂存); without it the day is 上报. A filing past `hard_bar_at`
 * answers **201 with `forfeited:true`** and the clause — that is an OUTCOME, not an error, and the form
 * renders it as such (G14). A second filing for the same day is an EDIT, never a second claim.
 */
export function portalWiDaywork(eid: string, wiid: string, payload: PortalWiDayworkPayload, photos: File[], draft = false) {
  const fd = new FormData();
  for (const [k, v] of Object.entries(payload)) if (v != null) fd.append(k, String(v));
  for (const f of photos) fd.append('photo', f);
  return apiUpload<PortalWiDayworkPostResult>(
    `/portal/engagements/${eid}/work-instructions/${wiid}/daywork${draft ? '?draft=1' : ''}`, fd);
}
export interface PortalWiDayworkList {
  instruction: {
    id: string; doc_no: string | null; title: string; state: PortalWiState;
    daywork_category: string | null; contract_form: ContractForm | null; effective_at: string | null;
  };
  records: PortalWiDayworkRecord[];
  /** the 48-hour rule travels with the LIST, not only with a refusal — nobody is surprised on day three */
  contract_text_zh: string;
  /** G11 · 固定总价 has no 计日工 clause at all; present only on a lump-sum contract */
  banner_zh?: string | null;
}
export function portalWiDayworkList(eid: string, wiid: string) {
  return api<PortalWiDayworkList>(`/portal/engagements/${eid}/work-instructions/${wiid}/daywork`);
}

/** PR4b · 认价. `expected_total_rmb` binds consent to the figure ON SCREEN — a stale view 409s. */
export function portalWiConfirm(eid: string, wiid: string, expectedTotalRmb: number, confirmNoteMd?: string) {
  return apiSubmit<{ ok: boolean } & Partial<PortalWiDetail>>(
    `/portal/engagements/${eid}/work-instructions/${wiid}/confirm`,
    { expected_total_rmb: expectedTotalRmb, ...(confirmNoteMd ? { confirm_note_md: confirmNoteMd } : {}) });
}
/** PR4b · 有异议 — the reason is required; 预算 re-prices and a new 认价单 version opens. */
export function portalWiDispute(eid: string, wiid: string, noteMd: string) {
  return apiSubmit<{ ok: boolean } & Partial<PortalWiDetail>>(
    `/portal/engagements/${eid}/work-instructions/${wiid}/dispute`, { note_md: noteMd });
}

/** ¥ with no decimals — the portal's house style for a headline figure; null renders as an em dash. */
export function wiMoney(n: number | null | undefined): string {
  return n == null ? '—' : `¥${Math.round(n).toLocaleString('zh-CN')}`;
}
/** Same, but keeping the 分 — a 单价 of 185.45 must not read as 185. */
export function wiRate(n: number | null | undefined): string {
  return n == null ? '—' : `¥${n.toLocaleString('zh-CN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}
/** Today in Beijing (UTC+8, fixed — the server's lib/beijingClock.ts uses the same offset, no DST in CN). */
export function beijingToday(now: number = Date.now()): string {
  return new Date(now + 8 * 3_600_000).toISOString().slice(0, 10);
}
/** An ISO timestamp as Beijing `M/D HH:mm` (same fixed UTC+8), `YYYY/M/D HH:mm` outside the current Beijing
 *  year. A zone-less SQLite stamp reads as UTC. */
export function beijingStamp(iso: string | null | undefined, now: number = Date.now()): string {
  if (!iso) return '';
  const t = Date.parse(/[zZ]|[+-]\d\d:?\d\d$/.test(iso) ? iso : `${iso.replace(' ', 'T')}Z`);
  if (Number.isNaN(t)) return iso;
  const d = new Date(t + 8 * 3_600_000);
  const p = (n: number) => String(n).padStart(2, '0');
  const year = d.getUTCFullYear() === new Date(now + 8 * 3_600_000).getUTCFullYear() ? '' : `${d.getUTCFullYear()}/`;
  return `${year}${d.getUTCMonth() + 1}/${d.getUTCDate()} ${p(d.getUTCHours())}:${p(d.getUTCMinutes())}`;
}

/**
 * 工程指令 refusal codes → bilingual copy. Fallbacks ONLY: every route above sends a bilingual `detail`
 * that apiErrText prefers; these carry a bare code (an old cached bundle, or a route that has not shipped
 * yet). Codes Layer 1 already spells (too_many_files, unsupported_scan_type, file_too_large…) are
 * deliberately NOT repeated — one wording per code, for both audiences.
 */
Object.assign(ERR_COPY, {
  not_served: '指令尚未送达或已进入审核，暂不能报价。/ the order is not in a state that accepts a quote.',
  quote_forfeited: '报价期限已过，报价权已失效（合同 9.1.3）。/ the 7-day quote window has closed.',
  photo_required: '请附现场影像资料后再提交（合同 9.1.3 / 9.2.11）。/ attach site photo evidence before submitting.',
  lines_mismatch: '报价须覆盖指令的全部明细，且不得包含其他明细。/ quote every line of the order, and only its lines.',
  rate_invalid: '单价与合价须为不小于 0 的数字。/ rates and amounts must be numbers ≥ 0.',
  scan_too_large: '文件过大（单个上限 30MB），请压缩后再上传。/ file too large (30MB per file) — compress it.',
  instruction_not_found: '未找到该工程指令，请刷新后重试。/ work instruction not found — refresh and retry.',
  engagement_not_found: '未找到该合作项目，请刷新后重试。/ engagement not found — refresh and retry.',
  rework_not_required: '该指令当前无需提交返工完成。/ no rework is outstanding on this order.',
  not_a_daywork_instruction: '本指令不是零星工程指令，无需填报计日工日报。/ this is not a 零星工程 order — no daily record applies.',
  work_date_invalid: '施工日期格式须为 YYYY-MM-DD。/ expected a YYYY-MM-DD date.',
  work_date_future: '施工日期不能晚于今天。/ a future date cannot be reported.',
  work_date_before_effective: '施工日期早于指令生效日，请核对。/ the date precedes the order taking effect.',
  persons_required: '请填写当日出工人员（姓名、工种、等级、工时）。/ the day’s crew is required — name, trade, grade, hours (9.2.11).',
  record_verified: '该日日报已核实确认，不能再修改。/ that day has been verified and can no longer be edited.',
  not_approved: '该指令尚未批准，暂不能认价。/ the order has not been approved yet.',
  note_required: '请填写异议理由。/ a reason is required.',
  total_changed: '认价金额已更新，请刷新后再确认。/ the confirmed total changed — refresh and confirm again.',
  not_priced: '认价单尚未批准，暂不可下载。/ the price confirmation has not been approved yet.',
  variant_invalid: '文档类型不正确。/ unknown document variant.',
  template_render_failed: '文档生成失败，请联系 WTL。/ the document could not be rendered — contact WTL.',
});
// ── Layer 2 · portal instructions (PR9) ── (block end)
